import { useState, useEffect, useMemo, useRef, Fragment, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import {
  collection, collectionGroup, doc, onSnapshot, query, orderBy, where, serverTimestamp,
  runTransaction, getDocs, getDoc, updateDoc,
  setDoc,
} from 'firebase/firestore';
import type { Transaction, Firestore, DocumentReference, DocumentSnapshot } from 'firebase/firestore';
import { createdStamp, updatedStamp, logActivity, addedByName } from '../utils/activity';
import AuthorLine from '../components/AuthorLine';
import { canAccessPage, COST_MARGIN_KEY } from '../utils/pages';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/AppLayout';
import { useNewParam, useViewParam, useItemParam } from '../hooks/useNewParam';
import { ImportSalesModal, type SaleImportGroup } from './SalesImport';
import { sellableByCode } from '../utils/saleImport';
import { IconImportSm } from '../components/HeaderControls';
import { useFrozenColumns } from '../hooks/useFrozenColumns';
import { useFitHeight } from '../hooks/useFitHeight';
import { useColumnFilters, type FilterColumn } from '../hooks/useColumnFilters';
import { ColumnFilterButton, ColumnFilterChips } from '../components/ColumnFilter';
import { Button, Modal, ModalBanner, ConfirmModal, FormField, Dropdown, EntityCombobox, TotalsFigures, PageTabs } from '../components/ui';
import { EntityFormModal } from '../components/EntityFormModal';
import RecordSalePaymentModal from '../components/RecordSalePaymentModal';
import { SaleMoneyStrip, SalePaymentsTab, SaleBrokersTab } from '../components/SaleMoney';
import { useSaleMoney, useLiveSale, saleBrokerRows } from '../utils/saleMoney';
import { invoiceCurrencyOf, invoiceRateOf, fmtInInvoiceCurrency, suggestInvoiceRate } from '../utils/invoiceCurrency';
import { CURRENCY_LIST } from '../utils/currency';
import { useSettings } from '../hooks/useSettings';
import { isIndian, withBusinessSystem, lineNet, defaultGst, gstConflict, gstMode, invoiceGst, type GstPercents, gstInputFrom, gstPercentsOf, type GstInput } from '../utils/tradingSystem';
import { GstBoxes, GstSummary } from '../components/GstBoxes';
import { useGridKeyNav } from '../hooks/useGridKeyNav';
import { useCategoryFilter } from '../context/CategoryFilterContext';
import { fmtMoney } from '../utils/currency';
import { buildEntry, postEntry, entryId, linesSaleInvoice, linesBrokerAccrual } from '../utils/ledger';
import { downloadCSV } from '../utils/csv';
import { formatAddressLines, legacyToBillTo } from '../utils/address';
import { computeDueGroups } from '../utils/dueGroups';
import { planDummyDelta, applyDummyDelta, type DummyDeltaPlan } from '../utils/dummyCode';
import {
  SALE_EXPENSE_CATEGORIES, newSaleExpenseRow, saleExpenseToRow, saleExpenseTotals, validateSaleExpenseRows,
  planSaleExpenses, extraCostsOf, readSaleExpenseAccounts, applySaleExpenses, saleRevenueOf, type SaleExpenseRow,
} from '../utils/saleExpenses';
import type {
  Entity, BusinessSettings, InventoryItem, InventoryStatus, BankAccount,
  SaleInvoice, SaleLineItem, PaymentStatus, InventoryMemo, InventoryMemoStatus, DueDateMode,
  TransactionCategory, BrokerPayment, SaleExpense, SaleExtraCost, EntityAddress, PurchaseInvoice,
} from '../types';
import {
  paginateRows, numberToWords,
  LOGO_W_DEFAULT, LOGO_W_MIN, LOGO_W_MAX, LOGO_RATIO,
  FOOTER_IMG_H_DEFAULT, FOOTER_IMG_H_MIN, FOOTER_IMG_H_MAX,
} from '../utils/invoiceSheets';
import { invoiceNoMatches, shortInvoiceNo } from '../utils/invoiceNo';

// ── Constants ─────────────────────────────────────────────────────────────────

const PAGE_SIZE   = 50;
const PAY_RANK: Record<PaymentStatus, number> = { unpaid: 0, partial: 1, paid: 2 };

function outstandingOf(inv: { totalAmountUSD: number; paidUSD?: number; creditedUSD?: number }) {
  return Math.max(0, inv.totalAmountUSD - (inv.paidUSD ?? 0) - (inv.creditedUSD ?? 0));
}

// ── Invoice document options panel — Sales.tsx's InvoiceDocument view ──────
const INVOICE_ACCENT_SWATCHES = ['#0F172A', '#DB2777', '#EA580C', '#1E3A8A', '#2563EB', '#0EA5E9', '#059669', '#6B7280'];
const INVOICE_OPTION_TOGGLES: { key: keyof NonNullable<SaleInvoice['invoiceOptions']>; label: string }[] = [
  { key: 'hideTotal',           label: 'Hide the total' },
  { key: 'hideItemPrice',       label: 'Hide item price (Price/ct)' },
  { key: 'hidePaymentInfo',     label: 'Hide payment information' },
  { key: 'hideCustomerCountry', label: 'Hide customer country' },
  { key: 'showCustomerPhone',   label: "Show customer's phone number" },
  { key: 'showCustomerEmail',   label: "Show customer's email" },
];
// Free-text trade-document fields — shown in the invoice doc's Terms/Ship
// Via/Insured By and Due Date/Salesman/CIF Details rows.
const INVOICE_TEXT_FIELDS: { key: keyof NonNullable<SaleInvoice['invoiceOptions']>; label: string }[] = [
  { key: 'shipVia',    label: 'Ship Via' },
  { key: 'insuredBy',  label: 'Insured By' },
  { key: 'salesman',   label: 'Salesman' },
  { key: 'cifDetails', label: 'CIF Details' },
  { key: 'origin',      label: 'Origin' },
  { key: 'roughOrigin', label: 'Origin of Rough' },
];

type DateFilter = 'this-month' | '3-months' | 'this-year' | 'custom';
const DATE_FILTER_LABELS: Record<DateFilter, string> = {
  'this-month': 'This Month', '3-months': '3 Months', 'this-year': 'This Year', custom: 'Custom',
};

// ── Customize Columns — list table ──────────────────────────────────────────
// Personal, per-browser preference (localStorage), same pattern as
// Inventory's/Purchase's — see CLAUDE.md. Invoice No stays out of this list
// (pinned, not customizable) because the row's urgency accent border and the
// selection checkbox both live on a dedicated pinned cell right before it,
// same combined cell Purchase's list uses. Actions is excluded for the usual
// reason: it's a controls column, not data.

type ColumnKey = 'category' | 'invoiceNo' | 'customer' | 'saleDate' | 'itemCount'
  | 'totalCt' | 'pricePerCt' | 'netAmount' | 'gstAmount' | 'totalAmount' | 'terms' | 'broker' | 'dueDate' | 'payStatus' | 'sentStatus' | 'addedBy';

// Every column doubles as a sort key — same names, no separate mapping
// needed (unlike Purchase.tsx, which has a couple of columns that don't map
// 1:1 to a sortable field).
type SortKey = ColumnKey;

interface ColumnDef { key: ColumnKey; label: string; skeletonWidth: number }

// Order here = column order in the table. 'category' leads — same "Cat. is
// always the first column" convention as Purchase.tsx's own line-items
// sub-table.
const COLUMNS: ColumnDef[] = [
  { key: 'category',    label: 'Cat.',          skeletonWidth: 24  },
  { key: 'invoiceNo',   label: 'Invoice No',    skeletonWidth: 90  },
  { key: 'customer',    label: 'To (Customer)', skeletonWidth: 130 },
  { key: 'saleDate',    label: 'Sale Date',     skeletonWidth: 75  },
  { key: 'itemCount',   label: 'Items',         skeletonWidth: 45  },
  { key: 'totalCt',     label: 'Total ct',      skeletonWidth: 65  },
  { key: 'pricePerCt',  label: 'Price/ct',      skeletonWidth: 80  },
  // Indian trading system only: Amount (stones + charges) and GST, then Total Amount.
  { key: 'netAmount',   label: 'Amount',        skeletonWidth: 90  },
  { key: 'gstAmount',   label: 'GST',           skeletonWidth: 70  },
  { key: 'totalAmount', label: 'Total Amount',  skeletonWidth: 90  },
  { key: 'terms',       label: 'Terms',         skeletonWidth: 60  },
  { key: 'broker',      label: 'Broker',        skeletonWidth: 100 },
  { key: 'dueDate',     label: 'Due Date',      skeletonWidth: 90  },
  { key: 'payStatus',   label: 'Pay Status',    skeletonWidth: 90  },
  { key: 'sentStatus',  label: 'Invoice Sent',  skeletonWidth: 80  },
  { key: 'addedBy',     label: 'Added by',      skeletonWidth: 80  },
];
const GST_COLUMN_KEYS = new Set<ColumnKey>(['netAmount', 'gstAmount']);
const ACCENT_SKELETON_WIDTH  = 8;
// Fixed widths of the frozen columns (and Customer), so they never resize
// as rows, filters or pages change.
const FIXED_W: Partial<Record<ColumnKey, number>> = { category: 56, invoiceNo: 112, customer: 220 };

const COLUMN_VISIBILITY_KEY = 'sales-visible-columns';
const DEFAULT_VISIBLE: Record<ColumnKey, boolean> =
  Object.fromEntries(COLUMNS.map(c => [c.key, c.key !== 'addedBy'])) as Record<ColumnKey, boolean>;  // "Added by" starts hidden

// Merges the saved blob over the all-visible default, key-by-key, so a
// column removed from COLUMNS in a future change is silently dropped and a
// column newly added to COLUMNS defaults to visible for existing users —
// never trust the raw parsed object wholesale.
function loadVisibleColumns(): Record<ColumnKey, boolean> {
  try {
    const raw = localStorage.getItem(COLUMN_VISIBILITY_KEY);
    if (!raw) return { ...DEFAULT_VISIBLE };
    const saved = JSON.parse(raw) as Partial<Record<ColumnKey, boolean>>;
    const merged = { ...DEFAULT_VISIBLE };
    for (const c of COLUMNS) if (typeof saved[c.key] === 'boolean') merged[c.key] = saved[c.key]!;
    return merged;
  } catch { return { ...DEFAULT_VISIBLE }; }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

// Local calendar day — never toISOString(), which is UTC: in any timezone
// ahead of UTC (India, Europe…) local midnight is still the previous day in
// UTC, so every computed date (due dates, today, filter ranges) came out a day early.
function toDateStr(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function addDays(n: number) {
  const d = new Date(); d.setDate(d.getDate() + n); return toDateStr(d);
}
function todayISO() { return toDateStr(new Date()); }
function addDaysToDate(base: string, n: number): string {
  if (!base) return addDays(n);
  const d = new Date(base + 'T00:00:00');
  if (isNaN(d.getTime())) return addDays(n);
  d.setDate(d.getDate() + n);
  return toDateStr(d);
}
function fmtInvoiceNo(prefix: string, count: number) {
  return `${prefix}-${new Date().getFullYear()}-${String(count).padStart(4, '0')}`;
}
// Invoice No is stored in full, but shown here as just the prefix + last 4
// digits to keep things compact. Keeping the prefix matters — a bare last-4
// truncation would collapse different prefixes into the same number.
function lotNoDisplay(invoiceNo: string) {
  if (!invoiceNo) return '—';
  const m = invoiceNo.match(/^([A-Za-z]+)-\d+-(\d+)$/);
  return m ? `${m[1]}-${m[2].slice(-4)}` : invoiceNo.slice(-4);
}
function fmtShortDate(dateStr?: string | null) {
  if (!dateStr) return '—';
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function dueDateDisplay(dateStr: string) {
  const due   = new Date(dateStr + 'T00:00:00');
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff  = Math.round((due.getTime() - today.getTime()) / 86_400_000);
  const label = due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return {
    label,
    badge:     diff === 0 ? 'Today' : diff > 0 ? `${diff}d left` : `${Math.abs(diff)}d over`,
    isOverdue: diff < 0,
    isSoon:    diff >= 0 && diff <= 7,
  };
}
// A single invoice's line items can carry different categories (that's the
// whole point of the feature — one parcel split Invoice-portion/Cash-portion
// across two lines) — 'mixed' when they don't all agree, undefined only if
// the invoice somehow has no line items. A missing per-line category (legacy
// data from before this feature existed) counts as 'books-real', matching
// the line-item default everywhere else.
function invoiceCategoryOf(inv: SaleInvoice): TransactionCategory | 'mixed' | undefined {
  if (!inv.lineItems || inv.lineItems.length === 0) return undefined;
  const first = inv.lineItems[0].category ?? 'books-real';
  return inv.lineItems.every(li => (li.category ?? 'books-real') === first) ? first : 'mixed';
}
// Deep-clean: also strips undefined out of nested plain objects/arrays (e.g. the
// lineItems array embedded in a sale/purchase doc), which a shallow, top-level-only
// filter misses — Firestore rejects a write with ANY undefined anywhere in it, no
// matter how deeply nested. Firestore sentinels (serverTimestamp(), Timestamp, etc.)
// are class instances, not plain object literals, so they pass through untouched.
function stripUndef<T>(obj: T): T {
  if (Array.isArray(obj)) return obj.map(v => stripUndef(v)) as unknown as T;
  if (obj !== null && typeof obj === 'object' && (obj as object).constructor === Object) {
    return Object.fromEntries(
      Object.entries(obj as Record<string, unknown>)
        .filter(([, v]) => v !== undefined)
        .map(([k, v]) => [k, stripUndef(v)])
    ) as T;
  }
  return obj;
}
function round2(n: number) { return Math.round(n * 100) / 100; }

// ── Per-line broker commission (Broker1/Broker2 on each line item) ─────────
// Completes the per-line broker feature left at "captured, not computed" —
// same helper as Purchase.tsx's own copy (duplicated per this app's
// per-page-helper convention). Unlike Purchase, Sales has no working
// header-level broker anymore (removed outright — see SaleHeader), so this
// is now the ONLY thing driving broker commission on a sale.
function computeLineBrokerCommissions(
  // Brokers earn on the full amount — before the sale's Less % (grossAmountUSD).
  lineItems: Pick<SaleLineItem, 'amountUSD' | 'grossAmountUSD' | 'broker1EntityId' | 'broker1CommissionPercent' | 'broker2EntityId' | 'broker2CommissionPercent'>[],
): Map<string, number> {
  const totals = new Map<string, number>();
  for (const li of lineItems) {
    if (li.broker1EntityId && li.broker1CommissionPercent) {
      const amt = round2((li.grossAmountUSD ?? li.amountUSD ?? 0) * li.broker1CommissionPercent / 100);
      totals.set(li.broker1EntityId, round2((totals.get(li.broker1EntityId) ?? 0) + amt));
    }
    if (li.broker2EntityId && li.broker2CommissionPercent) {
      const amt = round2((li.grossAmountUSD ?? li.amountUSD ?? 0) * li.broker2CommissionPercent / 100);
      totals.set(li.broker2EntityId, round2((totals.get(li.broker2EntityId) ?? 0) + amt));
    }
  }
  return totals;
}
const lineBrokerAccrualId = (saleId: string, brokerEntityId: string) =>
  entryId('broker_accrual', `${saleId}_${brokerEntityId}`);

// Same normalization as Purchase.tsx/InventoryMerge.tsx — keys the shared
// itemCodes/{codeId}/history ledger, so this must resolve identically no
// matter which page writes to it.
function normalizeCode(code: string): string {
  return code.trim().toUpperCase().replace(/[^A-Z0-9_-]/g, '_').slice(0, 400);
}
// carat - whatever's actively held by concurrent memos (see InventoryMemo in
// types/index.ts). This, not the item's own `carat`, is the real ceiling on
// how much of a lot can be sold — an item stays 'in-stock' even while part
// of it is out on memo, so `status` alone doesn't gate this. Rate math
// (cost/ct, price/ct) still divides by the FULL `carat` — only quantity caps
// use this.
function availableCarat(item: InventoryItem): number {
  return round2(Math.max(0, item.carat - (item.memoCommittedCarat ?? 0)));
}

// ── The Dummy/Books Adjustment code is a running balance, not a stone ──
// Selling from it has NO carat ceiling — it may go below zero (sell 100 ct
// of Books stock while it only holds 30 ct, because future Cash/Real sales
// will credit it back up). And a Dummy line's cost is its own sale amount,
// not an average: the sale subtracts exactly its carats and its amount from
// the Dummy balance, and whatever's left (value ÷ carats) is the new
// cost/ct — e.g. 30 ct/$15,000 − 100 ct × $600 → −70 ct/−$45,000. So a
// Dummy line always carries zero margin.
// ── Less % — the customer keeps this share for his own client ─────────────
// The user types the agreed (full) price; the line then carries the NET
// price (full × (1 − Less%)) in salePricePerCt / amountUSD — what the
// customer pays and the invoice prints — plus the full price in
// grossPricePerCt / grossAmountUSD (shown in the form, and what broker
// commission is worked out on). With no Less %, the gross fields are cleared
// and the line is exactly as before.
function applyLess(li: SaleLineItem, lessPercent: number): SaleLineItem {
  const gross = li.grossPricePerCt ?? li.salePricePerCt;
  if (!(lessPercent > 0)) {
    return { ...li, salePricePerCt: gross, amountUSD: gross * li.carat, grossPricePerCt: undefined, grossAmountUSD: undefined };
  }
  const net = round2(gross * (1 - lessPercent / 100));
  return { ...li, grossPricePerCt: gross, grossAmountUSD: gross * li.carat, salePricePerCt: net, amountUSD: net * li.carat };
}
// Indian trading system: the agreed price per ct, then the line's own
// Less 1 % and Less 2 % give the final price. That final price is what the
// rest of the app sees (Keep % then works on it exactly as in Belgium, and
// brokerage is on it). The agreed price and the two % are only for details.
function applyIndianLess(li: SaleLineItem): SaleLineItem {
  const agreed = li.agreedPricePerCt ?? li.grossPricePerCt ?? li.salePricePerCt;
  const hasLess = (li.less1Percent ?? 0) > 0 || (li.less2Percent ?? 0) > 0;
  const final = hasLess ? round2(lineNet(agreed, li.less1Percent, li.less2Percent)) : agreed;
  return {
    ...li, agreedPricePerCt: hasLess ? agreed : undefined,
    salePricePerCt: final, amountUSD: final * li.carat, grossPricePerCt: undefined, grossAmountUSD: undefined,
  };
}
const parseLess = (v: string) => { const n = parseFloat(v); return n > 0 && n < 100 ? n : 0; };

function withDummyCost(li: SaleLineItem, dummyId: string | undefined): SaleLineItem {
  return dummyId && li.inventoryItemId === dummyId
    ? { ...li, costUSD: li.amountUSD, costingUSD: li.amountUSD }
    : li;
}

function computeDateRange(
  dateFilter: DateFilter, customFrom: string, customTo: string,
): { start: string; end: string } | null {
  const now = new Date();
  switch (dateFilter) {
    case 'this-month':
      return { start: toDateStr(new Date(now.getFullYear(), now.getMonth(), 1)), end: toDateStr(new Date(now.getFullYear(), now.getMonth() + 1, 1)) };
    case '3-months':
      return { start: toDateStr(new Date(now.getFullYear(), now.getMonth() - 2, 1)), end: toDateStr(new Date(now.getFullYear(), now.getMonth() + 1, 1)) };
    case 'this-year':
      return { start: toDateStr(new Date(now.getFullYear(), 0, 1)), end: toDateStr(new Date(now.getFullYear() + 1, 0, 1)) };
    case 'custom': {
      if (!customFrom || !customTo) return null;
      const endDate = new Date(customTo + 'T00:00:00');
      endDate.setDate(endDate.getDate() + 1);
      return { start: customFrom, end: toDateStr(endDate) };
    }
  }
}

// `id` is a per-ROW key, distinct from `inventoryItemId` — two rows can now
// reference the same inventory item (selling the same lot at two prices via
// "+ Add Row"), so every row needs its own identity independent of its source.
let _slRowKey = 0;

// Every broker on a sale: the invoice-level one (older sales) plus Broker 1 /
// Broker 2 set on each line (current layout), once each, in order.
function saleBrokerIds(inv: SaleInvoice): string[] {
  const ids = [inv.brokerEntityId, ...(inv.lineItems ?? []).flatMap(li => [li.broker1EntityId, li.broker2EntityId])];
  return [...new Set(ids.filter((x): x is string => !!x))];
}

// The Description typed on the stone's purchase line (or opening stock row),
// filled into the sale line when the Code is picked. Older stones don't
// carry it on the stock doc: SaleAddModal reads it from the purchase line.
function stoneDescription(item: InventoryItem): string | undefined {
  if (item.description) return item.description;
  if (item.purchaseId === 'opening' && item.notes) return item.notes;   // opening stock kept it in notes
  return undefined;
}
// purchase invoice id → (line id → description), so each invoice is read once.
const purchaseDescCache = new Map<string, Map<string, string>>();
async function purchaseLineDescription(db: Firestore, item: InventoryItem): Promise<string | undefined> {
  if (!item.purchaseId || item.purchaseId === 'opening' || !item.lineItemId) return undefined;
  let lines = purchaseDescCache.get(item.purchaseId);
  if (!lines) {
    const snap = await getDoc(doc(db, 'invoices', item.purchaseId));
    lines = new Map();
    const li = (snap.exists() ? (snap.data() as PurchaseInvoice).lineItems : []) ?? [];
    li.forEach(l => { if (l.id && l.description) lines!.set(l.id, l.description); });
    purchaseDescCache.set(item.purchaseId, lines);
  }
  return lines.get(item.lineItemId);
}

function inventoryToLineItem(item: InventoryItem, caratOverride?: number): SaleLineItem {
  const carat = caratOverride ?? item.carat;
  const costPerCt     = item.carat > 0 ? item.costingUSD / item.carat : 0;
  const baseCostPerCt = item.carat > 0 ? item.costUSD   / item.carat : 0;
  // Suggested starting Price/ct — rounded to a clean 2 decimals so the
  // editable input shows e.g. "1003.71" instead of inventory's raw division
  // precision (e.g. "1003.7130081300813"). Cost figures below stay at full
  // precision — only this user-facing, user-editable default gets rounded.
  const suggestedPricePerCt = round2(costPerCt);
  return {
    id: `${item.id}_${++_slRowKey}`,
    inventoryItemId: item.id,
    itemType: item.type === 'polished-certified' ? 'certified' : 'loose',
    shape: item.shape, carat, color: item.color, clarity: item.clarity,
    lab: item.lab, certificateNo: item.certificateNo,
    size: item.size, subSize: item.subSize, sku: item.sku, code: item.code,
    description: stoneDescription(item),
    costUSD: baseCostPerCt * carat, costingUSD: costPerCt * carat,
    amountUSD: suggestedPricePerCt * carat,
    salePricePerCt: suggestedPricePerCt,
    category: 'books-real',
  };
}

// Building a line item straight from an active memo (Sales' "Customer's
// Memos" panel — see SaleAddModal) instead of the general item picker: carat
// is exactly what's on that memo (never editable — closing a memo means
// selling exactly what's out, not more or less; sell the rest of the parcel
// as a separate ordinary line item if needed), and price defaults to
// whatever was already agreed on the memo rather than cost.
function memoToLineItem(item: InventoryItem, memo: InventoryMemo): SaleLineItem {
  const carat = memo.carat;
  const costPerCt     = item.carat > 0 ? item.costingUSD / item.carat : 0;
  const baseCostPerCt = item.carat > 0 ? item.costUSD   / item.carat : 0;
  const pricePerCt = memo.pricePerCt ?? round2(costPerCt);
  return {
    id: `${item.id}_${++_slRowKey}`,
    inventoryItemId: item.id,
    itemType: item.type === 'polished-certified' ? 'certified' : 'loose',
    shape: item.shape, carat, color: item.color, clarity: item.clarity,
    lab: item.lab, certificateNo: item.certificateNo,
    size: item.size, subSize: item.subSize, sku: item.sku, code: item.code,
    description: memo.description || stoneDescription(item),
    costUSD: baseCostPerCt * carat, costingUSD: costPerCt * carat,
    amountUSD: pricePerCt * carat,
    salePricePerCt: pricePerCt,
    sourceMemoId: memo.id,
    category: 'books-real',
  };
}

// ── Partial-carat sale planning ──────────────────────────────────────────────
// This app tracks inventory purely in carat + Cost/ct. Selling less than a
// loose lot's full carat weight must never disturb its Cost/ct — only carat
// and the derived cost totals scale down proportionally. When a sale is less
// than the full lot, a "sold slice" doc is spun off to record exactly what
// left the building (immutable, points back via splitFromId); the remainder
// keeps its own identity and Cost/ct, still in stock. Selling the full amount
// skips the split entirely — the source item itself just flips to 'sold',
// exactly like before this feature existed.
//
// A profit/loss-aware "what would Cost/ct become" write-down was considered
// here (sell a slice above cost, the remainder gets cheaper) but explicitly
// scoped OUT of real data — real costUSD/costingUSD must stay exactly as
// they are today. That idea lives purely as a display-only estimate in the
// Inventory page's Item History modal (see ITEM_HISTORY_CAP/simulateCostPerCt
// in Inventory.tsx), computed client-side from the read-only ledger and never
// written back here.

interface SplitPlan {
  finalLineItems:      SaleLineItem[];
  wholeSaleUpdates:     { id: string; soldAmountUSD: number; memoCommittedCarat?: number }[];
  sourceShrinkUpdates:  { id: string; carat: number; costingUSD: number; costUSD: number; soldOut?: boolean; memoCommittedCarat?: number; forceInStock?: boolean }[];
  newSliceDocs:         { ref: DocumentReference; data: Record<string, unknown> }[];
  // One entry per sold item/slice that carries a Code — written to
  // itemCodes/{codeId}/history so "we sold" shows up in that Code's ledger
  // alongside any memo-out/memo-in events, regardless of which specific
  // InventoryItem doc ends up holding the sale.
  codeHistoryEntries:  { codeId: string; data: Record<string, unknown> }[];
  // One entry per line item that closes an active memo (sourceMemoId set).
  // Selling less than the memo's full carat splits it, same "slice" idea as
  // an ordinary partial sale: originalUpdate shrinks the existing doc to the
  // remainder and keeps it 'active' (still on memo, still returnable/
  // sellable later); newClosedDoc — present only for a partial close — is a
  // brand-new immutable doc recording exactly the carat THIS sale took.
  // finalLineItems[i].sourceMemoId is repointed at that new doc (see the
  // memoIdPatch pass below) so a later reversal targets the slice that
  // actually represents this sale, not the original (now-remainder) memo.
  memoClosures: {
    memoRef: DocumentReference;
    originalUpdate: Record<string, unknown>;
    newClosedDoc?: { ref: DocumentReference; data: Record<string, unknown> };
  }[];
}

// Grouped by source, not by line: two+ line items can now reference the same
// source (selling the same lot at two different prices in one invoice via
// "+ Add Row"), so validation and shrink math must operate on the COMBINED
// carat requested against a source, not on each line independently — checking
// each line alone would let two lines together overdraw a lot without ever
// tripping a single over-limit check.
function planNewSales(
  db: Firestore,
  items: SaleLineItem[],
  sources: InventoryItem[],
  header: { toEntityId: string; saleDate: string },
  saleInvoiceId: string,
  saleInvoiceNo: string,
  liveMemos: Map<string, InventoryMemo>,
  dummyItemId?: string,
): SplitPlan {
  const finalLineItems: SaleLineItem[] = new Array(items.length);
  const wholeSaleUpdates: SplitPlan['wholeSaleUpdates'] = [];
  const codeHistoryEntries: SplitPlan['codeHistoryEntries'] = [];
  const newSliceDocs: SplitPlan['newSliceDocs'] = [];
  const sourceShrinkUpdates: SplitPlan['sourceShrinkUpdates'] = [];
  const memoClosures: SplitPlan['memoClosures'] = [];
  // index -> the new closed-slice doc id a partially-closed line's
  // finalLineItems entry must be repointed at (see SplitPlan.memoClosures).
  const memoIdPatch = new Map<number, string>();

  const groups = new Map<string, { source: InventoryItem; indices: number[] }>();
  items.forEach((_li, i) => {
    const source = sources[i];
    const g = groups.get(source.id) ?? { source, indices: [] };
    g.indices.push(i);
    groups.set(source.id, g);
  });

  groups.forEach(({ source, indices }) => {
    // How much of this group's carat is closing an active memo (sourceMemoId
    // set) rather than being freshly sold from availableCarat — that carat
    // draws from the memo's own held carat, a separate pool from
    // availableCarat, so it must NOT count against the availableCarat cap
    // below (only the "extra" ordinary carat does). It still needs to come
    // back OUT of the source's memoCommittedCarat, on top of the ordinary
    // carat/cost shrink, or it'd stay double-counted as still "held" even
    // though it just left as a sale.
    const memoCaratClosed = indices.reduce((s, i) => s + (items[i].sourceMemoId ? items[i].carat : 0), 0);
    indices.forEach(i => {
      const memoId = items[i].sourceMemoId;
      if (!memoId) return;
      const liveMemo = liveMemos.get(memoId);
      if (!liveMemo) throw new Error('Internal error: memo data missing for closure — please reload and try again.');
      const memoRef    = doc(db, 'inventory', source.id, 'memos', memoId);
      const closeCarat = round2(items[i].carat);
      // The memo may have shrunk (another partial return/sale) since this
      // line was added in the UI — re-check against the live carat rather
      // than trusting the client's, or `remaining` below could go negative.
      if (closeCarat > liveMemo.carat + 1e-6) {
        throw new Error(`${items[i].shape} ${items[i].carat.toFixed(2)}ct's memo now holds only ${liveMemo.carat.toFixed(2)}ct — it changed since this line was added. Please reload and try again.`);
      }
      // round2 — plain float subtraction (e.g. 100 - 33.33) routinely lands
      // a few bits off a clean 2-decimal value (66.670000000000002), which
      // then compounds every time this memo is split again. Rounded once
      // here, not just at display time, so it never accumulates in what's
      // actually stored.
      const remaining = round2(liveMemo.carat - closeCarat);
      if (remaining <= 1e-9) {
        // Full close — same as before this feature existed.
        memoClosures.push({
          memoRef,
          originalUpdate: {
            status: 'sold' as InventoryMemoStatus,
            soldDate: header.saleDate, saleInvoiceId,
          },
        });
      } else {
        // Partial close — the original doc shrinks to the remainder and
        // stays 'active' (still on memo for this customer); a new immutable
        // doc records exactly the slice this sale took.
        const closedRef = doc(collection(db, 'inventory', source.id, 'memos'));
        memoClosures.push({
          memoRef,
          originalUpdate: {
            carat: remaining,
            amountUSD: liveMemo.pricePerCt != null ? round2(liveMemo.pricePerCt * remaining) : liveMemo.amountUSD,
          },
          newClosedDoc: {
            ref: closedRef,
            data: stripUndef({
              ...liveMemo,
              carat: closeCarat,
              amountUSD: liveMemo.pricePerCt != null ? round2(liveMemo.pricePerCt * closeCarat) : liveMemo.amountUSD,
              status: 'sold' as InventoryMemoStatus,
              soldDate: header.saleDate, saleInvoiceId,
            }),
          },
        });
        memoIdPatch.set(i, closedRef.id);
      }
    });

    // The Dummy code: no carat ceiling, always sliced, cost = sale amount,
    // and its own balance just moves down (possibly below zero) — see
    // withDummyCost above.
    if (dummyItemId && source.id === dummyItemId) {
      let balCarat = source.carat, balCostUSD = source.costUSD, balCostingUSD = source.costingUSD;
      indices.forEach(i => {
        const li = items[i];
        const carat  = round2(li.carat);
        const amount = round2(li.amountUSD);
        const sliceRef = doc(collection(db, 'inventory'));
        newSliceDocs.push({
          ref: sliceRef,
          data: stripUndef({
            purchaseId: source.purchaseId, purchaseNo: source.purchaseNo, lineItemId: source.lineItemId,
            type: source.type, status: 'sold' as InventoryStatus,
            shape: source.shape, carat, color: source.color, clarity: source.clarity,
            code: source.code, category: source.category,
            costUSD: amount, costingUSD: amount,
            soldToEntityId: header.toEntityId, soldDate: header.saleDate, soldAmountUSD: amount,
            splitFromId: source.id,
            createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
          }),
        });
        if (source.code) {
          codeHistoryEntries.push({
            codeId: normalizeCode(source.code),
            data: {
              type: 'sale', date: header.saleDate, carat, entityId: header.toEntityId,
              pricePerCt: carat > 0 ? amount / carat : 0, amountUSD: amount, inventoryItemId: sliceRef.id,
              category: li.category, saleInvoiceId, saleInvoiceNo,
            },
          });
        }
        balCarat = round2(balCarat - carat);
        balCostUSD = round2(balCostUSD - amount);
        balCostingUSD = round2(balCostingUSD - amount);
        finalLineItems[i] = { ...li, carat, inventoryItemId: sliceRef.id, costUSD: amount, costingUSD: amount };
      });
      sourceShrinkUpdates.push({ id: source.id, carat: balCarat, costUSD: balCostUSD, costingUSD: balCostingUSD, forceInStock: true });
      return;
    }

    const totalRequested = indices.reduce((s, i) => s + items[i].carat, 0);
    const extraRequested = totalRequested - memoCaratClosed;
    const avail = availableCarat(source);
    if (extraRequested > avail + 1e-6) {
      const label = `${source.shape} ${source.color}/${source.clarity}`;
      const heldNote = avail < source.carat - 1e-6 ? ` (${(source.carat - avail).toFixed(2)}ct is held on memo)` : '';
      throw new Error(indices.length === 1
        ? `${label}: cannot sell ${extraRequested.toFixed(2)}ct — only ${avail.toFixed(2)}ct available${heldNote}.`
        : `${label}: cannot sell ${extraRequested.toFixed(2)}ct across ${indices.length} lines — only ${avail.toFixed(2)}ct available${heldNote}.`);
    }
    const newMemoCommittedCarat = memoCaratClosed > 1e-9
      ? Math.max(0, (source.memoCommittedCarat ?? 0) - memoCaratClosed)
      : undefined;

    // Only a single line selling the FULL lot skips the slice — the source's
    // own doc becomes the sale record directly, exactly as before this
    // feature existed. Two-or-more lines sharing a source always slice every
    // one of them, even if together they exhaust it — never let more than one
    // line item resolve to the same raw source doc id.
    const isSingleFull = indices.length === 1 && items[indices[0]].carat >= source.carat - 1e-6;
    if (isSingleFull) {
      const i  = indices[0];
      const li = items[i];
      wholeSaleUpdates.push({ id: source.id, soldAmountUSD: li.amountUSD, memoCommittedCarat: newMemoCommittedCarat });
      finalLineItems[i] = { ...li, inventoryItemId: source.id, carat: source.carat, costUSD: source.costUSD, costingUSD: source.costingUSD };
      if (source.code) {
        codeHistoryEntries.push({
          codeId: normalizeCode(source.code),
          data: {
            type: 'sale', date: header.saleDate, carat: source.carat,
            entityId: header.toEntityId,
            pricePerCt: source.carat > 0 ? li.amountUSD / source.carat : 0,
            amountUSD: li.amountUSD, inventoryItemId: source.id,
            category: li.category,
            saleInvoiceId, saleInvoiceNo,
          },
        });
      }
      return;
    }

    const costPerCt     = source.carat > 0 ? source.costingUSD / source.carat : 0;
    const baseCostPerCt = source.carat > 0 ? source.costUSD   / source.carat : 0;
    let remainingCarat      = source.carat;
    let remainingCostingUSD = source.costingUSD;
    let remainingCostUSD    = source.costUSD;

    indices.forEach(i => {
      const li = items[i];
      // round2 — carat is user-entered but routed through several multiply/
      // divide steps upstream; rounding once here keeps every value derived
      // from it (the slice doc, the remainder below) clean to 2 decimals
      // instead of carrying forward whatever float noise crept in earlier.
      const carat           = round2(li.carat);
      const sliceCostingUSD = costPerCt * carat;
      const sliceCostUSD    = baseCostPerCt * carat;
      const sliceRef = doc(collection(db, 'inventory'));

      newSliceDocs.push({
        ref: sliceRef,
        data: stripUndef({
          purchaseId: source.purchaseId, purchaseNo: source.purchaseNo, lineItemId: source.lineItemId,
          type: source.type, status: 'sold' as InventoryStatus,
          shape: source.shape, carat, color: source.color, clarity: source.clarity,
          size: source.size, subSize: source.subSize, sku: source.sku, skuId: source.skuId,
          // Plain `code` (no `codeId`) — the slice doesn't claim the ItemCode
          // registry entry, it just stays findable under the same Code as the
          // remainder for history purposes. See CodeHistoryEntry.
          code: source.code,
          costUSD: sliceCostUSD, costingUSD: sliceCostingUSD,
          soldToEntityId: header.toEntityId, soldDate: header.saleDate, soldAmountUSD: li.amountUSD,
          splitFromId: source.id,
          createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
        }),
      });

      if (source.code) {
        codeHistoryEntries.push({
          codeId: normalizeCode(source.code),
          data: {
            type: 'sale', date: header.saleDate, carat,
            entityId: header.toEntityId,
            pricePerCt: carat > 0 ? li.amountUSD / carat : 0,
            amountUSD: li.amountUSD, inventoryItemId: sliceRef.id,
            category: li.category,
            saleInvoiceId, saleInvoiceNo,
          },
        });
      }

      remainingCarat      = round2(remainingCarat - carat);
      remainingCostingUSD -= sliceCostingUSD;
      remainingCostUSD    -= sliceCostUSD;
      finalLineItems[i] = { ...li, carat, inventoryItemId: sliceRef.id, costUSD: sliceCostUSD, costingUSD: sliceCostingUSD };
    });

    // Multiple partial slices can exactly exhaust a lot (unlike the single-line
    // case, which is caught by isSingleFull above) — when that happens, close
    // the source out instead of leaving a zero-carat 'in-stock' zombie item.
    const soldOut = remainingCarat < 1e-6;
    sourceShrinkUpdates.push({
      id: source.id,
      carat:      soldOut ? 0 : remainingCarat,
      costingUSD: soldOut ? 0 : remainingCostingUSD,
      costUSD:    soldOut ? 0 : remainingCostUSD,
      soldOut,
      memoCommittedCarat: newMemoCommittedCarat,
    });
  });

  // A partially-closed memo's line item must point at the new closed-slice
  // doc, not the original (which now represents the still-active
  // remainder) — otherwise a later reversal would reopen the wrong doc.
  memoIdPatch.forEach((newMemoId, i) => {
    finalLineItems[i] = { ...finalLineItems[i], sourceMemoId: newMemoId };
  });

  return { finalLineItems, wholeSaleUpdates, sourceShrinkUpdates, newSliceDocs, codeHistoryEntries, memoClosures };
}

function applySplitPlan(
  tx: Transaction, db: Firestore, plan: SplitPlan, header: { toEntityId: string; saleDate: string },
  createdBy: string,
) {
  plan.wholeSaleUpdates.forEach(u => tx.update(doc(db, 'inventory', u.id), stripUndef({
    status: 'sold' as InventoryStatus,
    soldToEntityId: header.toEntityId, soldDate: header.saleDate, soldAmountUSD: u.soldAmountUSD,
    memoCommittedCarat: u.memoCommittedCarat,
    updatedAt: serverTimestamp(),
  })));
  plan.sourceShrinkUpdates.forEach(u => tx.update(doc(db, 'inventory', u.id), stripUndef({
    carat: u.carat, costingUSD: u.costingUSD, costUSD: u.costUSD,
    memoCommittedCarat: u.memoCommittedCarat,
    updatedAt: serverTimestamp(),
    ...(u.soldOut ? { status: 'sold' as InventoryStatus, soldToEntityId: header.toEntityId, soldDate: header.saleDate } : {}),
    // The Dummy code never "sells out" — at 0 or below it's still the live
    // balance future Cash/Real sales credit back into.
    ...(u.forceInStock ? { status: 'in-stock' as InventoryStatus, soldToEntityId: null, soldDate: null, soldAmountUSD: null } : {}),
  })));
  plan.newSliceDocs.forEach(({ ref, data }) => tx.set(ref, data));
  plan.codeHistoryEntries.forEach(({ codeId, data }) => {
    tx.set(doc(collection(db, 'itemCodes', codeId, 'history')), stripUndef({
      ...data, createdAt: serverTimestamp(), createdBy,
    }));
  });
  plan.memoClosures.forEach(({ memoRef, originalUpdate, newClosedDoc }) => {
    tx.update(memoRef, { ...originalUpdate, updatedAt: serverTimestamp() });
    if (newClosedDoc) {
      tx.set(newClosedDoc.ref, { ...newClosedDoc.data, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
    }
  });
}

// ── Reversing a sale — deleting it, or dropping/resizing a line item on edit ──

// A source can now be sold directly from 'on-memo' status (memo→sale
// conversion), not just 'in-stock' — and neither the whole-sale update nor
// the shrink update ever clears memoToEntityId/memoDate/etc. when that
// happens (only `status` moves to 'sold'). So reading a source/whole item's
// CURRENT doc still tells us what it should revert back to: if those memo
// fields are sitting there, it was on memo, and reversal must restore
// 'on-memo' with them — not silently dump it into plain 'in-stock' and lose
// who it was out with.
interface MemoSnapshot {
  memoToEntityId?: string; memoDate?: string; memoReturnDate?: string;
  memoCarat?: number; memoPricePerCt?: number; memoAmountUSD?: number;
}
function memoSnapshotOf(d: InventoryItem): MemoSnapshot | null {
  if (!d.memoToEntityId) return null;
  return {
    memoToEntityId: d.memoToEntityId, memoDate: d.memoDate, memoReturnDate: d.memoReturnDate,
    memoCarat: d.memoCarat, memoPricePerCt: d.memoPricePerCt, memoAmountUSD: d.memoAmountUSD,
  };
}

interface ReversalPlan {
  restoreDeltas:    Map<string, { carat: number; costingUSD: number; costUSD: number; restoreStatus: InventoryStatus; memo: MemoSnapshot | null; memoCommittedCarat?: number }>;
  sliceIdsToDelete: string[];
  wholeIdsToRevert: { id: string; restoreStatus: InventoryStatus; memo: MemoSnapshot | null; memoCommittedCarat?: number }[];
  // A reversed line item that closed a memo (sourceMemoId) reopens it —
  // mirrors memoClosures in SplitPlan, the exact inverse operation.
  reopenMemos:      { sourceItemId: string; memoId: string }[];
}

// Inventory Activity reads itemCodes/{codeId}/history — reversing/deleting
// a sale must also remove the 'sale' entries it wrote there, or the rows
// keep showing for a sale that's been undone. A CodeHistoryEntry has no
// back-reference to the sale that wrote it, only `inventoryItemId` — but
// that already matches `SaleLineItem.inventoryItemId` exactly (the source
// item's id for a whole sale, or the slice's own id for a partial one), so
// `type === 'sale' && inventoryItemId === li.inventoryItemId` is a precise,
// safe correlation with no schema change needed.
//
// Must run BEFORE runTransaction — transactions only support tx.get() on a
// known DocumentReference, not an arbitrary collection query like this one.
// A stale read here can't corrupt anything: tx.delete() on a ref that no
// longer exists by commit time is a no-op.
async function findSaleCodeHistoryRefs(db: Firestore, items: SaleLineItem[]): Promise<DocumentReference[]> {
  const withCode = items.filter(li => li.code);
  const snaps = await Promise.all(withCode.map(li => getDocs(query(
    collection(db, 'itemCodes', normalizeCode(li.code!), 'history'),
    where('type', '==', 'sale'), where('inventoryItemId', '==', li.inventoryItemId),
  ))));
  return snaps.flatMap(s => s.docs.map(d => d.ref));
}

async function planSaleReversal(
  tx: Transaction, db: Firestore, itemsToReverse: SaleLineItem[],
): Promise<ReversalPlan> {
  const restoreDeltas = new Map<string, { carat: number; costingUSD: number; costUSD: number; restoreStatus: InventoryStatus; memo: MemoSnapshot | null; memoCommittedCarat?: number }>();
  const sliceIdsToDelete: string[] = [];
  const wholeIdsToRevert: ReversalPlan['wholeIdsToRevert'] = [];
  const reopenMemos: ReversalPlan['reopenMemos'] = [];
  if (itemsToReverse.length === 0) return { restoreDeltas, sliceIdsToDelete, wholeIdsToRevert, reopenMemos };

  // All reads before any writes — Firestore transactions require it.
  const directSnaps = await Promise.all(itemsToReverse.map(li => tx.get(doc(db, 'inventory', li.inventoryItemId))));
  const directData  = directSnaps.map(s => (s.exists() ? (s.data() as InventoryItem) : null));

  const splitFromIds = [...new Set(directData.filter((d): d is InventoryItem => !!d?.splitFromId).map(d => d.splitFromId!))];
  const indirectSnaps = await Promise.all(splitFromIds.map(id => tx.get(doc(db, 'inventory', id))));
  const indirectMap = new Map(splitFromIds.map((id, i) => [id, indirectSnaps[i].exists() ? (indirectSnaps[i].data() as InventoryItem) : null]));

  directData.forEach((d, i) => {
    if (!d) return;
    const li = itemsToReverse[i];
    if (d.splitFromId) {
      const src = indirectMap.get(d.splitFromId);
      const memo = src ? memoSnapshotOf(src) : null;
      const base = restoreDeltas.get(d.splitFromId) ?? {
        carat: src?.carat ?? 0, costingUSD: src?.costingUSD ?? 0, costUSD: src?.costUSD ?? 0,
        restoreStatus: (memo ? 'on-memo' : 'in-stock') as InventoryStatus, memo,
        memoCommittedCarat: src?.memoCommittedCarat,
      };
      restoreDeltas.set(d.splitFromId, {
        ...base,
        carat:      base.carat + li.carat,
        costingUSD: base.costingUSD + li.costingUSD,
        costUSD:    base.costUSD + li.costUSD,
        memoCommittedCarat: li.sourceMemoId ? (base.memoCommittedCarat ?? 0) + li.carat : base.memoCommittedCarat,
      });
      if (li.sourceMemoId) reopenMemos.push({ sourceItemId: d.splitFromId, memoId: li.sourceMemoId });
      sliceIdsToDelete.push(li.inventoryItemId);
    } else {
      const memo = memoSnapshotOf(d);
      wholeIdsToRevert.push({
        id: li.inventoryItemId, restoreStatus: memo ? 'on-memo' : 'in-stock', memo,
        memoCommittedCarat: li.sourceMemoId ? (d.memoCommittedCarat ?? 0) + li.carat : undefined,
      });
      if (li.sourceMemoId) reopenMemos.push({ sourceItemId: li.inventoryItemId, memoId: li.sourceMemoId });
    }
  });

  return { restoreDeltas, sliceIdsToDelete, wholeIdsToRevert, reopenMemos };
}

function applySaleReversal(tx: Transaction, db: Firestore, plan: ReversalPlan) {
  plan.restoreDeltas.forEach((delta, sourceId) => {
    tx.update(doc(db, 'inventory', sourceId), stripUndef({
      // Always reset status too, not just carat/cost: a source that was
      // fully consumed by multiple partial slices (see planNewSales' `soldOut`
      // handling) gets flipped to 'sold' even though it's not a slice itself —
      // restoring its carat without reviving its status would leave it stuck
      // 'sold' at a nonzero carat, invisible to `available`/on-memo lists
      // forever. Goes back to 'on-memo' (with its memo fields) rather than
      // 'in-stock' when that's what it was before a direct memo→sale.
      status: delta.restoreStatus,
      carat: delta.carat, costingUSD: delta.costingUSD, costUSD: delta.costUSD,
      memoCommittedCarat: delta.memoCommittedCarat,
      updatedAt: serverTimestamp(),
      ...(delta.memo ?? {}),
    }));
  });
  plan.sliceIdsToDelete.forEach(id => tx.delete(doc(db, 'inventory', id)));
  plan.wholeIdsToRevert.forEach(({ id, restoreStatus, memo, memoCommittedCarat }) => tx.update(doc(db, 'inventory', id), stripUndef({
    status: restoreStatus, soldToEntityId: null, soldDate: null, soldAmountUSD: null,
    memoCommittedCarat,
    updatedAt: serverTimestamp(),
    ...(memo ?? {}),
  })));
  // Reopening the memo(s) a reversed line item had closed — the inverse of
  // applySplitPlan's memoClosures.
  plan.reopenMemos.forEach(({ sourceItemId, memoId }) => {
    tx.update(doc(db, 'inventory', sourceItemId, 'memos', memoId), {
      status: 'active' as InventoryMemoStatus,
      soldDate: null, saleInvoiceId: null,
      updatedAt: serverTimestamp(),
    });
  });
}

// ── Cash/Real sales — crediting the shared Dummy/Books Adjustment code ──────
// See TransactionCategory in types/index.ts. A 'cash-real' sale line always
// shrinks its own source item normally (unchanged planNewSales/applySplitPlan
// above) — but that carat is now physically gone while the Books view still
// "believes" it's there, since nothing else told the books it left. This
// credits the same carat, plus its proportional COST basis (never the cash
// price — SaleLineItem.costUSD/costingUSD already ARE that proportional cost
// slice, computed by planNewSales), into ONE shared, lazily-created
// InventoryItem per business (BusinessSettings.dummyInventoryItemId,
// category:'books-dummy'). Reading it back down later is just an ordinary
// sale FROM that item — no special logic exists anywhere else for that half.
//
// Invoice-scoped (sums every cash-real line on the invoice, regardless of
// which source item each came from), unlike planNewSales/planSaleReversal's
// per-source grouping — doesn't fit either shape, so it's its own plan/apply
// pair, called alongside them at all three save sites (create/edit/delete).
// The delta (this invoice's new cash-real total minus what it credited last
// time it was saved) is what actually gets applied, so editing an invoice's
// categories/carats — or deleting it — self-corrects the shared running
// total instead of double-counting.

// The shared balance logic lives in utils/dummyCode.ts (Purchase's
// 'books-dummy' lines credit the same balance). This adds the Sales-only
// counter bookkeeping on top.
interface DummyCreditPlan extends DummyDeltaPlan {
  newItemCodeCounter?: number;     // only when isNewDummyItem — fold into the caller's own counters/{businessId} write
}

// Same walk-forward-to-a-free-Code logic as Purchase.tsx's nextFreeCode —
// duplicated per this codebase's per-page-helper convention (this file
// already duplicates normalizeCode from the same source).
async function nextFreeCode(
  tx: Transaction, db: Firestore, cursor: { n: number }, reserved: Set<string>,
): Promise<string> {
  for (let attempts = 0; attempts < 200; attempts++) {
    cursor.n += 1;
    const candidate   = String(cursor.n);
    const candidateId = normalizeCode(candidate);
    if (reserved.has(candidateId)) continue;
    const snap = await tx.get(doc(db, 'itemCodes', candidateId));
    if (!snap.exists()) { reserved.add(candidateId); return candidate; }
  }
  throw new Error('Could not auto-generate a unique Code for the Dummy/Books Adjustment item.');
}

// All reads — no writes — so this can run anywhere before the caller's own
// first write, same "reads before writes" rule as planSaleReversal above.
async function planDummyCreditDelta(
  tx: Transaction, db: Firestore, businessId: string, saleInvoiceId: string,
  finalLineItems: SaleLineItem[], settings: BusinessSettings | null,
  counterSnap: DocumentSnapshot | null,
): Promise<DummyCreditPlan | null> {
  const aggregate = finalLineItems.reduce((s, li) => li.category === 'cash-real'
    ? { carat: s.carat + li.carat, costUSD: s.costUSD + li.costUSD, costingUSD: s.costingUSD + li.costingUSD }
    : s, { carat: 0, costUSD: 0, costingUSD: 0 });

  const cursor = { n: counterSnap?.exists() ? (counterSnap.data()?.itemCode ?? settings?.itemCodeStart ?? 0) : (settings?.itemCodeStart ?? 0) };
  const plan = await planDummyDelta(tx, db, {
    businessId, historyId: `dummycredit_${saleInvoiceId}`, aggregate,
    cursor, reserved: new Set(), nextCode: nextFreeCode,
  });
  if (!plan) return null;

  // The Books Adjustment/Dummy code can only ever be sold tagged
  // Dummy/Books — that's the entire point of it (reconciling the Books
  // view back down). Client-side the picker is locked for this row (see
  // SaleAddModal's committed-row render), but this is the authoritative
  // backstop.
  if (finalLineItems.some(li => li.inventoryItemId === plan.dummyRef.id && li.category !== 'books-dummy')) {
    throw new Error("The Books Adjustment code can only be sold with the Dummy/Books category.");
  }

  // Only a REAL conflict: this invoice both sells the Dummy item itself
  // (applySplitPlan will write to its doc) AND separately credits it via a
  // cash-real line elsewhere in the same invoice (applyDummyCreditDelta
  // would also write to it) — two writes to the same doc in one
  // transaction. The far more common case — an ordinary reconciling sale
  // FROM the Dummy item, with no cash-real line alongside it — must be
  // allowed; that's the primary way this feature gets used.
  if (plan.needsItemWrite && finalLineItems.some(li => li.inventoryItemId === plan.dummyRef.id)) {
    throw new Error("The Books Adjustment code can't be sold in the same invoice as a Cash/Real line — save them as separate sales.");
  }

  return { ...plan, newItemCodeCounter: plan.isNewDummyItem ? cursor.n : undefined };
}

function applyDummyCreditDelta(
  tx: Transaction, db: Firestore, plan: DummyCreditPlan | null,
  header: { toEntityId: string; saleDate: string }, invoiceNo: string, createdBy: string,
) {
  applyDummyDelta(tx, db, plan, {
    type: 'dummy-credit', date: header.saleDate, entityId: header.toEntityId,
    category: 'cash-real', notes: `Cash/Real credit — Sale ${invoiceNo}`, createdBy,
  });
}

// ── Icons ─────────────────────────────────────────────────────────────────────

function IconEdit() {
  return <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <path d="M9.5 2.5l2 2-7 7H2.5v-2l7-7z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
  </svg>;
}
function IconTrash() {
  return <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <path d="M2 3.5h10M5.5 3.5V2.5h3v1M4 3.5l.667 8h4.666L10 3.5"
      stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
function IconLockMini() {
  return <svg width="10" height="10" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0 }}>
    <rect x="2.5" y="5.5" width="7" height="5" rx="1.2" stroke="currentColor" strokeWidth="1.2" />
    <path d="M4 5.5V4a2 2 0 0 1 4 0v1.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>;
}
function IconPlus() {
  return <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <path d="M7 2v10M2 7h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>;
}
function IconPrint() {
  return <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path d="M4.5 6V2h7v4M3 6.5h10a1.5 1.5 0 0 1 1.5 1.5v4H12v2H4v-2H1.5V8A1.5 1.5 0 0 1 3 6.5Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    <path d="M4 10.5h8" stroke="currentColor" strokeWidth="1.3" />
  </svg>;
}
function IconPdf() {
  return <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path d="M4 1.5h5.5L12.5 4.5V13.5a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2.5a1 1 0 0 1 1-1Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    <path d="M9.5 1.5V4.5H12.5" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    <path d="M4.8 11.5V8.2h.9c.55 0 1 .4 1 1v.3c0 .6-.45 1-1 1h-.9M7.8 11.5V8.2h1c.6 0 1.05.45 1.05 1.05v1.2c0 .6-.45 1.05-1.05 1.05h-1ZM11 11.5V8.2h1.4M11 9.9h1.2"
      stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
function IconCheck() {
  return <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
    <path d="M2.5 7.2l3 3 6-6.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
function IconChevronDown() {
  return <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <path d="M3 5.5l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
function IconColumns() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <rect x="1.5" y="2" width="3.2" height="10" rx="1" stroke="currentColor" strokeWidth="1.3" />
      <rect x="5.9" y="2" width="3.2" height="10" rx="1" stroke="currentColor" strokeWidth="1.3" />
      <rect x="10.3" y="2" width="2.2" height="10" rx="1" stroke="currentColor" strokeWidth="1.3" />
    </svg>
  );
}
function IconSortArrow({ dir }: { dir: 'asc' | 'desc' }) {
  return <svg width="9" height="9" viewBox="0 0 10 10" fill="none"
    style={{ transform: dir === 'asc' ? 'rotate(180deg)' : undefined, transition: 'transform 0.12s' }}>
    <path d="M2 3.5L5 7l3-3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
// Mirrors Purchase.tsx's SortableTh exactly — click a header to sort by it,
// click again to flip direction.
function SortableTh({ label, sortKey, activeKey, dir, onSort, align, minWidth, width, filter }: {
  label: string; sortKey: SortKey; activeKey: SortKey; dir: 'asc' | 'desc';
  onSort: (k: SortKey) => void; align?: 'right' | 'center'; minWidth?: number;
  /** A fixed width (frozen and name columns): the column never resizes. */
  width?: number;
  /** Excel-style filter button for this column (ColumnFilterButton). */
  filter?: ReactNode;
}) {
  const active = activeKey === sortKey;
  return (
    <th
      className={`th-sortable${active ? ' active' : ''}`}
      style={{ textAlign: align, minWidth: width ?? minWidth, width, maxWidth: width }}
      onClick={() => onSort(sortKey)}
    >
      <span className="th-sort-inner" style={{ justifyContent: align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start' }}>
        {label}
        <span className="th-sort-arrow"><IconSortArrow dir={active ? dir : 'desc'} /></span>
        {filter}
      </span>
    </th>
  );
}
function IconExport() {
  return <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M7 1v8M4 6.5L7 9.5l3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M1.5 10.5v1.5a1 1 0 001 1h9a1 1 0 001-1v-1.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>;
}
function IconPay() {
  return <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <rect x="1" y="3" width="12" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M1 6h12" stroke="currentColor" strokeWidth="1.4" />
    <circle cx="4" cy="9" r="1" fill="currentColor" />
  </svg>;
}
function IconSearch({ size = 14 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
    <circle cx="6" cy="6" r="4" stroke="currentColor" strokeWidth="1.4" />
    <path d="M9.5 9.5l2.5 2.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>;
}
function IconTrendUp({ size = 14 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
    <path d="M1.5 10.5l3.5-4 2.5 2.5 4.5-5.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9 3h3v3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
function IconCalendar({ size = 13 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
    <rect x="1.5" y="2.5" width="11" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M1.5 6h11M4.5 1v3M9.5 1v3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>;
}

// ── Shared inline table styles ────────────────────────────────────────────────

const TI: React.CSSProperties = {
  width: '100%', minWidth: 0, padding: '6px 9px', fontSize: 11,
  height: 32,
  border: '1.5px solid var(--border)', borderRadius: 'var(--r-sm)',
  background: 'var(--surface)', color: 'var(--text-1)',
  outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
};
const TH: React.CSSProperties = {
  padding: '7px 10px', fontWeight: 600, fontSize: 10.5,
  color: 'var(--text-2)', textAlign: 'left', whiteSpace: 'nowrap',
  background: 'var(--surface-2)', borderBottom: '1.5px solid var(--border)',
};
const TD: React.CSSProperties = {
  padding: '6px 8px', verticalAlign: 'middle',
  borderBottom: '1px solid var(--surface-2)',
};
// Sticky header cell for the Add/Edit Sale line-items table — keeps the
// column labels pinned while scrolling to a row further down.
const STH: React.CSSProperties = { ...TH, position: 'sticky', top: 0, zIndex: 2 };

// ── Excel-style grid cell styles — SaleAddModal's own copy of the same
// system Purchase.tsx's grid uses (connected borderless cells via the
// shared .excel-grid-table/.grid-cell CSS + useGridKeyNav) — NOT the
// TI/TH/TD/STH above, which the read-only line-items sub-table (and other
// tables in this file) still rely on unchanged. ─────────────────────────
const SGTD: React.CSSProperties = { padding: 0, verticalAlign: 'middle' };
const SGTD_TEXT: React.CSSProperties = { ...SGTD, padding: '6px 8px', fontSize: 11 };
const SGTH: React.CSSProperties = { ...STH, ...SGTD_TEXT, fontSize: 9.5, fontWeight: 700 };
// Red "to fix" tint for an empty required cell — same as Purchase's grids.
const SALE_MISSING_BG = 'color-mix(in srgb, var(--danger) 12%, transparent)';
type SaleReqField = 'carat' | 'price';
const SALE_REQUIRED: SaleReqField[] = ['carat', 'price'];
function saleCellMissing(li: SaleLineItem, f: SaleReqField): boolean {
  return f === 'carat' ? !(li.carat > 0) : !(li.salePricePerCt > 0);
}
const saleMissBg = (li: SaleLineItem, f: SaleReqField): React.CSSProperties =>
  saleCellMissing(li, f) ? { background: SALE_MISSING_BG } : {};

// ── PayBadge ──────────────────────────────────────────────────────────────────

function PayBadge({ inv }: { inv: SaleInvoice }) {
  const status = inv.paymentStatus ?? 'unpaid';
  const paid   = inv.paidUSD ?? 0;
  const pct    = inv.totalAmountUSD > 0 ? Math.round((paid / inv.totalAmountUSD) * 100) : 0;
  if (status === 'paid')    return <span className="badge" style={{ background: 'var(--success-bg)', color: 'var(--success)', border: '1px solid var(--success)', fontSize: 11 }}>Paid</span>;
  if (status === 'partial') return <span className="badge" style={{ background: 'var(--warning-bg)', color: '#c2410c', border: '1px solid var(--warning)', fontSize: 11 }}>{pct}% paid</span>;
  return <span className="badge" style={{ background: 'var(--danger-bg)', color: 'var(--danger)', border: '1px solid var(--danger)', fontSize: 11 }}>Unpaid</span>;
}

function SentBadge({ sent }: { sent: boolean }) {
  return sent
    ? <span className="badge" style={{ background: 'var(--success-bg)', color: 'var(--success)', border: '1px solid var(--success)', fontSize: 11 }}>Sent</span>
    : <span className="badge" style={{ background: 'var(--surface-2)', color: 'var(--text-3)', border: '1px solid var(--border)', fontSize: 11 }}>Not Sent</span>;
}

// ── Header facts — Company Name / Lot No, matching Purchase, Inventory & AP ──

function VDivider() {
  return <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--border)' }} />;
}

function FactPanel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 14,
      background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 999, padding: '7px 18px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    }}>
      {children}
    </div>
  );
}

function MiniFact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ textAlign: 'right' }}>
      <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', marginBottom: 2 }}>
        {label}
      </div>
      <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-1)' }}>
        {value}
      </div>
    </div>
  );
}

function LotPill({ children }: { children: React.ReactNode }) {
  return (
    <span style={{
      display: 'inline-block', background: 'var(--primary-light)', color: 'var(--primary)',
      fontFamily: 'var(--font-num)', fontSize: 11, fontWeight: 700,
      padding: '2px 8px', borderRadius: 5, letterSpacing: '0.02em',
    }}>
      {children}
    </span>
  );
}

// ── The printable invoice document itself — pure function of its data, used
// both for the on-screen "View Invoice" page and, unchanged, as what
// actually prints (see SalesPage's `viewingInvoice` swap below). Always a
// fixed white/black palette regardless of the app's theme — a real document
// shouldn't render in dark mode (same reasoning as .landing-page forcing
// its own light tokens). settings is nullable here (SalesPage keeps its own
// raw BusinessSettings|null rather than the useSettings() hook's
// always-defaulted shape), so every field reads through `settings?.x`. ────

// ── Collapsible group for the Invoice Options side panel — click the
// uppercase label to expand/collapse its contents. Plain conditional
// render (no height-animation trick) since this is a compact settings-style
// panel, not a marketing page. Starts closed by default; pass defaultOpen
// for a group that should be visible right away.
function OptionsSection({ title, defaultOpen, children }: {
  title: string; defaultOpen?: boolean; children: React.ReactNode;
}) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div style={{ marginBottom: 18 }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%',
          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
          fontSize: 11, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.04em',
        }}
        aria-expanded={open}
      >
        {title}
        <span style={{
          display: 'flex', color: 'var(--text-3)',
          transform: open ? 'rotate(180deg)' : undefined, transition: 'transform 0.18s ease',
        }}>
          <IconChevronDown />
        </span>
      </button>
      {open && <div style={{ marginTop: 10 }}>{children}</div>}
    </div>
  );
}

type DocRow =
  | { kind: 'item';   key: string; sr: number; li: SaleInvoice['lineItems'][number] }
  | { kind: 'charge'; key: string; c: SaleExtraCost };

// 1.085 → "1.0850": four decimals, the usual way an FX rate is quoted.
function fmtRate(r: number) { return r.toFixed(4); }

function InvoiceDocument({ invoice, entity, settings, business, bankAccount }: {
  invoice: SaleInvoice;
  entity: Entity | undefined;
  settings: BusinessSettings | null;
  business: { businessName: string } | null;
  bankAccount: BankAccount | undefined;
}) {
  const companyName = settings?.invoiceCompanyName || business?.businessName || '—';
  // Pincode, City and Country share one line: "2018 Antwerp, Belgium".
  const companyAddressLines = [
    settings?.invoiceAddressLine1,
    settings?.invoiceAddressLine2,
    [
      [settings?.invoicePostalCode, settings?.invoiceCity].filter(Boolean).join(' '),
      settings?.invoiceCountry,
    ].filter(Boolean).join(', '),
  ].filter((l): l is string => !!l && l.trim().length > 0);

  const opts = invoice.invoiceOptions ?? {};
  // "Hide customer country" drops the country line from BOTH Bill To and
  // Ship To. Filtered by value (not "drop the last line") so it stays
  // right when an address has no country at all.
  const withoutCountry = (a: EntityAddress | undefined | null) => {
    const lines = formatAddressLines(a);
    return opts.hideCustomerCountry && a?.country ? lines.filter(l => l !== a.country!.trim()) : lines;
  };
  const billTo = entity?.billTo ?? legacyToBillTo(entity?.address);
  const billLines = withoutCountry(billTo);
  // Ship To always renders as its own box — Bill To's address when "same
  // as" is set (or no distinct Ship To was ever entered).
  const shipTo = entity?.shipToSameAsBillTo ? billTo : (entity?.shipTo ?? billTo);
  const shipLines = withoutCountry(shipTo);

  // Shown in the invoice's own currency when it has one (books stay in Settings' currency).
  const baseCur = settings?.currency ?? 'USD';
  const invCur  = invoiceCurrencyOf(invoice, baseCur);
  const invRate = invoiceRateOf(invoice, baseCur);
  const money = (v: number) => invRate ? fmtMoney((v ?? 0) / invRate, invCur) : fmtMoney(v ?? 0, baseCur);
  const accent = opts.accentColor || '#0F172A';
  const totalCarat = invoice.lineItems.reduce((s, li) => s + li.carat, 0);
  const goodsUSD = invoice.totalAmountUSD - (invoice.chargesUSD ?? 0) - (invoice.taxTotalUSD ?? 0);
  const avgPricePerCt = totalCarat > 0 ? goodsUSD / totalCarat : 0;
  // Only the charges re-billed to the customer appear on their invoice.
  const charges = (invoice.extraCosts ?? []).filter(c => c.chargeToCustomer);
  const showPayment = !opts.hidePaymentInfo && !!bankAccount;
  const showTotals = !opts.hideTotal;
  const descStyle = opts.hideItemPrice ? { width: '59%' } : undefined;

  const rows: DocRow[] = [
    ...invoice.lineItems.map((li, i): DocRow => ({ kind: 'item', key: li.id, sr: i + 1, li })),
    ...charges.map((c): DocRow => ({ kind: 'charge', key: `c-${c.saleExpenseId}`, c })),
  ];

  // Footer: the customer memo (left, small, up to ~7 lines) and the footer
  // image (right). Settings' general footer note sits under the memo.
  const memo = invoice.notes?.trim();
  const footerNote = opts.hideFooterNote ? undefined : settings?.invoiceFooterNote?.trim();
  const footerImg = opts.hideFooterImage ? undefined : settings?.invoiceRjcCertDataUrl;
  const footerImgH = settings?.invoiceFooterImageHeight ?? FOOTER_IMG_H_DEFAULT;
  const hasFooter = !!(memo || footerNote || footerImg);

  // ── Pagination ────────────────────────────────────────────────────────
  const measureRef = useRef<HTMLDivElement>(null);
  const [pages, setPages] = useState<{ sig: string; pages: number[][] } | null>(null);
  const sig = `${invoice.id}:${rows.length}`;
  useEffect(() => {
    const el = measureRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const next = paginateRows(el, rows.length);
      if (!next) return;
      setPages(prev => (prev && prev.sig === sig && JSON.stringify(prev.pages) === JSON.stringify(next)) ? prev : { sig, pages: next });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [sig, rows.length]);
  const sheets: number[][] = pages?.sig === sig ? pages.pages : [rows.map((_, i) => i)];
  const pageCount = sheets.length;
  // Last sheet that has rows: it carries the Total row. An empty sheet
  // after it holds only the bank details + footer.
  const lastTableSheet = sheets.reduce((acc, idxs, i) => idxs.length > 0 ? i : acc, 0);

  const logoW = settings?.invoiceLogoWidth ?? LOGO_W_DEFAULT;

  // ── Pieces shared by every sheet ──────────────────────────────────────
  const top = (pageNo: number, count: number) => (
    <div data-m="top" style={{ display: 'flow-root' }}>
      <div className="invoice-doc-header">
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
          {settings?.invoiceLogoDataUrl && (
            <img src={settings.invoiceLogoDataUrl} alt="" className="invoice-doc-logo"
              style={{ width: logoW, height: Math.round(logoW * LOGO_RATIO) }} />
          )}
          <div>
            <div className="invoice-doc-company">{companyName}</div>
            {companyAddressLines.map((l, i) => <div key={i} className="invoice-doc-meta-line">{l}</div>)}
            {settings?.invoiceVatNumber && <div className="invoice-doc-meta-line">VAT: {settings.invoiceVatNumber}</div>}
            {(settings?.invoiceEmail || settings?.invoicePhone) && (
              <div className="invoice-doc-meta-line">
                {[settings?.invoiceEmail, settings?.invoicePhone].filter(Boolean).join(' · ')}
              </div>
            )}
          </div>
        </div>
        <div className="invoice-doc-facts">
          <div className="invoice-doc-eyebrow">Invoice</div>
          <div className="invoice-doc-fact-row">Invoice No.<strong title={invoice.invoiceNo || undefined}>{lotNoDisplay(invoice.invoiceNo)}</strong></div>
          <div className="invoice-doc-fact-row">Date<strong>{fmtShortDate(invoice.saleDate)}</strong></div>
          {count > 1 && <div className="invoice-doc-fact-row">Page<strong>{pageNo} of {count}</strong></div>}
        </div>
      </div>

      <div className="invoice-doc-address-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
        <div>
          <div className="invoice-doc-section-label">Bill To</div>
          <div className="invoice-doc-billto">
            <div style={{ fontWeight: 700, color: '#0F172A' }}>{entity?.companyName ?? '—'}</div>
            {billLines.map((l, i) => <div key={i}>{l}</div>)}
            {entity?.vatGstBtw && <div>VAT: {entity.vatGstBtw}</div>}
            {opts.showCustomerPhone && entity?.phone && <div>{entity.phone}</div>}
            {opts.showCustomerEmail && entity?.email && <div>{entity.email}</div>}
          </div>
        </div>
        <div>
          <div className="invoice-doc-section-label">Ship To</div>
          <div className="invoice-doc-billto">
            <div style={{ fontWeight: 700, color: '#0F172A' }}>{shipTo?.name || entity?.companyName || '—'}</div>
            {shipLines.map((l, i) => <div key={i}>{l}</div>)}
            {entity?.vatGstBtw && <div>VAT: {entity.vatGstBtw}</div>}
            {opts.showCustomerPhone && entity?.phone && <div>{entity.phone}</div>}
            {opts.showCustomerEmail && entity?.email && <div>{entity.email}</div>}
          </div>
        </div>
      </div>

      {/* Trade details: one bordered 4 × 2 grid (Terms, Ship Via, Insured
          By, Origin / Due Date, Salesman, CIF Details, Origin of Rough). */}
      <div className="invoice-doc-terms-grid">
        {([
          ['Terms',           invoice.terms ? `${invoice.terms} Days` : ''],
          ['Ship Via',        opts.shipVia],
          ['Insured By',      opts.insuredBy],
          ['Origin',          opts.origin],
          ['Due Date',        fmtShortDate(invoice.dueDate)],
          ['Salesman',        opts.salesman],
          ['CIF Details',     opts.cifDetails],
          ['Origin of Rough', opts.roughOrigin],
        ] as [string, string | undefined][]).map(([label, value]) => (
          <div key={label} className="invoice-doc-terms-cell">
            <div className="invoice-doc-payment-label">{label}</div>
            <div className="invoice-doc-payment-value">{value || '—'}</div>
          </div>
        ))}
      </div>
    </div>
  );

  const thead = (
    <thead data-m="thead">
      <tr>
        <th className="sr">Sr.</th>
        <th className="desc" style={descStyle}>Description</th>
        <th className="num carat">Carat</th>
        {!opts.hideItemPrice && <th className="num price">Rate/ct</th>}
        <th className="num amount">Amount</th>
      </tr>
    </thead>
  );

  const renderRow = (r: DocRow, measure: boolean) => r.kind === 'item' ? (
    <tr key={r.key} data-m-row={measure ? '' : undefined}>
      <td className="sr">{r.sr}</td>
      <td className="desc" style={descStyle}>{r.li.description || <span style={{ opacity: 0.4 }}>—</span>}</td>
      <td className="num carat">{r.li.carat.toFixed(2)}</td>
      {!opts.hideItemPrice && <td className="num price">{money(r.li.salePricePerCt)}</td>}
      <td className="num amount">{money(r.li.amountUSD)}</td>
    </tr>
  ) : (
    <tr key={r.key} data-m-row={measure ? '' : undefined}>
      <td className="sr" />
      <td className="desc" style={descStyle}>{r.c.description || r.c.category}</td>
      <td className="num carat" />
      {!opts.hideItemPrice && <td className="num price" />}
      <td className="num amount">{money(r.c.amountUSD)}</td>
    </tr>
  );

  // This invoice's own EUR rate (older invoices fall back to the default).
  const rate = opts.eurUsdRate ?? settings?.invoiceEurUsdRate;
  // A foreign currency invoice prints its own rate instead of the EUR line.
  const showRate = !invRate && !!rate && rate > 0;
  const tfoot = showTotals && (
    <tfoot data-m="tfoot">
      <tr>
        <td colSpan={2} className="invoice-doc-table-total-label">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {showRate && (
              <span className="invoice-doc-fx">
                1 EUR = {fmtRate(rate!)} US$, ( {fmtMoney(invoice.totalAmountUSD / rate!, 'EUR')} )
              </span>
            )}
            {invRate && (
              <span className="invoice-doc-fx">Rate 1 {invCur} = {fmtRate(invRate)} {baseCur}</span>
            )}
            <span style={{ marginLeft: 'auto' }}>Total</span>
          </div>
        </td>
        <td className="num carat invoice-doc-table-total-label">{totalCarat.toFixed(2)}</td>
        {!opts.hideItemPrice && <td className="num price invoice-doc-table-total-label">{money(avgPricePerCt)}</td>}
        <td className="num amount invoice-doc-table-total-label">{money(invoice.totalAmountUSD)}</td>
      </tr>
    </tfoot>
  );

  // Bank details as a small "Label : value" list, then the amount in words.
  const bankRows: [string, string | undefined][] = showPayment ? [
    ['Bank Name',      bankAccount!.bankName || bankAccount!.name],
    ['Account Number', bankAccount!.accountNumber],
    ['IBAN',           bankAccount!.iban],
    ['SWIFT / BIC',    bankAccount!.swiftCode],
  ] : [];
  const end = (
    <div data-m="end" style={{ display: 'flow-root' }}>
      {(showPayment || showTotals) && (
        <div className="invoice-doc-bank">
          {bankRows.filter(([, v]) => v && v.trim()).map(([label, value]) => (
            <div key={label} className="invoice-doc-bank-row">
              <span className="invoice-doc-bank-label">{label}</span>
              <span className="invoice-doc-bank-sep">:</span>
              <span className="invoice-doc-bank-value">{value}</span>
            </div>
          ))}
          {showTotals && (
            <div className="invoice-doc-bank-value" style={{ fontStyle: 'italic', marginTop: 4 }}>
              {numberToWords(invRate ? invoice.totalAmountUSD / invRate : invoice.totalAmountUSD)} Only
            </div>
          )}
        </div>
      )}
    </div>
  );

  const footer = hasFooter && (
    <div data-m="footer" className="invoice-doc-footer-row">
      <div className="invoice-doc-footer-note">
        {memo && <div className="invoice-doc-memo">{memo}</div>}
        {footerNote && <div className="invoice-doc-settings-note" style={memo ? { marginTop: 6 } : undefined}>{footerNote}</div>}
      </div>
      {footerImg && <div className="invoice-doc-footer-img-box"><img src={footerImg} alt="" className="invoice-doc-footer-img" style={{ maxHeight: footerImgH }} /></div>}
    </div>
  );

  const docStyle = { '--invoice-accent': accent } as React.CSSProperties;

  return (
    <>
      {sheets.map((idxs, p) => {
        const isLast = p === pageCount - 1;
        return (
          <div key={p} className="invoice-doc invoice-doc-sheet" style={docStyle}>
            {top(p + 1, pageCount)}
            {idxs.length > 0 && (
              <table className="invoice-doc-table">
                {thead}
                <tbody>{idxs.map(i => renderRow(rows[i], false))}</tbody>
                {p === lastTableSheet && tfoot}
              </table>
            )}
            {isLast && end}
            {isLast && footer}
          </div>
        );
      })}

      {/* Hidden full-length copy, only used to measure row heights. */}
      <div ref={measureRef} className="invoice-doc invoice-doc-measure" style={docStyle} aria-hidden>
        {top(1, 2)}
        <table className="invoice-doc-table">
          {thead}
          <tbody>{rows.map(r => renderRow(r, true))}</tbody>
          {tfoot}
        </table>
        {end}
        {footer}
      </div>
    </>
  );
}

// ── Transaction Category — icon + picker + badge ─────────────────────────────
// Duplicated verbatim from Purchase.tsx's own Category feature (per this
// app's per-page icon/component duplication convention) — same three
// hand-drawn icons, same meaning, so a stone's category reads identically
// whether it's shown on a Purchase or a Sale.
function IconCatBooksReal() {
  return <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
    <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.4" />
    <path d="M4.2 7.2l2 2 3.6-4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
function IconCatCashReal() {
  return <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
    <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.4" />
    <path d="M3.6 3.6l6.8 6.8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>;
}
function IconCatBooksDummy() {
  return <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
    <path d="M3.5 1.5h4.5l2.5 2.5v8a1 1 0 0 1-1 1h-6a1 1 0 0 1-1-1v-9.5a1 1 0 0 1 1-1Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    <path d="M8 1.5v2.5h2.5" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    <path d="M5 8.3c.3-.5 1-.6 1.4-.3.5.3.5.9 0 1.2-.6.3-.6.9 0 1.2.4.3 1.1.2 1.4-.3M6.6 7v5" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" />
  </svg>;
}

const CATEGORY_META: Record<TransactionCategory, { label: string; short: string; color: string; icon: React.ReactNode }> = {
  'books-real':  { label: 'By Books & Real',  short: 'By Books', color: 'var(--success)', icon: <IconCatBooksReal /> },
  'cash-real':   { label: 'Cash & Real',      short: 'Cash',     color: 'var(--danger)',  icon: <IconCatCashReal /> },
  'books-dummy': { label: 'By Books & Dummy', short: 'Dummy',    color: 'var(--text-2)',  icon: <IconCatBooksDummy /> },
};
const CATEGORY_ORDER: TransactionCategory[] = ['books-real', 'cash-real', 'books-dummy'];

function IconCalendarSm() {
  return <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
    <rect x="2" y="3" width="12" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M2 6.5h12M5.5 1.5v3M10.5 1.5v3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>;
}
function IconChevronDownSm() {
  return <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
    <path d="M3 5.5l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
function IconCheckSm() {
  return <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
    <path d="M2.5 7.5l3 3 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}

// Icon-only Dropdown, same as Purchase.tsx's CategoryPicker — a 'cash-real'
// line here also credits the shared Dummy/Books Adjustment code (see
// planDummyCreditDelta above), so this isn't purely cosmetic like it is on
// most other rows.
function CategoryPicker({ value, onChange }: { value: TransactionCategory | ''; onChange: (v: TransactionCategory | '') => void }) {
  const current = value ? CATEGORY_META[value] : null;
  return (
    <Dropdown
      panelClassName="icon-only"
      trigger={() => (
        <button type="button" title={current?.label ?? 'Select category'} style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3,
          height: 22, padding: '0 6px', borderRadius: 5, boxSizing: 'border-box', flexShrink: 0,
          border: `1.5px solid ${current ? current.color : 'var(--border)'}`,
          background: current ? `color-mix(in srgb, ${current.color} 14%, transparent)` : 'var(--surface)',
          color: current ? current.color : 'var(--text-3)',
          cursor: 'pointer', fontFamily: 'inherit',
        }}>
          {current ? current.icon : <IconCatBooksReal />}
          <span style={{ color: 'var(--text-3)', display: 'flex' }}><IconChevronDownSm /></span>
        </button>
      )}
    >
      {CATEGORY_ORDER.map(key => {
        const meta = CATEGORY_META[key];
        return (
          <button
            key={key} type="button" className="dropdown-item" title={meta.label}
            style={{ color: meta.color, justifyContent: 'center' }}
            onClick={() => onChange(key)}
          >
            {meta.icon}
          </button>
        );
      })}
    </Dropdown>
  );
}

// Read-only, for the main invoice list — see invoiceCategoryOf. 'mixed'
// shows both icons it's a blend of (an invoice-level category is never
// actually 'books-dummy'-only unless it's a pure reconciling sale from the
// locked Books Adjustment code — see Settings › Custom Options — so mixed
// in practice almost always means a Books+Cash split across two lines).
function InvoiceCategoryBadge({ category }: { category?: TransactionCategory | 'mixed' }) {
  if (!category) return <span style={{ opacity: 0.4 }}>—</span>;
  if (category === 'mixed') {
    return (
      <span title="Mixed categories across this invoice's lines" style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
        <span style={{ color: CATEGORY_META['books-real'].color, display: 'flex' }}>{CATEGORY_META['books-real'].icon}</span>
        <span style={{ color: CATEGORY_META['cash-real'].color, display: 'flex' }}>{CATEGORY_META['cash-real'].icon}</span>
      </span>
    );
  }
  const meta = CATEGORY_META[category];
  return (
    <span title={meta.label} style={{ display: 'inline-flex', alignItems: 'center', color: meta.color }}>
      {meta.icon}
    </span>
  );
}

// ── Terms selector ────────────────────────────────────────────────────────────

// Just the days box — no preset 30d/45d/60d/90d buttons (same as Purchase.tsx).
function TermsSelector({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
      <input type="number" min={1} value={value}
        onChange={e => onChange(parseInt(e.target.value) || 1)}
        className="terms-input" style={{ width: 58, textAlign: 'center', fontWeight: 700 }} />
      <span style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 500 }}>days</span>
    </div>
  );
}

// ── Line items sub-table (read-only expanded row) ─────────────────────────────

function SaleLineItemsSubTable({ items }: { items: SaleLineItem[] }) {
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v ?? 0, settings.currency);
  if (!items || items.length === 0) {
    return <div style={{ padding: '10px 16px', fontSize: 12, color: 'var(--text-3)' }}>No line items.</div>;
  }
  const totalCt      = items.reduce((s, li) => s + li.carat, 0);
  const totalAmount  = items.reduce((s, li) => s + li.carat * li.salePricePerCt, 0);
  const avgPricePerCt = totalCt > 0 ? totalAmount / totalCt : 0;
  // Sale with a Keep(%): also show the price actually agreed (before Keep),
  // right before the invoice price, so both are visible.
  const hasKeep = items.some(li => li.grossPricePerCt != null);
  // Indian sale: show each stone's Less 1 / Less 2 (only when any line has one).
  const hasLess = items.some(li => (li.less1Percent ?? 0) > 0 || (li.less2Percent ?? 0) > 0);
  const pct = (v?: number) => (v ?? 0) > 0 ? `${v}%` : <span style={{ opacity: 0.4 }}>—</span>;
  const soldPerCt = (li: SaleLineItem) => li.grossPricePerCt ?? li.salePricePerCt;
  const avgSoldPerCt = totalCt > 0 ? items.reduce((s, li) => s + li.carat * soldPerCt(li), 0) / totalCt : 0;

  return (
    <div className="line-sub-scroll" style={{ background: 'var(--surface-2)', borderTop: '1px solid var(--border)' }}>
      {/* Scrolls inside its own box (heads and Total pinned), not the whole popup. */}
      <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 12 }}>
        <thead>
          <tr>
            <th style={{ ...TH, width: 28, textAlign: 'center' }}>Cat.</th>
            <th style={{ ...TH, width: 28, textAlign: 'center' }}>#</th>
            <th style={TH}>SKU</th>
            <th style={TH}>Code</th>
            <th style={TH}>Description</th>
            <th style={TH}>Shape</th>
            <th style={TH}>Ct</th>
            {/* Short headers on purpose; the tooltip says what each one is. */}
            {hasLess && <>
              <th style={{ ...TH, textAlign: 'right' }} title="Price agreed per ct, before Less 1 % and Less 2 %">Agreed/ct</th>
              <th style={{ ...TH, textAlign: 'right' }}>Less 1</th>
              <th style={{ ...TH, textAlign: 'right' }}>Less 2</th>
            </>}
            {hasKeep && <th style={{ ...TH, textAlign: 'right' }} title="Price you sold at, before Keep(%)">Sold/ct</th>}
            <th style={{ ...TH, textAlign: 'right' }} title={hasKeep ? 'Price on the invoice, after Keep(%)' : undefined}>
              {hasKeep ? 'Net/ct' : 'Price/ct'}
            </th>
            <th style={TH}>Color</th>
            <th style={TH}>Clarity</th>
            <th style={TH}>Cert / Size</th>
            <th style={{ ...TH, textAlign: 'right' }}>Total Amount</th>
          </tr>
        </thead>
        <tbody>
          {items.map((li, i) => {
            const rowBg = i % 2 === 0 ? 'var(--surface)' : 'var(--surface-2)';
            return (
              <tr key={li.inventoryItemId || i} style={{ background: rowBg }}>
                <td style={{ ...TD, textAlign: 'center' }}><InvoiceCategoryBadge category={li.category} /></td>
                <td style={{ ...TD, textAlign: 'center', color: 'var(--text-3)' }}>{i + 1}</td>
                <td style={{ ...TD, fontFamily: 'var(--font-num)', fontSize: 11 }}>{li.sku || <span style={{ opacity: 0.4, fontFamily: 'inherit' }}>—</span>}</td>
                <td style={{ ...TD, fontFamily: 'var(--font-num)', fontSize: 11 }}>{li.code || <span style={{ opacity: 0.4, fontFamily: 'inherit' }}>—</span>}</td>
                <td style={TD}>{li.description || <span style={{ opacity: 0.4 }}>—</span>}</td>
                <td style={TD}>{li.shape}</td>
                <td style={{ ...TD, fontFamily: 'var(--font-num)' }}>{li.carat.toFixed(2)}</td>
                {hasLess && <>
                  <td style={{ ...TD, textAlign: 'right', fontFamily: 'var(--font-num)' }}>{money(li.agreedPricePerCt ?? soldPerCt(li))}</td>
                  <td style={{ ...TD, textAlign: 'right', fontFamily: 'var(--font-num)' }}>{pct(li.less1Percent)}</td>
                  <td style={{ ...TD, textAlign: 'right', fontFamily: 'var(--font-num)' }}>{pct(li.less2Percent)}</td>
                </>}
                {hasKeep && <td style={{ ...TD, textAlign: 'right', fontFamily: 'var(--font-num)' }}>{money(soldPerCt(li))}</td>}
                <td style={{ ...TD, textAlign: 'right', fontFamily: 'var(--font-num)' }}>{money(li.salePricePerCt)}</td>
                <td style={TD}>{li.color}</td>
                <td style={TD}>{li.clarity}</td>
                <td style={{ ...TD, fontSize: 11 }}>{li.certificateNo || `${li.size ?? '—'}/${li.subSize ?? '—'}`}</td>
                <td style={{ ...TD, textAlign: 'right', fontFamily: 'var(--font-num)', fontWeight: 700 }}>{money(li.carat * li.salePricePerCt)}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ background: 'var(--surface)', borderTop: '2px solid var(--border)' }}>
            <td colSpan={6} style={{ ...TD, fontWeight: 700, fontSize: 11, color: 'var(--text-2)' }}>Total</td>
            <td style={{ ...TD, fontFamily: 'var(--font-num)', fontWeight: 700 }}>{totalCt.toFixed(2)}</td>
            {hasLess && <><td style={TD} /><td style={TD} /><td style={TD} /></>}
            {hasKeep && <td style={{ ...TD, textAlign: 'right', fontFamily: 'var(--font-num)', fontWeight: 700 }}>{money(avgSoldPerCt)}</td>}
            <td style={{ ...TD, textAlign: 'right', fontFamily: 'var(--font-num)', fontWeight: 700 }}>{money(avgPricePerCt)}</td>
            <td style={TD} />
            <td style={TD} />
            <td style={TD} />
            <td style={{ ...TD, textAlign: 'right', fontFamily: 'var(--font-num)', fontWeight: 700 }}>{money(totalAmount)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ── Sale preview modal — opened by clicking an invoice row ────────────────────

// Exported: Accounts Receivable opens this same popup for a sale (without
// Edit / Delete), adding its payment parts as `children` and its own
// buttons as `footerExtra`, so a sale looks the same everywhere.
export type SalePopupTab = 'stones' | 'payments' | 'brokers';

export function SalePreviewModal({ invoice: openedInvoice, entityName, onClose, onPay, onView, onEdit, onDelete, children, footerExtra, initialTab, highlightBankTxId, highlightBrokerId }: {
  invoice: SaleInvoice | null;
  entityName: (id: string) => string;
  onClose: () => void;
  children?: ReactNode;
  footerExtra?: ReactNode;
  /** Leave out to hide Record Payment (e.g. Entity History, read only). */
  onPay?: (inv: SaleInvoice) => void;
  // Row Actions column moved in here (see the list table below) — these
  // close the preview first, then trigger the same flow the old Print/Edit/
  // Delete icon buttons did, rather than stacking on top of it: editing and
  // the full print view both take over the whole modal/page, so layering
  // them on top of an already-open preview would just look redundant.
  onView?: (id: string) => void;
  onEdit?: (inv: SaleInvoice) => void;
  onDelete?: (inv: SaleInvoice) => void;
  /** Which tab opens first: Receivable opens Payments, Brokers opens Brokers. */
  initialTab?: SalePopupTab;
  /** The bank row this was opened from: its payment is picked out. */
  highlightBankTxId?: string;
  highlightBrokerId?: string;
}) {
  const { user: me } = useAuth();
  const canSeeCost = canAccessPage(me, COST_MARGIN_KEY);
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v, settings?.currency ?? 'USD');
  // Follow the live sale, so a payment recorded or edited shows at once.
  const invoice = useLiveSale(openedInvoice);
  const data = useSaleMoney(invoice);
  const [tab, setTab] = useState<SalePopupTab>(initialTab ?? 'stones');
  const [lastId, setLastId] = useState(openedInvoice?.id);
  if (openedInvoice?.id !== lastId) { setLastId(openedInvoice?.id); setTab(initialTab ?? 'stones'); }
  if (!invoice) return null;
  const isPaid = invoice.paymentStatus === 'paid';
  // Read only views (no onPay, e.g. Entity History) can't change payments.
  const readOnly = !onPay;
  const canEditPayments = !readOnly && (canAccessPage(me, 'ar') || canAccessPage(me, 'sales'));
  const canPayBrokers = !readOnly && canAccessPage(me, 'brokers');
  const brokerCount = saleBrokerRows(invoice).length;
  const moneyCount = data.payments.length + data.advanceUses.length + data.credits.length;
  const saleDateLabel = invoice.saleDate
    ? new Date(invoice.saleDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '—';

  const tabs: { key: SalePopupTab; label: string }[] = [
    { key: 'stones',   label: `Items (${invoice.lineItems?.length ?? 0})` },
    { key: 'payments', label: moneyCount > 0 ? `Payments (${moneyCount})` : 'Payments' },
    ...(brokerCount > 0 ? [{ key: 'brokers' as const, label: `Brokers (${brokerCount})` }] : []),
  ];
  const activeTab = tabs.some(t => t.key === tab) ? tab : 'stones';

  return (
    <Modal
      open={!!invoice}
      onClose={onClose}
      hideHead
      size="2xl"
      footer={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {onDelete && (
              <Button variant="danger" icon={<IconTrash />} onClick={() => { onClose(); onDelete(invoice); }}>
                Delete Invoice
              </Button>
            )}
            <AuthorLine record={invoice} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {footerExtra}
            {!isPaid && onEdit && (
              <Button variant="secondary" icon={<IconEdit />} onClick={() => { onClose(); onEdit(invoice); }}>
                Edit
              </Button>
            )}
            {!isPaid && onPay && (
              <Button variant="primary" icon={<IconPay />} onClick={() => onPay(invoice)}>
                Record Payment
              </Button>
            )}
            <Button variant="secondary" onClick={onClose}>Close</Button>
          </div>
        </div>
      }
    >
      <ModalBanner
        title={entityName(invoice.toEntityId)}
        eyebrow="Sale Invoice"
        right={
          <FactPanel>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', marginBottom: 2 }}>
                Invoice No
              </div>
              <LotPill>{lotNoDisplay(invoice.invoiceNo)}</LotPill>
            </div>
            <VDivider />
            <MiniFact label="Date" value={saleDateLabel} />
            <VDivider />
            {(invoice.lessPercent ?? 0) > 0 && (
              <>
                <MiniFact label="Keep(%)" value={`${invoice.lessPercent}%`} />
                <VDivider />
              </>
            )}
            <PayBadge inv={invoice} />
{onView && (<>
            <VDivider />
            <button
              type="button" className="icon-btn" title="View / Print Invoice"
              style={{ height: 30, borderRadius: 999, padding: '0 10px', fontWeight: 600, border: 'none', background: 'transparent' }}
              onClick={() => { onClose(); onView(invoice.id); }}
            >
              <IconPrint /> Print
            </button>
            </>)}
          </FactPanel>
        }
        onClose={onClose}
      />

      {/* Total · Received · Outstanding · Due: said once, here. */}
      <SaleMoneyStrip invoice={invoice} />

      <PageTabs tabs={tabs} active={activeTab} onChange={k => setTab(k as SalePopupTab)} className="sm-tabs" />

      {activeTab === 'stones' && (
        <>
          {invoice.notes && (
            <p className="form-hint" style={{ marginBottom: 12 }}>Notes: {invoice.notes}</p>
          )}
          <SaleLineItemsSubTable items={invoice.lineItems ?? []} />
          <GstSummary inv={invoice} money={money} />

          {(invoice.extraCosts?.length ?? 0) > 0 && (
            <div style={{ marginTop: 16, maxWidth: 520, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <div className="form-section-label" style={{ marginBottom: 2 }}>Additional Charges &amp; Expenses</div>
              {invoice.extraCosts!.map(c => (
                <div key={c.saleExpenseId} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12 }}>
                  <span style={{ color: 'var(--text-2)' }}>
                    {c.description || c.category}
                    <span style={{ color: 'var(--text-3)' }}> · {c.category}</span>
                    <span style={{
                      marginLeft: 6, fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 999,
                      background: c.chargeToCustomer ? 'var(--primary-light)' : 'var(--surface-2)',
                      color: c.chargeToCustomer ? 'var(--primary)' : 'var(--text-3)',
                    }}>{c.chargeToCustomer ? 'Charged to customer' : 'Our cost'}</span>
                  </span>
                  <span style={{ fontFamily: 'var(--font-num)', fontWeight: 600, color: 'var(--text-1)' }}>{money(c.amountUSD)}</span>
                </div>
              ))}
              {canSeeCost && (
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12, paddingTop: 4, borderTop: '1px solid var(--border)' }}>
                  <span style={{ fontWeight: 600, color: 'var(--text-2)' }}>Margin after our costs</span>
                  <span style={{ fontFamily: 'var(--font-num)', fontWeight: 700, color: 'var(--text-1)' }}>
                    {money((invoice.totalMarginUSD ?? 0) - (invoice.sellingExpensesUSD ?? 0))}
                  </span>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {activeTab === 'payments' && (
        <SalePaymentsTab invoice={invoice} data={data} canEdit={canEditPayments} highlightBankTxId={highlightBankTxId} />
      )}

      {activeTab === 'brokers' && (
        <SaleBrokersTab invoice={invoice} data={data} entityName={entityName} canPay={canPayBrokers} highlightBrokerId={highlightBrokerId} highlightBankTxId={highlightBankTxId} />
      )}

      {children}
    </Modal>
  );
}

// ── Pagination ────────────────────────────────────────────────────────────────

function Pagination({ current, total, totalItems, pageSize, onChange }: {
  current: number; total: number; totalItems: number; pageSize: number; onChange: (p: number) => void;
}) {
  if (total <= 1) return null;
  const from = (current - 1) * pageSize + 1;
  const to   = Math.min(current * pageSize, totalItems);
  const pages: (number | 'e')[] = [];
  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i);
  } else {
    pages.push(1);
    if (current > 3) pages.push('e');
    for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i);
    if (current < total - 2) pages.push('e');
    pages.push(total);
  }
  return (
    <div className="pagination">
      <span className="pagination-info">
        Showing <strong>{from}–{to}</strong> of <strong>{totalItems}</strong> invoice{totalItems !== 1 ? 's' : ''}
      </span>
      <div className="pagination-btns">
        <button className="pg-btn" disabled={current === 1} onClick={() => onChange(current - 1)}>‹</button>
        {pages.map((p, i) => p === 'e'
          ? <span key={`e${i}`} style={{ minWidth: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: 'var(--text-3)' }}>…</span>
          : <button key={p} className={`pg-btn${p === current ? ' active' : ''}`} onClick={() => onChange(p)}>{p}</button>
        )}
        <button className="pg-btn" disabled={current === total} onClick={() => onChange(current + 1)}>›</button>
      </div>
    </div>
  );
}

// ── Item picker — search in-stock inventory by code, inline in the line-items table ──

// One persistent row, shaped exactly like a real line-item row — Code is the
// only editable cell; every other cell live-fills from whatever it currently
// resolves to (exact code match, or an unambiguous prefix while still
// typing) so the match is visible before committing. Enter adds it as a real
// line item below and clears the row for the next one. A typed prefix that
// matches MORE than one item used to just leave every cell blank with no way
// to see what's colliding — the Code input now also opens a portaled
// Code/SKU/Size suggestion panel (same technique as InventoryMerge.tsx's
// DestCodeCombobox) so those candidates are visible and pickable, instead of
// requiring the user to keep typing blindly until it narrows to one.
function CodeEntryRow({ available, onSelect, extraCols = 0 }: {
  available: InventoryItem[]; onSelect: (item: InventoryItem) => void;
  /** Empty cells for columns the line rows have on top of the base set (Less 1 / 2, Terms). */
  extraCols?: number;
}) {
  const [q, setQ] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const draft = q.trim();
  const match = useMemo(() => {
    if (!draft) return null;
    const norm = draft.toUpperCase();
    const exact = available.find(i => (i.code ?? '').toUpperCase() === norm);
    if (exact) return exact;
    const partial = available.filter(i => (i.code ?? '').toUpperCase().startsWith(norm));
    return partial.length === 1 ? partial[0] : null;
  }, [available, draft]);

  const suggestions = useMemo(() => {
    const norm = draft.toUpperCase();
    return available
      .filter(i => !norm || (i.code ?? '').toUpperCase().includes(norm) || (i.sku ?? '').toUpperCase().includes(norm))
      .slice(0, 20);
  }, [available, draft]);

  useEffect(() => {
    if (!open) return;
    function updatePosition() {
      const rect = inputRef.current?.getBoundingClientRect();
      if (!rect) return;
      setPos({ top: rect.bottom + 6, left: rect.left, width: Math.max(rect.width, 300) });
    }
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      const t = e.target as Node;
      if (inputRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  function commit() {
    if (!match) { setNotFound(!!draft); return; }
    onSelect(match);
    setQ(''); setNotFound(false); setOpen(false);
    inputRef.current?.focus();
  }

  // Picking a suggestion adds it immediately — the whole point of showing
  // candidates is to skip having to keep typing until the code is unambiguous.
  function pick(item: InventoryItem) {
    onSelect(item);
    setQ(''); setNotFound(false); setOpen(false);
    inputRef.current?.focus();
    inputRef.current?.scrollIntoView({ block: 'nearest' });
  }

  // The add row now sits below the committed rows, so after adding one keep
  // it in view within the scroll box.
  function commitAndScroll() {
    commit();
    inputRef.current?.scrollIntoView({ block: 'nearest' });
  }

  const dim: React.CSSProperties = { color: match ? 'var(--text-1)' : 'var(--text-3)' };
  // Plain highlighted "add" row — it sits below the committed rows now (not
  // pinned under the header), so no position:sticky.
  const entryTD: React.CSSProperties = { ...TD, background: 'var(--primary-light)' };

  return (
    <tr>
      <td style={{ ...entryTD, textAlign: 'center', color: 'var(--primary)' }}><IconPlus /></td>
      <td style={entryTD} />
      <td style={{ ...entryTD, padding: 0 }}>
        <input
          ref={inputRef}
          value={q}
          onChange={e => { setQ(e.target.value); setNotFound(false); setOpen(true); }}
          // Open on an explicit click or on typing — NOT on bare focus, so
          // the programmatic re-focus after committing/picking an item
          // doesn't immediately reopen the suggestion panel.
          onClick={() => setOpen(true)}
          onKeyDown={e => {
            if (e.key === 'Enter') { e.preventDefault(); commitAndScroll(); }
            else if (e.key === 'Escape' && open) {
              // Close just the suggestion panel first — stop the keydown from
              // bubbling up to the Modal's own Escape-to-close handler, or
              // this would dismiss the whole Add/Edit Sale modal instead.
              e.stopPropagation();
              setOpen(false);
              inputRef.current?.blur();
            }
          }}
          placeholder="Code…"
          autoComplete="off"
          className="grid-cell"
          style={{ fontFamily: 'var(--font-num)', color: notFound ? 'var(--danger)' : undefined }}
        />
        {notFound && <div style={{ fontSize: 9.5, color: 'var(--danger)', marginTop: 2, whiteSpace: 'nowrap' }}>No available item with that code</div>}
        {open && pos && createPortal(
          <div
            ref={panelRef}
            className="dropdown-panel"
            style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, maxHeight: 280, overflowY: 'auto' }}
          >
            <div className="dropdown-group-label">Code · SKU · Size</div>
            {suggestions.length === 0 && (
              <div style={{ padding: '8px 9px', fontSize: 12, color: 'var(--text-3)' }}>No available items match.</div>
            )}
            {suggestions.map(i => (
              <button key={i.id} type="button" className="dropdown-item" onClick={() => pick(i)}>
                <span style={{
                  display: 'inline-block', background: 'var(--primary-light)', color: 'var(--primary)',
                  fontFamily: 'var(--font-num)', fontSize: 11, fontWeight: 700,
                  padding: '3px 8px', borderRadius: 6, letterSpacing: '0.02em', flexShrink: 0,
                }}>
                  {i.code || '—'}
                </span>
                <span style={{ fontFamily: 'var(--font-num)', fontSize: 11.5, color: 'var(--text-2)' }}>{i.sku || '—'}</span>
                <span style={{ marginLeft: 'auto', fontSize: 11.5, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>
                  {i.certificateNo || `${i.size ?? '—'}/${i.subSize ?? '—'}`} · {availableCarat(i).toFixed(2)}ct
                </span>
              </button>
            ))}
          </div>,
          document.body,
        )}
      </td>
      {/* Description — filled in per row after it's committed; the entry row
          previews the matched stone (shape · cert) instead. */}
      <td style={{ ...entryTD, fontSize: 11, ...dim }}>
        {match ? `${match.shape}${match.certificateNo ? ` · ${match.certificateNo}` : ''}` : <span style={{ opacity: 0.4 }}>—</span>}
      </td>
      <td style={{ ...entryTD, ...dim }}>{match?.color || '—'}</td>
      <td style={{ ...entryTD, ...dim }}>{match?.clarity || '—'}</td>
      <td style={{ ...entryTD, fontFamily: 'var(--font-num)', textAlign: 'right', ...dim }}>{match ? availableCarat(match).toFixed(2) : '—'}</td>
      <td style={entryTD} />
      <td style={entryTD} />
      {Array.from({ length: extraCols }, (_, i) => <td key={`x${i}`} style={entryTD} />)}
      {/* Broker1 / Broker1% / Broker2 / Broker2% — nothing to enter until
          the item is actually committed as a real row. */}
      <td style={entryTD} />
      <td style={entryTD} />
      <td style={entryTD} />
      <td style={entryTD} />
      <td style={{ ...entryTD, textAlign: 'center' }}>
        {match && (
          <button type="button" onClick={commitAndScroll} title="Add to sale"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--success)', padding: '2px 4px', display: 'inline-flex' }}>
            <IconPlus />
          </button>
        )}
      </td>
    </tr>
  );
}

// ── DescriptionCombobox — free text + a type-ahead list of the saved
// descriptions from Settings › Custom Options. Value IS the text (anything
// goes); the list is just quick picks. ──────────────────────────────────────
function DescriptionCombobox({ value, onChange, options, className }: {
  value: string; onChange: (v: string) => void; options: string[];
  /** When given, replaces the default TI-styled look entirely — e.g. the
   *  line-items grid's flattened, edge-to-edge `grid-cell` class. */
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function update() {
      const r = inputRef.current?.getBoundingClientRect();
      if (!r) return;
      setPos({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 220) });
    }
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => { window.removeEventListener('scroll', update, true); window.removeEventListener('resize', update); };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      const t = e.target as Node;
      if (inputRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const q = value.trim().toLowerCase();
  const list = options.filter(o => !q || o.toLowerCase().includes(q)).slice(0, 40);
  const hasList = options.length > 0 && list.length > 0;

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <input
        ref={inputRef}
        className={className}
        style={className ? undefined : { ...TI, width: '100%' }}
        value={value}
        onChange={e => { onChange(e.target.value); setOpen(true); }}
        onClick={() => options.length > 0 && setOpen(true)}
        onKeyDown={e => { if (e.key === 'Escape' && open) { e.stopPropagation(); setOpen(false); } }}
        placeholder={options.length > 0 ? 'Add a note / pick…' : 'Add a note…'}
      />
      {open && pos && hasList && createPortal(
        <div ref={panelRef} className="dropdown-panel"
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, maxHeight: 240, overflowY: 'auto' }}>
          {list.map(o => (
            <button
              key={o} type="button" className="dropdown-item"
              onClick={() => { onChange(o); setOpen(false); inputRef.current?.blur(); }}
              style={{ display: 'block', width: '100%', textAlign: 'left', whiteSpace: 'normal' }}
            >
              {o}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}

// ── Customer's Memos panel ────────────────────────────────────────────────────
// Shown once a customer is picked, right below the header — everything
// currently out on memo with THAT client, one row each, so closing a memo as
// part of this sale doesn't require a separate trip to the Memos page first.
// "Add to Sale" drops it straight into the line-items table below (carat
// locked to exactly what's on memo); ordinary extra items are still added
// normally beneath it. Folds in legacy on-memo items too, via the existing
// `available`/`addItem` path — those already work as a direct conversion.

function CustomerMemosPanel({
  activeMemos, legacyItems, addedMemoIds, usedLegacyIds, onAddMemo, onAddLegacy,
}: {
  activeMemos:    InventoryMemo[];
  legacyItems:    InventoryItem[];
  addedMemoIds:   Set<string>;
  usedLegacyIds:  Set<string>;
  onAddMemo:      (memo: InventoryMemo) => void;
  onAddLegacy:    (item: InventoryItem) => void;
}) {
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v ?? 0, settings.currency);
  const total = activeMemos.length + legacyItems.length;
  return (
    <div style={{
      border: '1.5px solid var(--warning)', background: 'var(--warning-bg)',
      borderRadius: 'var(--r-sm)', overflow: 'hidden', marginBottom: 12,
    }}>
      <div style={{ padding: '8px 14px', fontSize: 11.5, fontWeight: 700, color: '#c2410c', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        On Memo with This Customer — {total} item{total !== 1 ? 's' : ''}
      </div>
      <div style={{ background: 'var(--surface)', maxHeight: 180, overflowY: 'auto' }}>
        {activeMemos.map(m => {
          const added = addedMemoIds.has(m.id);
          return (
            <div key={m.id} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '7px 14px',
              borderBottom: '1px solid var(--surface-2)', fontSize: 12,
            }}>
              <span style={{ flex: 1, color: 'var(--text-1)' }}>
                {m.code && <span style={{ fontFamily: 'var(--font-num)', color: 'var(--primary)', marginRight: 6 }}>{m.code}</span>}
                <strong>{m.shape}</strong> {m.carat.toFixed(2)}ct {m.color} {m.clarity}
              </span>
              <span style={{ color: 'var(--text-3)', fontFamily: 'var(--font-num)', fontSize: 11 }}>
                {m.pricePerCt ? `${money(m.pricePerCt)}/ct` : '—'}
              </span>
              <span style={{ color: 'var(--text-3)', fontSize: 11 }}>since {fmtShortDate(m.date)}</span>
              {added ? (
                <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--success)' }}>✓ Added</span>
              ) : (
                <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: 11, padding: '3px 9px' }} onClick={() => onAddMemo(m)}>
                  Add to Sale
                </button>
              )}
            </div>
          );
        })}
        {legacyItems.map(item => {
          const added = usedLegacyIds.has(item.id);
          return (
            <div key={item.id} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '7px 14px',
              borderBottom: '1px solid var(--surface-2)', fontSize: 12,
            }}>
              <span style={{ flex: 1, color: 'var(--text-1)' }}>
                {item.code && <span style={{ fontFamily: 'var(--font-num)', color: 'var(--primary)', marginRight: 6 }}>{item.code}</span>}
                <strong>{item.shape}</strong> {item.carat.toFixed(2)}ct {item.color} {item.clarity}
              </span>
              <span style={{ color: 'var(--text-3)', fontFamily: 'var(--font-num)', fontSize: 11 }}>
                {item.memoPricePerCt ? `${money(item.memoPricePerCt)}/ct` : '—'}
              </span>
              <span style={{ color: 'var(--text-3)', fontSize: 11 }}>since {fmtShortDate(item.memoDate)}</span>
              {added ? (
                <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--success)' }}>✓ Added</span>
              ) : (
                <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: 11, padding: '3px 9px' }} onClick={() => onAddLegacy(item)}>
                  Add to Sale
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Sale Add / Edit Modal ─────────────────────────────────────────────────────

interface SaleHeader {
  // No header-level broker here — replaced by per-line Broker1/Broker2 on
  // each SaleLineItem (see types/index.ts). Unlike Purchase.tsx, this was
  // removed outright rather than kept as a stopgap.
  toEntityId: string;
  saleDate: string; terms: number; dueDate: string; notes: string;
  // 'average' (default) = one invoice-level due date. 'separate' = each
  // line item's own Terms produces its own due date, grouped into
  // DueGroups and paid independently — mirrors Purchase.tsx exactly.
  dueDateMode: DueDateMode;
  // "Less %" for the whole invoice, as typed ('' = none). See applyLess.
  lessPercent: string;
}

// Comparable fingerprint of the Add/Edit Sale form. In 'average' mode Due
// Date is derived from the other fields (see the recompute effect), so it's
// left out — otherwise that effect alone would make a just-opened form look
// edited.
function formSnapshot(h: SaleHeader, li: SaleLineItem[]): string {
  return JSON.stringify({ ...h, dueDate: h.dueDateMode === 'average' ? '' : h.dueDate, li });
}

function SaleAddModal({
  open, onClose, entities, inventory, memos, settings, editInvoice, sales, bankAccounts,
  prefill, prefillLabel, onSaved, onSkip, initialItemId,
}: {
  open:         boolean;
  onClose:      () => void;
  entities:     Entity[];
  inventory:    InventoryItem[];
  memos:        InventoryMemo[];
  settings:     BusinessSettings | null;
  editInvoice?: SaleInvoice | null;
  sales:        SaleInvoice[];
  bankAccounts: BankAccount[];
  /** A sale from the Excel import: the form opens already filled with it. */
  prefill?:      SaleImportGroup;
  /** e.g. "Import · Sale 3 of 21", shown in the banner. */
  prefillLabel?: string;
  onSaved?:      () => void;
  /** Import only: move on to the next sale without saving this one. */
  onSkip?:       () => void;
  /** Opened from the Inventory popup (?item=…): start with this stone added. */
  initialItemId?: string;
}) {
  const { user, business } = useAuth();
  const isEdit = !!editInvoice;
  // Codes from the import that couldn't be added (not in stock, etc.).
  const [prefillNote, setPrefillNote] = useState('');
  const money = (v: number) => fmtMoney(v ?? 0, settings?.currency ?? 'USD');
  const dummyId = settings?.dummyInventoryItemId;
  // A new Dummy row points at the Dummy item itself; a saved one points at
  // the sold record it created (splitFromId = the Dummy item).
  const isDummyLine = (li: SaleLineItem) => !!dummyId && (li.inventoryItemId === dummyId
    || inventory.find(i => i.id === li.inventoryItemId)?.splitFromId === dummyId);

  const [header, setHeader] = useState<SaleHeader>({
    toEntityId: '', saleDate: todayISO(), terms: 30, dueDate: addDays(30), notes: '',
    dueDateMode: 'average', lessPercent: '',
  });
  const lessPct = parseLess(header.lessPercent);
  const indian = isIndian(settings);
  // Every price change goes through here: Less % (Belgian Keep %, or the
  // line's Less 1 / Less 2 when Indian) first, then the Dummy rule.
  const priced = (li: SaleLineItem, pct = lessPct) =>
    withDummyCost(applyLess(indian ? applyIndianLess(li) : li, pct), dummyId);
  const [lineItems, setLineItems] = useState<SaleLineItem[]>([]);
  // Indian: GST once on the invoice (CGST + SGST, or IGST), typed as %.
  const [gstIn, setGstIn] = useState<GstInput>({ cgst: '', sgst: '', igst: '' });
  const gstPcts: GstPercents = indian ? gstPercentsOf(gstIn) : {};
  // Lines added in this session whose stone carries no Description of its
  // own yet: look it up on the purchase line once. Saved lines are left as they are.
  const descLookedUp = useRef(new Set<string>());
  useEffect(() => {
    const db = getClientDb();
    if (!db || !open) return;
    const saved = new Set((editInvoice?.lineItems ?? []).map(l => l.id));
    const todo = lineItems.filter(l => !l.description && !saved.has(l.id) && !descLookedUp.current.has(l.id));
    if (todo.length === 0) return;
    todo.forEach(l => descLookedUp.current.add(l.id));
    // Not cancelled on re-render: the next run skips these ids, so this
    // reply must still land.
    (async () => {
      const found = new Map<string, string>();
      for (const l of todo) {
        const item = inventory.find(i => i.id === l.inventoryItemId);
        if (!item) continue;
        try {
          const d = await purchaseLineDescription(db, item);
          if (d) found.set(l.id, d);
        } catch { /* no access to purchases: leave it empty */ }
      }
      if (found.size === 0) return;
      // Only fill a box that's still empty (the user may have typed meanwhile).
      setLineItems(prev => prev.map(l => !l.description && found.has(l.id) ? { ...l, description: found.get(l.id) } : l));
    })();
  }, [lineItems, open, editInvoice, inventory]);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');
  const [createEntityMode, setCreateEntityMode] = useState<'customer' | null>(null);
  const [justCreatedEntity, setJustCreatedEntity] = useState<Entity | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  // Snapshot of the form as it was when the modal opened — "dirty" is just
  // "current form differs from this", so every one of the many setLineItems
  // call sites counts without each having to flag it. Same Discard / keep
  // editing prompt as Purchase's BatchAddModal.
  const baselineRef = useRef('');
  // Additional Charges & Expenses — saleExpenses docs tied to this sale.
  // originalExpensesRef holds what was loaded from Firestore when editing,
  // so save-time can diff (added / changed / removed) against it.
  const [expenseRows, setExpenseRows] = useState<SaleExpenseRow[]>([]);
  const originalExpensesRef = useRef<SaleExpense[]>([]);
  const expenseBaselineRef = useRef('[]');

  useEffect(() => {
    if (!open) return;
    let h: SaleHeader;
    let li: SaleLineItem[];
    if (editInvoice) {
      h = {
        toEntityId: editInvoice.toEntityId,
        saleDate:   editInvoice.saleDate,
        terms:      editInvoice.terms,
        dueDate:    editInvoice.dueDate,
        notes:      editInvoice.notes ?? '',
        dueDateMode: editInvoice.dueDateMode === 'separate' ? 'separate' : 'average',
        lessPercent: editInvoice.lessPercent ? String(editInvoice.lessPercent) : '',
      };
      li = editInvoice.lineItems;
    } else if (prefill) {
      const pct = prefill.keep > 0 && prefill.keep < 100 ? prefill.keep : 0;
      const date = prefill.saleDate || todayISO();
      h = {
        toEntityId: prefill.customerId, saleDate: date, terms: prefill.terms,
        dueDate: addDaysToDate(date, prefill.terms), notes: '', dueDateMode: 'average',
        lessPercent: pct ? String(pct) : '',
      };
      li = [];
      const skipped: string[] = [];
      const claimed = new Map<string, number>();
      for (const p of prefill.lines) {
        const item = sellableByCode(inventory, p.code, dummyId);
        if (!item) { skipped.push(`${p.code || `row ${p.rowNo}`} (not in stock)`); continue; }
        const isDummy = item.id === dummyId;
        const left = isDummy ? Infinity : availableCarat(item) - (claimed.get(item.id) ?? 0);
        if (!isDummy && left <= 1e-9) { skipped.push(`${p.code} (nothing left)`); continue; }
        const carat = round2(Math.min(p.carat > 0 ? p.carat : left, left));
        if (carat < p.carat - 1e-9) skipped.push(`${p.code} (only ${carat} ct of ${p.carat} added)`);
        claimed.set(item.id, (claimed.get(item.id) ?? 0) + carat);
        const base = inventoryToLineItem(item, carat);
        const price = p.pricePerCt > 0 ? p.pricePerCt : base.salePricePerCt;
        li.push(priced({
          ...base, salePricePerCt: price, amountUSD: price * carat,
          ...(p.brokerId ? { broker1EntityId: p.brokerId, broker1CommissionPercent: p.brokerPct } : {}),
        }, pct));
      }
      setPrefillNote(skipped.length ? `Not added: ${skipped.join(', ')}.` : '');
    } else {
      h = { toEntityId: '', saleDate: todayISO(), terms: 30, dueDate: addDays(30), notes: '', dueDateMode: 'average', lessPercent: '' };
      li = [];
    }
    setHeader(h);
    setLineItems(li);
    setGstIn(editInvoice ? gstInputFrom(editInvoice.gst) : gstInputFrom(isIndian(settings) ? defaultGst(settings) : null));
    baselineRef.current = formSnapshot(h, li);
    setError('');
    setConfirmDiscard(false);
    setExpenseRows([]);
    originalExpensesRef.current = [];
    expenseBaselineRef.current = '[]';
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Existing charges/expenses on the sale being edited — one-shot fetch (not
  // live), same as Purchase's Additional Details.
  useEffect(() => {
    if (!open || !editInvoice) return;
    const db = getClientDb();
    if (!db) return;
    let cancelled = false;
    getDocs(query(collection(db, 'saleExpenses'), where('saleId', '==', editInvoice.id)))
      .then(snap => {
        if (cancelled) return;
        const existing = snap.docs.map(d => ({ id: d.id, ...d.data() } as SaleExpense))
          .sort((a, b) => (a.expenseNo ?? '').localeCompare(b.expenseNo ?? ''));
        const rows = existing.map(saleExpenseToRow);
        originalExpensesRef.current = existing;
        expenseBaselineRef.current = JSON.stringify(rows);
        setExpenseRows(rows);
      })
      .catch(() => { /* leave the section empty rather than block opening the modal */ });
    return () => { cancelled = true; };
  }, [open, editInvoice]);

  function addExpenseRow() {
    setExpenseRows(prev => [...prev, newSaleExpenseRow(header.saleDate)]);
  }
  function updateExpenseRow<K extends keyof SaleExpenseRow>(key: string, field: K, value: SaleExpenseRow[K]) {
    setExpenseRows(prev => prev.map(r => {
      if (r._key !== key) return r;
      const next = { ...r, [field]: value };
      // Pre-pick the default account when switching to "paid now".
      if (field === 'payNow' && value && !next.bankAccountId) {
        next.bankAccountId = (bankAccounts.find(a => a.isDefault) ?? bankAccounts[0])?.id ?? '';
      }
      return next;
    }));
  }
  function removeExpenseRow(key: string) {
    setExpenseRows(prev => prev.filter(r => r._key !== key));
  }
  const expenseTotals = useMemo(() => saleExpenseTotals(expenseRows), [expenseRows]);

  const customers = useMemo(() => {
    const base = entities.filter(e => e.types.includes('customer'));
    return justCreatedEntity && justCreatedEntity.types.includes('customer') && !base.some(e => e.id === justCreatedEntity.id)
      ? [...base, justCreatedEntity]
      : base;
  }, [entities, justCreatedEntity]);

  const brokers = useMemo(() => {
    const base = entities.filter(e => e.types.includes('broker'));
    return justCreatedEntity && justCreatedEntity.types.includes('broker') && !base.some(e => e.id === justCreatedEntity.id)
      ? [...base, justCreatedEntity]
      : base;
  }, [entities, justCreatedEntity]);

  // Legacy on-memo items (status still 'on-memo') are sellable too — a direct
  // memo→sale conversion, no separate "return it first" step. planNewSales/
  // applySplitPlan don't care what status the source was in; they just
  // always leave it 'sold'. A current-model 'in-stock' item is only pickable
  // while it has some AVAILABLE carat left — part of it may be held on memo.
  const available = useMemo(() => {
    const usedIds = new Set(lineItems.map(li => li.inventoryItemId));
    return inventory.filter(i => {
      if (usedIds.has(i.id)) return false;
      // Consigned (memo-in) stock isn't ours to sell yet — it must be
      // converted to a purchase on the Memo In page first.
      if (i.provenance === 'memo-in') return false;
      // The Dummy code stays pickable at any balance, even 0 or below.
      if (i.id === dummyId) return true;
      if (i.status === 'on-memo') return true;
      if (i.status !== 'in-stock') return false;
      return availableCarat(i) > 1e-9;
    });
  }, [inventory, lineItems, dummyId]);

  // Rows carried over from the invoice being edited — carat is locked on these
  // (remove & re-add to change quantity on an already-saved sale); only freshly
  // added rows (here, or mid-edit) can have their sold carat adjusted. Keyed by
  // the row's own `id`, not `inventoryItemId` — a duplicate row (via "+ Add
  // Row") shares its source's inventoryItemId with an original row, but must
  // NOT inherit that row's carat lock.
  const originalIds = useMemo(
    () => new Set((editInvoice?.lineItems ?? []).map(li => li.id)),
    [editInvoice],
  );

  // ── Customer's Memos panel ── once a customer is picked, show everything
  // currently out on memo with THEM specifically — pick from here to close a
  // memo as part of this sale, alongside any ordinary line items added below.
  const customerActiveMemos = useMemo(
    () => (header.toEntityId ? memos.filter(m => m.entityId === header.toEntityId) : []),
    [memos, header.toEntityId],
  );
  const customerLegacyMemoItems = useMemo(
    () => (header.toEntityId ? inventory.filter(i => i.status === 'on-memo' && i.memoToEntityId === header.toEntityId) : []),
    [inventory, header.toEntityId],
  );
  const addedMemoIds = useMemo(
    () => new Set(lineItems.map(li => li.sourceMemoId).filter((x): x is string => !!x)),
    [lineItems],
  );

  // A memo-sourced line draws from that one memo's own held carat — a pool
  // entirely separate from the parcel's availableCarat (and, since a memo
  // can only be added to a sale once — see addedMemoIds/"✓ Added" above —
  // never shared with a sibling row either). Ordinary rows still cap at
  // availableCarat as before.
  function caratCapFor(li: SaleLineItem, source: InventoryItem | undefined): number {
    if (li.sourceMemoId) {
      const sourceMemo = memos.find(m => m.id === li.sourceMemoId);
      return sourceMemo ? sourceMemo.carat : li.carat;
    }
    if (dummyId && li.inventoryItemId === dummyId) return Infinity;   // no ceiling — may go below zero
    return source ? availableCarat(source) : li.carat;
  }

  function setH<K extends keyof SaleHeader>(k: K, v: SaleHeader[K]) { setHeader(h => ({ ...h, [k]: v })); }

  // Average mode: Due Date is derived live from every item's own Terms
  // (blank items fall back to the header's own Payment Terms), averaged
  // and rounded UP to the next whole day — e.g. 30d + 45d → 37.5 → 38d.
  // If no item overrides Terms, every item's effective value is just
  // header.terms, so the average is header.terms itself and Due Date
  // stays exactly saleDate + terms, today's original behavior. Doesn't
  // touch header.terms itself — that stays the plain, manually-set
  // baseline (and each new row's Terms placeholder), only Due Date is
  // derived. Mirrors Purchase.tsx's BatchAddModal exactly.
  useEffect(() => {
    if (header.dueDateMode !== 'average' || lineItems.length === 0) return;
    const effectiveTerms = lineItems.map(li => (li.terms && li.terms > 0) ? li.terms : header.terms);
    const avgTerms = Math.ceil(effectiveTerms.reduce((s, t) => s + t, 0) / effectiveTerms.length);
    setH('dueDate', addDaysToDate(header.saleDate, avgTerms));
  }, [lineItems, header.saleDate, header.terms, header.dueDateMode]); // eslint-disable-line react-hooks/exhaustive-deps

  function addItem(item: InventoryItem) {
    setLineItems(prev => {
      // Memo-sourced siblings don't count here — they draw from that memo's
      // own held carat (being closed by this same sale), not from
      // availableCarat, so they'd otherwise double-subtract against it.
      const claimed   = prev.filter(li => li.inventoryItemId === item.id && !li.sourceMemoId).reduce((s, li) => s + li.carat, 0);
      const remaining = round2(Math.max(0, availableCarat(item) - claimed));
      const isDummy = item.id === dummyId;
      // Dummy: start blank (0 ct, no price) — its balance may already be 0
      // or negative, and its average is no guide to the Books price.
      const li = isDummy
        ? { ...inventoryToLineItem(item, 0), salePricePerCt: 0, amountUSD: 0, costUSD: 0, costingUSD: 0 }
        : inventoryToLineItem(item, remaining);
      // The Books Adjustment/Dummy code (see Settings › Custom Options) can
      // only ever be sold with the Dummy/Books category — that's the whole
      // point of the code, reconciling the Books view back down. Locked
      // here, and re-enforced server-side in planDummyCreditDelta.
      if (item.id === settings?.dummyInventoryItemId) li.category = 'books-dummy';
      return [...prev, priced(li)];
    });
  }
  // Add the stone the form was opened for, once, as soon as stock has loaded.
  const startedWith = useRef<string | null>(null);
  useEffect(() => {
    if (!open) { startedWith.current = null; return; }
    if (editInvoice || !initialItemId || startedWith.current === initialItemId) return;
    const item = inventory.find(i => i.id === initialItemId);
    if (!item) return;
    startedWith.current = initialItemId;
    addItem(item);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialItemId, inventory, editInvoice]);
  function addMemoItem(memo: InventoryMemo) {
    const item = inventory.find(i => i.id === memo.inventoryItemId);
    if (!item) return;
    setLineItems(prev => [...prev, priced(memoToLineItem(item, memo))]);
  }
  function addDuplicateRow() {
    const last = lineItems[lineItems.length - 1];
    const source = last && inventory.find(i => i.id === last.inventoryItemId);
    if (source) addItem(source);
  }
  function removeItem(rowId: string) { setLineItems(prev => prev.filter(li => li.id !== rowId)); }
  function updateDescription(rowId: string, text: string) {
    setLineItems(prev => prev.map(li => li.id === rowId ? { ...li, description: text || undefined } : li));
  }
  function updateCategory(rowId: string, category: TransactionCategory) {
    setLineItems(prev => prev.map(li => li.id === rowId ? { ...li, category } : li));
  }
  function updateTerms(rowId: string, raw: string) {
    const t = parseInt(raw, 10);
    setLineItems(prev => prev.map(li => li.id === rowId ? { ...li, terms: t > 0 ? t : undefined } : li));
  }
  function updatePricePerCt(rowId: string, pricePerCt: number) {
    setLineItems(prev => prev.map(li => li.id === rowId
      // The typed price is the full (agreed) price; priced() works out the net.
      ? priced(indian
          ? { ...li, agreedPricePerCt: pricePerCt, grossPricePerCt: undefined, salePricePerCt: pricePerCt }
          : { ...li, grossPricePerCt: undefined, salePricePerCt: pricePerCt })
      : li));
  }
  // Indian: one line's Less 1 / Less 2 changed.
  function updateLineLess(rowId: string, which: 'less1Percent' | 'less2Percent', raw: string) {
    const v = parseLess(raw);
    setLineItems(prev => prev.map(li => li.id === rowId ? priced({ ...li, [which]: v || undefined }) : li));
  }
  // Less % changed — re-price every line from its full price.
  function setLess(raw: string) {
    setH('lessPercent', raw);
    const pct = parseLess(raw);
    setLineItems(prev => prev.map(li => priced(li, pct)));
  }
  function updateCarat(rowId: string, carat: number) {
    setLineItems(prev => {
      const target = prev.find(li => li.id === rowId);
      if (!target) return prev;
      const source = inventory.find(i => i.id === target.inventoryItemId);
      const maxCarat = caratCapFor(target, source);
      // Memo-sourced siblings draw from that memo's own held carat, not
      // availableCarat — excluded here for the same reason as in addItem.
      // A memo-sourced row has no siblings of its own to subtract (a memo
      // can only be added once), so this is a no-op for it either way.
      const claimedBySiblings = target.sourceMemoId ? 0 : prev
        .filter(li => li.id !== rowId && li.inventoryItemId === target.inventoryItemId && !li.sourceMemoId)
        .reduce((s, li) => s + li.carat, 0);
      const roomLeft = round2(Math.max(0.01, maxCarat - claimedBySiblings));
      const clamped  = round2(Math.min(Math.max(carat || 0, 0.01), roomLeft));
      const costPerCt     = source && source.carat > 0 ? source.costingUSD / source.carat : 0;
      const baseCostPerCt = source && source.carat > 0 ? source.costUSD   / source.carat : 0;
      return prev.map(li => li.id !== rowId ? li : priced({
        ...li,
        carat: clamped,
        costingUSD: costPerCt * clamped,
        costUSD: baseCostPerCt * clamped,
      }));
    });
  }
  function updateBroker1(rowId: string, entityId: string) {
    setLineItems(prev => prev.map(li => li.id === rowId
      ? { ...li, broker1EntityId: entityId || undefined, broker1CommissionPercent: entityId ? li.broker1CommissionPercent : undefined }
      : li));
  }
  function updateBroker1Pct(rowId: string, raw: string) {
    const p = parseFloat(raw);
    setLineItems(prev => prev.map(li => li.id === rowId ? { ...li, broker1CommissionPercent: p >= 0 ? p : undefined } : li));
  }
  function updateBroker2(rowId: string, entityId: string) {
    setLineItems(prev => prev.map(li => li.id === rowId
      ? { ...li, broker2EntityId: entityId || undefined, broker2CommissionPercent: entityId ? li.broker2CommissionPercent : undefined }
      : li));
  }
  function updateBroker2Pct(rowId: string, raw: string) {
    const p = parseFloat(raw);
    setLineItems(prev => prev.map(li => li.id === rowId ? { ...li, broker2CommissionPercent: p >= 0 ? p : undefined } : li));
  }

  const totals = useMemo(() => ({
    ct:    lineItems.reduce((s, li) => s + li.carat, 0),
    amt:   lineItems.reduce((s, li) => s + li.amountUSD, 0),                         // net
    gross: lineItems.reduce((s, li) => s + (li.grossAmountUSD ?? li.amountUSD), 0),  // before Less %
  }), [lineItems]);

  const lastLi = lineItems[lineItems.length - 1];
  const lastLiRoomLeft = (() => {
    if (!lastLi) return 0;
    const source = inventory.find(i => i.id === lastLi.inventoryItemId);
    if (!source) return 0;
    if (source.id === dummyId) return Infinity;
    const claimed = lineItems
      .filter(li => li.inventoryItemId === lastLi.inventoryItemId && !li.sourceMemoId)
      .reduce((s, li) => s + li.carat, 0);
    return Math.max(0, availableCarat(source) - claimed);
  })();
  const canAddDuplicateRow = !!lastLi && lastLi.itemType === 'loose' && lastLiRoomLeft > 1e-6;

  function validate(): string {
    if (!header.toEntityId) return 'Select a customer.';
    if (!header.dueDate)    return 'Set a due date.';
    if (lineItems.length === 0) return 'Add at least one item.';
    for (let i = 0; i < lineItems.length; i++) {
      if (!(lineItems[i].carat > 0))     return `Row ${i + 1}: Enter a carat weight.`;
      if (!(lineItems[i].amountUSD > 0)) return `Row ${i + 1}: Enter a sale amount.`;
    }
    if (header.lessPercent.trim() && !(parseFloat(header.lessPercent) >= 0 && parseFloat(header.lessPercent) < 100)) {
      return 'Keep(%) must be between 0 and 100.';
    }
    if (indian && gstConflict(gstPcts)) return 'Use CGST + SGST, or IGST, not both.';
    // A saved Books Adjustment line's amount is what left the Dummy balance;
    // re-pricing it (e.g. by changing Less %) would put the two out of step.
    if (isEdit && editInvoice) {
      const was = new Map(editInvoice.lineItems.map(l => [l.id, l.amountUSD]));
      if (lineItems.some(li => isDummyLine(li) && was.has(li.id) && Math.abs((was.get(li.id) ?? 0) - li.amountUSD) > 0.005)) {
        return 'A saved Books Adjustment line can’t change price. Delete it and add it again, or use the old Keep(%).';
      }
    }
    const expErr = validateSaleExpenseRows(expenseRows);
    if (expErr) return expErr;
    // Both would write the Dummy balance in one save (a Cash/Real line
    // credits it, a Books Adjustment line draws from it).
    if (lineItems.some(isDummyLine) && lineItems.some(li => li.category === 'cash-real')) {
      return 'The Books Adjustment code can’t be on the same invoice as a Cash/Real line — save them as separate sales.';
    }
    // Two+ rows can share a source (same lot, different price) — check the
    // combined carat requested against each source doesn't overdraw it. This
    // is a UX nicety only; the transaction re-checks against live data.
    // Memo-sourced rows are excluded from the total — they draw from that
    // memo's own held carat (a separate pool, being closed by this same
    // sale), not from availableCarat, so they mustn't count against it.
    const claimedBySource = new Map<string, number>();
    lineItems.forEach(li => {
      if (li.sourceMemoId) return;
      if (li.inventoryItemId === dummyId) return;   // the Dummy code has no ceiling
      claimedBySource.set(li.inventoryItemId, (claimedBySource.get(li.inventoryItemId) ?? 0) + li.carat);
    });
    for (const [invId, totalCarat] of claimedBySource) {
      const source = inventory.find(i => i.id === invId);
      if (source) {
        const avail = availableCarat(source);
        if (totalCarat > avail + 1e-6) {
          return `${source.shape} ${source.color}/${source.clarity}: rows total ${totalCarat.toFixed(2)}ct — only ${avail.toFixed(2)}ct available.`;
        }
      }
    }
    return '';
  }

  async function handleSave() {
    const err = validate();
    if (err) { setError(err); return; }
    setSaving(true); setError('');
    const db = getClientDb();
    if (!db) { setError('Database not connected.'); setSaving(false); return; }

    const itemCount       = lineItems.length;
    const totalCarat      = lineItems.reduce((s, li) => s + li.carat, 0);
    // Goods revenue vs. what the customer owes: re-billed charges ride on
    // top of the goods in totalAmountUSD (so AR / payments / status all
    // just work), but never count as revenue or margin.
    const goodsUSD        = lineItems.reduce((s, li) => s + li.amountUSD, 0);
    // Less %: what came off the full (agreed) prices. Lines already hold net.
    const lessAmountUSD   = round2(lineItems.reduce((s, li) => s + (li.grossAmountUSD ?? li.amountUSD), 0) - goodsUSD);
    const chargesUSD      = expenseTotals.charged;
    const sellingExpensesUSD = expenseTotals.own;
    // Indian GST: once, on the stones' final total (never on charges).
    const tax             = indian ? invoiceGst(round2(goodsUSD), gstPcts) : { cgst: 0, sgst: 0, igst: 0, total: 0 };
    const totalAmountUSD  = round2(goodsUSD + chargesUSD + tax.total);
    const gstFields = indian && tax.total > 0
      ? { gst: { cgstPct: gstPcts.cgstPct ?? null, sgstPct: gstPcts.sgstPct ?? null, igstPct: gstPcts.igstPct ?? null },
          taxableUSD: round2(goodsUSD), cgstUSD: tax.cgst, sgstUSD: tax.sgst, igstUSD: tax.igst, taxTotalUSD: tax.total }
      : { gst: null, taxableUSD: null, cgstUSD: null, sgstUSD: null, igstUSD: null, taxTotalUSD: null };
    const totalCostingUSD = lineItems.reduce((s, li) => s + li.costingUSD, 0);
    const totalMarginUSD  = goodsUSD - totalCostingUSD;
    const expensePlan     = planSaleExpenses(db, expenseRows, originalExpensesRef.current);
    const extraCosts      = extraCostsOf(expensePlan);
    const expensePrefix   = settings?.saleExpensePrefix || 'SE';
    // Charges are payable with the first (earliest) due date in 'separate' mode.
    const addChargesToFirstGroup = <G extends { amountUSD: number }>(groups: G[] | undefined) => {
      // GST goes on each due date in step with its stones (the last one takes the rounding).
      if (groups && groups.length > 0 && tax.total > 0 && goodsUSD > 0) {
        let left = tax.total;
        groups.forEach((g, i) => {
          const share = i === groups.length - 1 ? left : round2(tax.total * g.amountUSD / goodsUSD);
          left = round2(left - share);
          g.amountUSD = round2(g.amountUSD + share);
        });
      }
      if (groups && groups.length > 0 && chargesUSD > 0) groups[0].amountUSD = round2(groups[0].amountUSD + chargesUSD);
      return groups;
    };

    try {
      if (isEdit && editInvoice) {
        // A sale with any credit note against it is locked — editing lines
        // would silently undo the return's restock/credit.
        const srSnap = await getDocs(query(collection(db, 'salesReturns'), where('saleId', '==', editInvoice.id)));
        if (!srSnap.empty) {
          throw new Error('This sale has a credit note recorded against it — delete the credit note first if you need to edit it.');
        }
        // A 'separate'-mode sale with any payment already recorded against
        // one of its due dates is locked the same way — changing line items
        // or their Terms would reshuffle which due date each item belongs
        // to, and there's no safe way to re-map an already-recorded payment
        // onto a due-date grouping that no longer exists the same way.
        if ((editInvoice.dueGroups ?? []).some(g => g.paidUSD > 0 || g.creditedUSD > 0)) {
          throw new Error('This invoice has a payment recorded against one of its due dates — that payment must be removed first if you need to edit it.');
        }
        // paidUSD/creditedUSD are untouched by an edit, but paymentStatus is
        // stored (not derived), so it must be recomputed against the NEW
        // totalAmountUSD here — otherwise raising a paid sale's total (e.g.
        // adding a line item) leaves it showing "Paid" while it actually has
        // fresh outstanding balance.
        const settledUSD = (editInvoice.paidUSD ?? 0) + (editInvoice.creditedUSD ?? 0);
        const editedPaymentStatus: PaymentStatus =
          settledUSD >= totalAmountUSD - 0.005 ? 'paid' : settledUSD > 0 ? 'partial' : 'unpaid';
        const invRef  = doc(db, 'sales', editInvoice.id);
        const oldIds  = new Set(editInvoice.lineItems.map(li => li.inventoryItemId));
        const newIds  = new Set(lineItems.map(li => li.inventoryItemId));
        const removedLineItems = editInvoice.lineItems.filter(li => !newIds.has(li.inventoryItemId));
        const addedLineItems   = lineItems.filter(li => !oldIds.has(li.inventoryItemId));
        const staidLineItems   = lineItems.filter(li => oldIds.has(li.inventoryItemId));
        const removedSaleHistoryRefs = await findSaleCodeHistoryRefs(db, removedLineItems);

        await runTransaction(db, async tx => {
          // All reads before any writes.
          // Only needed if this edit is the very first 'cash-real' line ever
          // saved for this business (planDummyCreditDelta lazily creates the
          // Dummy item and needs a Code counter to auto-number it from) —
          // harmless extra read otherwise, same doc Purchase.tsx already
          // reads this way for its own itemCode auto-numbering.
          const counterSnap = await tx.get(doc(db, 'counters', business!.id));
          const expenseAccts = await readSaleExpenseAccounts(tx, expensePlan);
          const reversalPlan = await planSaleReversal(tx, db, removedLineItems);
          // A removed line item's source lot can still be restored below — if the
          // same lot was also freshly re-picked in this same edit, the two writes
          // would race against each other. Rare (remove-and-re-add-in-one-save),
          // but ask for a plain re-edit instead of risking a silently wrong split.
          // (The Dummy code is exempt — it's a running balance, handled by
          // folding the two moves into one below.)
          const collidesWithReversal = addedLineItems.some(li => li.inventoryItemId !== dummyId && reversalPlan.restoreDeltas.has(li.inventoryItemId));
          if (collidesWithReversal) {
            throw new Error('You removed and re-added the same stone in this edit. Save the removal first, then edit again to sell part of it.');
          }
          const addedSnaps = await Promise.all(addedLineItems.map(li => tx.get(doc(db, 'inventory', li.inventoryItemId))));
          const addedSources = addedSnaps.map((snap, i) => {
            const st = snap.exists() ? (snap.data() as InventoryItem).status : undefined;
            if (st !== 'in-stock' && st !== 'on-memo' && !(snap.exists() && snap.id === dummyId)) {
              throw new Error(`${addedLineItems[i].shape} is no longer available. Please review and try again.`);
            }
            // DocumentSnapshot.data() never includes the doc's own id — it must
            // be attached manually, or every `source.id` downstream (grouping,
            // splitFromId, the doc() calls in applySplitPlan) is silently undefined.
            return { id: snap.id, ...snap.data() } as InventoryItem;
          });
          // Removed AND added Dummy rows in one edit: both would write the
          // Dummy doc. Start the new rows from the balance the removal
          // restores, and let only planNewSales' update write it.
          const dummyRestore = dummyId ? reversalPlan.restoreDeltas.get(dummyId) : undefined;
          if (dummyRestore && addedSources.some(src => src.id === dummyId)) {
            addedSources.forEach((src, i) => {
              if (src.id === dummyId) addedSources[i] = { ...src, carat: dummyRestore.carat, costUSD: dummyRestore.costUSD, costingUSD: dummyRestore.costingUSD };
            });
            reversalPlan.restoreDeltas.delete(dummyId!);
          }
          // Re-verify any memo this sale is about to close is still active —
          // it may have been returned or already sold elsewhere since this
          // line item was added in the UI.
          const addedMemoSnaps = await Promise.all(addedLineItems.map((li, i) =>
            li.sourceMemoId ? tx.get(doc(db, 'inventory', addedSources[i].id, 'memos', li.sourceMemoId)) : Promise.resolve(null)
          ));
          const liveMemos = new Map<string, InventoryMemo>();
          addedLineItems.forEach((li, i) => {
            if (!li.sourceMemoId) return;
            const s = addedMemoSnaps[i];
            if (!s || !s.exists() || (s.data() as InventoryMemo).status !== 'active') {
              throw new Error(`${li.shape} ${li.carat.toFixed(2)}ct's memo is no longer active — it may already be returned or sold. Please reload and try again.`);
            }
            liveMemos.set(li.sourceMemoId, s.data() as InventoryMemo);
          });
          const splitPlan = planNewSales(db, addedLineItems, addedSources, header, invRef.id, editInvoice.invoiceNo, liveMemos, dummyId);

          const finalLineItems = [...staidLineItems, ...splitPlan.finalLineItems];
          const dummyPlan = await planDummyCreditDelta(tx, db, business!.id, editInvoice.id, finalLineItems, settings, counterSnap);

          // Re-derive dueGroups from the (possibly changed) line items —
          // safe because the lock above already guarantees no group has any
          // payment/credit yet, so there's nothing to reconcile.
          const dueGroups = addChargesToFirstGroup(header.dueDateMode === 'separate'
            ? computeDueGroups(finalLineItems, header.saleDate, header.terms, li => li.terms, li => li.amountUSD)
            : undefined);
          const effectiveDueDate = dueGroups && dueGroups.length > 0 ? dueGroups[0].dueDate : header.dueDate;

          // Persist the Dummy item's freshly-auto-numbered Code counter, if
          // this edit is what lazily created it — a transaction can't write
          // counters/{businessId} twice, and this edit path has no other
          // write to that doc, so it's just this one standalone set.
          const expenseCounterStart = counterSnap.exists() ? (counterSnap.data().saleExpense ?? 0) : 0;
          const editCounterUpdate: Record<string, number> = {};
          if (dummyPlan?.newItemCodeCounter !== undefined) editCounterUpdate.itemCode = dummyPlan.newItemCodeCounter;
          if (expensePlan.creates.length > 0) editCounterUpdate.saleExpense = expenseCounterStart + expensePlan.creates.length;
          if (Object.keys(editCounterUpdate).length > 0) {
            tx.set(doc(db, 'counters', business!.id), editCounterUpdate, { merge: true });
          }

          // No broker* keys here at all (not even undefined) — a legacy sale
          // that already has header-level broker data (from before this
          // field was removed from the form) keeps it completely untouched;
          // Firestore's partial update() simply never sees those keys.
          tx.update(invRef, stripUndef({
            toEntityId: header.toEntityId,
            saleDate:   header.saleDate,
            terms:      header.terms,
            dueDate:    effectiveDueDate,
            dueDateMode: header.dueDateMode === 'separate' ? 'separate' : null,
            dueGroups:   dueGroups ?? null,
            itemCount, totalCarat, totalAmountUSD, totalCostingUSD, totalMarginUSD,
            chargesUSD, sellingExpensesUSD, extraCosts,
            lessPercent: lessPct > 0 ? lessPct : null,
            lessAmountUSD: lessPct > 0 ? lessAmountUSD : null,
            ...gstFields,
            paymentStatus: editedPaymentStatus,
            lineItems: finalLineItems,
            notes: header.notes.trim() || undefined,
            updatedAt: serverTimestamp(),
            ...updatedStamp(user),
          } as Record<string, unknown>));

          logActivity(tx, db, business!.id, user, {
            module: 'sale', action: 'edit', recordId: editInvoice.id, recordNo: editInvoice.invoiceNo,
            partyId: header.toEntityId || undefined, itemCount, carat: totalCarat, amountUSD: totalAmountUSD,
            prevItemCount: editInvoice.itemCount, prevCarat: editInvoice.totalCarat, prevAmountUSD: editInvoice.totalAmountUSD,
          });

          applySaleReversal(tx, db, reversalPlan);
          removedSaleHistoryRefs.forEach(ref => tx.delete(ref));
          applySplitPlan(tx, db, splitPlan, header, user!.id);
          applyDummyCreditDelta(tx, db, dummyPlan, header, editInvoice.invoiceNo, user!.id);
          applySaleExpenses(tx, db, expensePlan, expenseAccts, {
            businessId: business!.id, userId: user!.id, baseCurrency: settings?.currency ?? 'USD',
            prefix: expensePrefix, counterStart: expenseCounterStart,
            saleId: editInvoice.id, saleInvoiceNo: editInvoice.invoiceNo,
            saleDate: header.saleDate || todayISO(), customerEntityId: header.toEntityId,
          });

          staidLineItems.forEach(li => tx.update(doc(db, 'inventory', li.inventoryItemId), {
            soldAmountUSD: li.amountUSD, updatedAt: serverTimestamp(),
          }));

          // Ledger (additive) — overwrite this sale's revenue/COGS entry, and
          // its broker-accrual entry, with recomputed values. A now-$0 sale or
          // a broker removed on this edit drops the corresponding entry.
          const cogsUSD = round2(finalLineItems.reduce((s, li) => s + (li.costUSD || 0), 0));
          const siId = entryId('sales_invoice', editInvoice.id);
          if (goodsUSD > 0.005) {
            postEntry(tx, db, buildEntry({
              businessId: business!.id, sourceType: 'sales_invoice', sourceId: editInvoice.id,
              sourceCollection: 'sales', date: header.saleDate || todayISO(),
              currency: settings?.currency ?? 'USD', createdBy: user!.id,
              note: `Sale ${editInvoice.invoiceNo}`,
              lines: linesSaleInvoice(goodsUSD, cogsUSD, header.toEntityId, tax.total),
            }), siId);
          } else {
            tx.delete(doc(db, 'journalEntries', siId));
          }
          // No broker_accrual recompute/delete here — a legacy sale's
          // existing accrual entry (if any) is left exactly as it was,
          // same "untouched" reasoning as the invoice doc's own fields above.

          // Per-line Broker1/Broker2 commission on edit — any broker present
          // in the OLD line items but not (or no longer earning anything in)
          // the NEW ones gets its accrual entry deleted; every broker still
          // earning something gets it upserted at the recomputed amount.
          const oldLineBrokerTotals = computeLineBrokerCommissions(editInvoice.lineItems ?? []);
          const newLineBrokerTotals = computeLineBrokerCommissions(finalLineItems);
          const allLineBrokerIds = new Set([...oldLineBrokerTotals.keys(), ...newLineBrokerTotals.keys()]);
          for (const brokerEntityId of allLineBrokerIds) {
            const commissionUSD = newLineBrokerTotals.get(brokerEntityId) ?? 0;
            const lbaId = lineBrokerAccrualId(editInvoice.id, brokerEntityId);
            if (commissionUSD > 0.005) {
              postEntry(tx, db, buildEntry({
                businessId: business!.id, sourceType: 'broker_accrual', sourceId: editInvoice.id,
                sourceCollection: 'sales', date: header.saleDate || todayISO(),
                currency: settings?.currency ?? 'USD', createdBy: user!.id,
                note: `Broker commission (per-line) — ${editInvoice.invoiceNo}`,
                lines: linesBrokerAccrual(commissionUSD, brokerEntityId),
              }), lbaId);
            } else {
              tx.delete(doc(db, 'journalEntries', lbaId));
            }
          }
        });
      } else {
        const prefix     = settings?.salesPrefix ?? 'SL';
        const counterRef = doc(db, 'counters', business!.id);
        const invRef     = doc(collection(db, 'sales'));

        await runTransaction(db, async tx => {
          const snap  = await tx.get(counterRef);
          const count = snap.exists() ? (snap.data().salesInvoice ?? 0) : 0;
          const invoiceNo = fmtInvoiceNo(prefix, count + 1);
          const expenseCounterStart = snap.exists() ? (snap.data().saleExpense ?? 0) : 0;
          const expenseAccts = await readSaleExpenseAccounts(tx, expensePlan);

          // All reads before any writes: re-verify every selected item is still in-stock at commit time.
          const itemSnaps = await Promise.all(lineItems.map(li => tx.get(doc(db, 'inventory', li.inventoryItemId))));
          const sources = itemSnaps.map((s, i) => {
            const status = s.exists() ? (s.data() as InventoryItem).status : undefined;
            if (status !== 'in-stock' && status !== 'on-memo' && !(s.exists() && s.id === dummyId)) {
              throw new Error(`${lineItems[i].shape} ${lineItems[i].carat.toFixed(2)}ct is no longer available${status ? ` (already ${status})` : ''}.`);
            }
            // DocumentSnapshot.data() never includes the doc's own id — it must
            // be attached manually, or every `source.id` downstream (grouping,
            // splitFromId, the doc() calls in applySplitPlan) is silently undefined.
            return { id: s.id, ...s.data() } as InventoryItem;
          });
          // Re-verify any memo this sale is about to close is still active —
          // it may have been returned or already sold elsewhere since this
          // line item was added in the UI.
          const memoSnaps = await Promise.all(lineItems.map((li, i) =>
            li.sourceMemoId ? tx.get(doc(db, 'inventory', sources[i].id, 'memos', li.sourceMemoId)) : Promise.resolve(null)
          ));
          const liveMemos = new Map<string, InventoryMemo>();
          lineItems.forEach((li, i) => {
            if (!li.sourceMemoId) return;
            const s = memoSnaps[i];
            if (!s || !s.exists() || (s.data() as InventoryMemo).status !== 'active') {
              throw new Error(`${li.shape} ${li.carat.toFixed(2)}ct's memo is no longer active — it may already be returned or sold. Please reload and try again.`);
            }
            liveMemos.set(li.sourceMemoId, s.data() as InventoryMemo);
          });
          const splitPlan = planNewSales(db, lineItems, sources, header, invRef.id, invoiceNo, liveMemos, dummyId);
          const dummyPlan = await planDummyCreditDelta(tx, db, business!.id, invRef.id, splitPlan.finalLineItems, settings, snap);

          // 'separate' mode: group line items by their own computed due
          // date; the invoice's own dueDate becomes the earliest group's
          // date. 'average' mode (default): today's exact behavior, no
          // dueGroups written at all.
          const dueGroups = addChargesToFirstGroup(header.dueDateMode === 'separate'
            ? computeDueGroups(splitPlan.finalLineItems, header.saleDate, header.terms, li => li.terms, li => li.amountUSD)
            : undefined);
          const effectiveDueDate = dueGroups && dueGroups.length > 0 ? dueGroups[0].dueDate : header.dueDate;

          // A transaction can't write counters/{businessId} twice — fold the
          // Dummy item's freshly-auto-numbered Code counter into this same
          // write, if it needed lazily creating (same convention Purchase.tsx
          // uses to fold its own item-code + invoice-number counters together).
          tx.set(counterRef, stripUndef({
            salesInvoice: count + 1, itemCode: dummyPlan?.newItemCodeCounter,
            saleExpense: expensePlan.creates.length > 0 ? expenseCounterStart + expensePlan.creates.length : undefined,
          }), { merge: true });
          tx.set(invRef, stripUndef({
            businessId: business!.id,
            invoiceNo,
            toEntityId: header.toEntityId,
            saleDate:   header.saleDate,
            terms:      header.terms,
            dueDate:    effectiveDueDate,
            dueDateMode: header.dueDateMode === 'separate' ? 'separate' : undefined,
            dueGroups,
            itemCount, totalCarat, totalAmountUSD, totalCostingUSD, totalMarginUSD,
            chargesUSD: chargesUSD || undefined,
            sellingExpensesUSD: sellingExpensesUSD || undefined,
            extraCosts: extraCosts.length > 0 ? extraCosts : undefined,
            lessPercent: lessPct > 0 ? lessPct : undefined,
            lessAmountUSD: lessPct > 0 ? lessAmountUSD : undefined,
            ...(gstFields.taxTotalUSD ? gstFields : {}),
            // The EUR rate is saved on the invoice from day one, so a later
            // change to the default never rewrites this invoice.
            invoiceOptions: settings?.invoiceEurUsdRate ? { eurUsdRate: settings.invoiceEurUsdRate } : undefined,
            paidUSD:       0,
            paymentStatus: 'unpaid' as PaymentStatus,
            lineItems: splitPlan.finalLineItems,
            notes: header.notes.trim() || undefined,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            ...createdStamp(user),
          } as Record<string, unknown>));

          logActivity(tx, db, business!.id, user, {
            module: 'sale', action: 'create', recordId: invRef.id, recordNo: invoiceNo,
            partyId: header.toEntityId || undefined, itemCount, carat: totalCarat, amountUSD: totalAmountUSD,
          });

          applySplitPlan(tx, db, splitPlan, header, user!.id);
          applyDummyCreditDelta(tx, db, dummyPlan, header, invoiceNo, user!.id);
          applySaleExpenses(tx, db, expensePlan, expenseAccts, {
            businessId: business!.id, userId: user!.id, baseCurrency: settings?.currency ?? 'USD',
            prefix: expensePrefix, counterStart: expenseCounterStart,
            saleId: invRef.id, saleInvoiceNo: invoiceNo,
            saleDate: header.saleDate || todayISO(), customerEntityId: header.toEntityId,
          });

          // Ledger (additive) — Dr AR / Cr Revenue, and Dr COGS / Cr
          // Inventory (Owned) at actual cost. Broker commission accrues as
          // its own entry so it settles independently.
          const cogsUSD = round2(splitPlan.finalLineItems.reduce((s, li) => s + (li.costUSD || 0), 0));
          if (goodsUSD > 0.005) {
            postEntry(tx, db, buildEntry({
              businessId: business!.id, sourceType: 'sales_invoice', sourceId: invRef.id,
              sourceCollection: 'sales', date: header.saleDate || todayISO(),
              currency: settings?.currency ?? 'USD', createdBy: user!.id,
              note: `Sale ${invoiceNo}`,
              lines: linesSaleInvoice(goodsUSD, cogsUSD, header.toEntityId, tax.total),
            }), entryId('sales_invoice', invRef.id));
          }

          // Per-line Broker1/Broker2 commission — one accrual entry per
          // distinct broker found across the line items (see
          // computeLineBrokerCommissions). This is the only broker
          // accounting a sale gets now — the header broker was removed.
          for (const [brokerEntityId, commissionUSD] of computeLineBrokerCommissions(splitPlan.finalLineItems)) {
            if (commissionUSD <= 0.005) continue;
            postEntry(tx, db, buildEntry({
              businessId: business!.id, sourceType: 'broker_accrual', sourceId: invRef.id,
              sourceCollection: 'sales', date: header.saleDate || todayISO(),
              currency: settings?.currency ?? 'USD', createdBy: user!.id,
              note: `Broker commission (per-line) — ${invoiceNo}`,
              lines: linesBrokerAccrual(commissionUSD, brokerEntityId),
            }), lineBrokerAccrualId(invRef.id, brokerEntityId));
          }
        });
      }
      onSaved?.();
      onClose();
    } catch (e) {
      console.error('[Sales] handleSave failed:', e); // eslint-disable-line no-console
      setError(e instanceof Error ? e.message : 'Failed to save invoice.');
    }
    setSaving(false);
  }

  // Same "identity in the title, category as an eyebrow" header used by the
  // read-only sale preview — once a customer's picked, the modal reads as
  // "that customer's invoice" instead of a generic form.
  const selectedCustomer = entities.find(e => e.id === header.toEntityId);
  const customerName = selectedCustomer?.companyName;

  // Credit limit — a soft warning, not a hard block (an owner may legitimately
  // choose to sell past it for a trusted customer), so this never appears in
  // `validate()`. Outstanding = every OTHER sale this customer still owes on
  // (excluding the invoice currently being edited, so re-saving it unchanged
  // doesn't double-count itself) plus this invoice's own running total.
  const customerOutstandingUSD = useMemo(() => {
    if (!header.toEntityId) return 0;
    return sales
      .filter(s => s.toEntityId === header.toEntityId && !(isEdit && s.id === editInvoice!.id))
      .reduce((sum, s) => sum + outstandingOf(s), 0);
  }, [sales, header.toEntityId, isEdit, editInvoice]);
  const projectedOutstandingUSD = customerOutstandingUSD + totals.amt + expenseTotals.charged;
  const overCreditLimit = !!selectedCustomer?.creditLimitUSD && projectedOutstandingUSD > selectedCustomer.creditLimitUSD;

  // Arrow-key nav across each committed row's editable cells only — Carat,
  // Price/ct, [Terms when separate mode], Broker1, Broker1%, Broker2,
  // Broker2%. CodeEntryRow (the always-present add-row) keeps its own
  // existing type→match→Enter flow untouched, outside this nav grid.
  const termsColShown = header.dueDateMode === 'separate';
  const CARAT_COL = 0, PRICE_COL = 1;
  // Indian: Less 1 / Less 2 sit right after Price/ct.
  const LESS1_COL = indian ? 2 : -1, LESS2_COL = indian ? 3 : -1;
  const afterPrice = indian ? 4 : 2;
  const TERMS_COL       = termsColShown ? afterPrice : -1;
  const BROKER1_COL     = afterPrice + (termsColShown ? 1 : 0);
  const BROKER1PCT_COL  = BROKER1_COL + 1;
  const BROKER2_COL     = BROKER1_COL + 2;
  const BROKER2PCT_COL  = BROKER1_COL + 3;
  const SALE_NAV_COLS = BROKER1_COL + 4;
  const saleNav = useGridKeyNav(lineItems.length, SALE_NAV_COLS);
  const toFixCount = lineItems.reduce((n, li) => n + SALE_REQUIRED.filter(f => saleCellMissing(li, f)).length, 0);

  // Escape, the backdrop, the × button, and Cancel all funnel through this —
  // with unsaved edits, ask first instead of silently throwing the form away.
  function requestClose() {
    if (formSnapshot(header, lineItems) !== baselineRef.current
      || JSON.stringify(expenseRows) !== expenseBaselineRef.current) setConfirmDiscard(true);
    else onClose();
  }

  return (
    <Fragment>
    <Modal
      open={open}
      onClose={requestClose}
      hideHead
      size="full"
      footer={
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
          {error && <div className="msg msg-error" style={{ margin: 0 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
            {onSkip && <Button variant="secondary" onClick={onSkip} disabled={saving} style={{ marginRight: 'auto' }}>Skip This Sale</Button>}
            <Button variant="secondary" onClick={requestClose} disabled={saving}>{onSkip ? 'Stop Import' : 'Cancel'}</Button>
            <Button variant="primary" onClick={handleSave} loading={saving}>
              {isEdit ? 'Save Changes' : `Save Invoice (${lineItems.length} item${lineItems.length !== 1 ? 's' : ''})`}
            </Button>
          </div>
        </div>
      }
    >
      <ModalBanner
        title={customerName || (isEdit ? 'Edit Sale Invoice' : 'Add Sale Invoice')}
        eyebrow={prefillLabel ?? (customerName ? (isEdit ? 'Edit Sale Invoice' : 'New Sale Invoice') : `${lineItems.length} item${lineItems.length !== 1 ? 's' : ''}`)}
        right={isEdit && editInvoice ? (
          <FactPanel>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', marginBottom: 2 }}>
                Invoice No
              </div>
              <LotPill>{lotNoDisplay(editInvoice.invoiceNo)}</LotPill>
            </div>
          </FactPanel>
        ) : undefined}
        onClose={requestClose}
      />
      {prefillNote && (
        <div className="msg-note" style={{ marginBottom: 12 }}>
          {prefillNote}
          <button type="button" className="line-item-link-btn" style={{ marginLeft: 8 }} onClick={() => setPrefillNote('')}>Dismiss</button>
        </div>
      )}
      {/* ── Invoice header — same layout as the Add Purchase Invoice header ── */}
      <div className="form-section-label" style={{ marginBottom: 8 }}>Invoice Header</div>
      <div style={{ border: '1.5px solid var(--border)', borderRadius: 'var(--r-sm)', overflow: 'hidden', marginBottom: 20 }}>
        <div className="form-table-compact" style={{ padding: '12px 14px' }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ flex: '1.3 1 220px', minWidth: 200 }}>
              <FormField label="To (Customer)" required>
                <EntityCombobox
                  entities={customers}
                  value={header.toEntityId}
                  onChange={v => {
                    setH('toEntityId', v);
                    // Pre-fill Payment Terms/Due Date from the customer's own
                    // Default Terms (set on their Entity record) — same
                    // "pre-fills the invoice" convention as Default Currency.
                    const ent = customers.find(c => c.id === v);
                    if (ent?.defaultTerms) {
                      setH('terms', ent.defaultTerms);
                      setH('dueDate', addDaysToDate(header.saleDate, ent.defaultTerms));
                    }
                    // Same for the share this customer keeps for his own client.
                    if (ent?.defaultLessPercent) setLess(String(ent.defaultLessPercent));
                    // Indian: CGST + SGST in our state, IGST for another state.
                    if (indian && !isEdit && ent) setGstIn(gstInputFrom(defaultGst(settings, (ent.billTo ?? ent.shipTo)?.state)));
                  }}
                  placeholder="Select customer…"
                  createLabel="+ Create New Entity"
                  onCreateNew={() => setCreateEntityMode('customer')}
                />
              </FormField>
            </div>
            <div style={{ width: 128, flexShrink: 0 }}>
              <FormField label="Sale Date" required>
                <input type="date" value={header.saleDate}
                  onChange={e => { const sd = e.target.value; setH('saleDate', sd); setH('dueDate', addDaysToDate(sd, header.terms)); }} />
              </FormField>
            </div>
            <div style={{ width: 128, flexShrink: 0 }}>
              <FormField label="Due Date">
                <input type="date" value={header.dueDate} onChange={e => setH('dueDate', e.target.value)} />
              </FormField>
            </div>
            <div style={{ width: 62, flexShrink: 0 }}>
              <FormField label="Keep(%)">
                <input
                  className="no-spinner" type="number" step="0.01" min="0" max="99" placeholder="0"
                  style={{ textAlign: 'right' }}
                  title="The customer keeps this % for his own client. Prices on the invoice go down by it."
                  value={header.lessPercent} onChange={e => setLess(e.target.value)}
                />
              </FormField>
            </div>
            <div style={{ flexShrink: 0 }}>
              <FormField label="Payment Terms">
                <TermsSelector value={header.terms} onChange={v => { setH('terms', v); setH('dueDate', addDaysToDate(header.saleDate, v)); }} />
              </FormField>
            </div>
            <div style={{ flexShrink: 0 }}>
              <FormField label="Due Date Mode">
                <div style={{ display: 'flex', gap: 6 }}>
                  {([
                    { key: 'average',  label: 'Avg. due date' },
                    { key: 'separate', label: 'Separate' },
                  ] as { key: DueDateMode; label: string }[]).map(({ key, label }) => (
                    <button key={key} type="button" className={`filter-chip${header.dueDateMode === key ? ' active' : ''}`}
                      onClick={() => setH('dueDateMode', key)}>{label}</button>
                  ))}
                </div>
              </FormField>
            </div>
            <div style={{ flex: '1 1 160px', minWidth: 140 }}>
              <FormField label="Notes (optional)">
                <input value={header.notes} onChange={e => setH('notes', e.target.value)}
                  placeholder="Any notes…" />
              </FormField>
            </div>
          </div>
        </div>
      </div>

      {overCreditLimit && selectedCustomer && (
        <div className="msg msg-warning">
          ⚠ {selectedCustomer.companyName}'s outstanding balance would be {money(projectedOutstandingUSD)}
          {' '}after this sale — over their {money(selectedCustomer.creditLimitUSD!)} credit limit
          {' '}(currently {money(customerOutstandingUSD)} outstanding). You can still save if this is intentional.
        </div>
      )}

      {header.toEntityId && (customerActiveMemos.length > 0 || customerLegacyMemoItems.length > 0) && (
        <CustomerMemosPanel
          activeMemos={customerActiveMemos}
          legacyItems={customerLegacyMemoItems}
          addedMemoIds={addedMemoIds}
          usedLegacyIds={new Set(lineItems.map(li => li.inventoryItemId))}
          onAddMemo={addMemoItem}
          onAddLegacy={addItem}
        />
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <div className="form-section-label" style={{ marginBottom: 0 }}>Line Items</div>
        <Button
          variant="secondary" size="sm" icon={<IconPlus />}
          disabled={!canAddDuplicateRow} onClick={addDuplicateRow}
          title={lastLi?.itemType === 'certified' ? 'Certified stones are sold whole — split a loose lot instead' : undefined}
        >
          Add Row (same item, new price)
        </Button>
      </div>
      <p className="form-hint" style={{ marginTop: -2, marginBottom: 6 }}>
        Type a Code into the highlighted row and press Enter to add it — the rest of that row fills in
        automatically. Price/ct is what you're selling at.
        {isEdit && ' Carat on an already-saved line is locked — to fix a wrong carat, delete that row and re-add the stone.'}
      </p>
      {/* A fixed-height box, not just a cap — `height` (not `maxHeight`) so it
          stays the same generous size (~6 rows' worth) whether there are 0
          items or 5, instead of collapsing back down to fit whatever's
          actually there right now (a real bug: an earlier maxHeight-only
          version looked appropriately big when empty — thanks to the empty
          state's own padding — then visibly shrank the instant the first
          real item was added, since nothing else was left to fill the box).
          Split into two flex children so the totals bar can sit pinned at
          the true bottom of that fixed box: the scrollable thead+tbody
          region (`flex:1, minHeight:0` — the `minHeight:0` is required for
          a flex child to actually respect its own `overflow:auto` instead
          of growing the whole box past its fixed height) and, below it, a
          plain (non-scrolling) totals bar — not a `<tfoot>` this time,
          since `position:sticky` only pins an element while its container
          is actually scrolling; with few rows there's nothing to scroll, so
          a sticky tfoot would just sit right after the last row with blank
          space stranded beneath it instead of at the bottom of the box. */}
      <div style={{
        display: 'flex', flexDirection: 'column', height: 350, borderRadius: 'var(--r-sm)',
        border: '1.5px solid var(--border)', overflow: 'hidden',
      }}>
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', overscrollBehavior: 'contain' }}>
        <table className="excel-grid-table">
          <thead>
            <tr>
              <th style={{ ...SGTH, width: 30, textAlign: 'center' }}>#</th>
              <th style={{ ...SGTH, width: 30, textAlign: 'center' }}>Cat.</th>
              <th style={{ ...SGTH, width: 72 }}>Code</th>
              <th style={{ ...SGTH, minWidth: 140 }}>Description</th>
              <th style={{ ...SGTH, width: 56 }}>Color</th>
              <th style={{ ...SGTH, width: 62 }}>Clarity</th>
              <th style={{ ...SGTH, minWidth: 100, textAlign: 'right' }}>Carat</th>
              <th style={{ ...SGTH, width: 90 }}>Price/ct *</th>
              {indian && <>
                <th style={{ ...SGTH, width: 62 }} title="First discount on the price">Less 1 %</th>
                <th style={{ ...SGTH, width: 62 }} title="Second discount, taken after Less 1 %">Less 2 %</th>
              </>}
              <th style={{ ...SGTH, width: 100 }} title={indian ? 'After Less 1 % and Less 2 %' : undefined}>Total Amount</th>
              {termsColShown && (
                <th style={{ ...SGTH, width: 60 }}>Terms</th>
              )}
              <th style={{ ...SGTH, minWidth: 120 }}>Broker1</th>
              <th style={{ ...SGTH, width: 66 }}>Broker1%</th>
              <th style={{ ...SGTH, minWidth: 120 }}>Broker2</th>
              <th style={{ ...SGTH, width: 66 }}>Broker2%</th>
              <th className="excel-grid-remove-col" style={{ ...SGTH, width: 34 }} />
            </tr>
          </thead>
          <tbody>
            {lineItems.map((li, idx) => {
              const source = inventory.find(i => i.id === li.inventoryItemId);
              const isSavedLine = originalIds.has(li.id);
              // A memo-sourced row is still editable while unsaved (it draws
              // from that memo's own separate carat pool, capped via
              // caratCapFor) — the certified-vs-loose and already-saved
              // locks still apply exactly as for any other row.
              const caratLocked = li.itemType !== 'loose' || isSavedLine;
              const claimedBySiblings = li.sourceMemoId ? 0 : lineItems
                .filter(other => other.id !== li.id && other.inventoryItemId === li.inventoryItemId && !other.sourceMemoId)
                .reduce((s, other) => s + other.carat, 0);
              const roomLeft = round2(Math.max(0, caratCapFor(li, source) - claimedBySiblings));
              // Dummy code: show where its balance lands after this invoice's
              // new (unsaved) Dummy rows — it's allowed to go below zero.
              const isDummyRow = isDummyLine(li);
              const dummyAfter = isDummyRow && source
                ? round2(source.carat - lineItems.filter(o => o.inventoryItemId === dummyId && !originalIds.has(o.id)).reduce((t, o) => t + o.carat, 0))
                : 0;
              return (
                <tr key={li.id}>
                  <td style={SGTD_TEXT}>{idx + 1}</td>
                  <td style={SGTD_TEXT}>
                    {li.inventoryItemId === settings?.dummyInventoryItemId ? (
                      // Locked — the Books Adjustment/Dummy code can only ever
                      // be sold as Dummy/Books, so there's nothing to pick.
                      <span title="The Books Adjustment code can only be sold as Dummy/Books" style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        height: 22, padding: '0 6px', borderRadius: 5, boxSizing: 'border-box',
                        border: `1.5px solid ${CATEGORY_META['books-dummy'].color}`,
                        background: `color-mix(in srgb, ${CATEGORY_META['books-dummy'].color} 14%, transparent)`,
                        color: CATEGORY_META['books-dummy'].color,
                      }}>
                        {CATEGORY_META['books-dummy'].icon}
                      </span>
                    ) : (
                      <CategoryPicker value={li.category ?? 'books-real'} onChange={v => v && updateCategory(li.id, v)} />
                    )}
                  </td>
                  <td style={{ ...SGTD_TEXT, fontFamily: 'var(--font-num)' }}>{li.code || <span style={{ opacity: 0.4, fontFamily: 'inherit' }}>—</span>}</td>
                  <td style={{ ...SGTD, minWidth: 140 }}>
                    <DescriptionCombobox
                      className="grid-cell"
                      value={li.description ?? ''}
                      onChange={v => updateDescription(li.id, v)}
                      options={settings?.saleLineDescriptions ?? []}
                    />
                  </td>
                  <td style={SGTD_TEXT}>{li.color}</td>
                  <td style={SGTD_TEXT}>{li.clarity}</td>
                  <td style={{ ...SGTD, minWidth: 100, textAlign: 'right', ...saleMissBg(li, 'carat') }}>
                    {caratLocked ? (
                      <span
                        style={{ fontFamily: 'var(--font-num)', fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4, padding: '0 8px', color: isSavedLine ? 'var(--text-3)' : undefined, cursor: isSavedLine ? 'help' : undefined }}
                        title={isSavedLine
                          ? 'Carat is locked on a line that’s already saved. To correct it, delete this row (trash icon on the right) and add the stone again with the right carat, then Save.'
                          : undefined}
                      >
                        {li.carat.toFixed(2)}
                        {isSavedLine && <IconLockMini />}
                      </span>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4, padding: '0 8px' }}>
                        <input
                          ref={saleNav.cellRef(idx, CARAT_COL)} onKeyDown={saleNav.onCellKeyDown(idx, CARAT_COL)}
                          className="grid-cell no-spinner" style={{ width: 52, textAlign: 'right', padding: '6px 2px' }}
                          type="number" step="0.01" min="0.01" max={roomLeft}
                          value={li.carat}
                          onChange={e => updateCarat(li.id, parseFloat(e.target.value) || 0)}
                        />
                        {isDummyRow ? (
                          <span
                            title={`Dummy balance now ${source?.carat.toFixed(2) ?? '0.00'} ct → ${dummyAfter.toFixed(2)} ct after this invoice. It may go below zero; future Cash/Real sales fill it back.`}
                            style={{ fontSize: 9.5, whiteSpace: 'nowrap', fontWeight: dummyAfter < 0 ? 700 : 400, color: dummyAfter < 0 ? 'var(--danger)' : 'var(--text-3)' }}>
                            bal → {dummyAfter.toFixed(2)}
                          </span>
                        ) : (
                          <span style={{ fontSize: 9.5, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>
                            / {roomLeft.toFixed(2)}
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  <td style={{ ...SGTD, ...saleMissBg(li, 'price') }}>
                    <input
                      ref={saleNav.cellRef(idx, PRICE_COL)} onKeyDown={saleNav.onCellKeyDown(idx, PRICE_COL)}
                      // A saved Dummy row's amount IS what was taken off the
                      // Dummy balance — re-pricing it in place would leave the
                      // two out of step, so it's locked like carat is.
                      disabled={isDummyRow && isSavedLine}
                      title={isDummyRow && isSavedLine ? 'Price is locked on a saved Books Adjustment line — delete the row and add it again to change it.' : undefined}
                      className="grid-cell no-spinner" type="number" step="0.01" min="0"
                      value={(indian ? (li.agreedPricePerCt ?? li.grossPricePerCt ?? li.salePricePerCt) : (li.grossPricePerCt ?? li.salePricePerCt)) || ''}
                      onChange={e => updatePricePerCt(li.id, parseFloat(e.target.value) || 0)}
                    />
                  </td>
                  {indian && (['less1Percent', 'less2Percent'] as const).map((k, ki) => (
                    <td key={k} style={SGTD}>
                      <input
                        ref={saleNav.cellRef(idx, ki === 0 ? LESS1_COL : LESS2_COL)} onKeyDown={saleNav.onCellKeyDown(idx, ki === 0 ? LESS1_COL : LESS2_COL)}
                        disabled={isDummyRow && isSavedLine}
                        className="grid-cell no-spinner" type="number" step="0.01" min="0" max="99" placeholder="0"
                        style={{ textAlign: 'right' }}
                        value={li[k] ?? ''}
                        onChange={e => updateLineLess(li.id, k, e.target.value)}
                      />
                    </td>
                  ))}
                  <td style={{ ...SGTD_TEXT, fontFamily: 'var(--font-num)', whiteSpace: 'nowrap' }}>{money(li.amountUSD)}</td>
                  {termsColShown && (
                    <td style={SGTD}>
                      <input
                        ref={saleNav.cellRef(idx, TERMS_COL)} onKeyDown={saleNav.onCellKeyDown(idx, TERMS_COL)}
                        className="grid-cell no-spinner" type="number" step="1" min="0"
                        placeholder={`${header.terms}`}
                        value={li.terms ?? ''}
                        onChange={e => updateTerms(li.id, e.target.value)}
                      />
                    </td>
                  )}
                  <td style={{ ...SGTD, minWidth: 120 }}>
                    <EntityCombobox
                      inputClassName="grid-cell"
                      cellRef={saleNav.cellRef(idx, BROKER1_COL)} onKeyDown={saleNav.onCellKeyDown(idx, BROKER1_COL)}
                      entities={brokers} value={li.broker1EntityId ?? ''}
                      onChange={v => updateBroker1(li.id, v)}
                      placeholder="Broker 1" clearLabel="No broker"
                    />
                  </td>
                  <td style={SGTD}>
                    <input
                      ref={saleNav.cellRef(idx, BROKER1PCT_COL)} onKeyDown={saleNav.onCellKeyDown(idx, BROKER1PCT_COL)}
                      className="grid-cell no-spinner" type="number" step="0.1" min="0" max="100" placeholder="0"
                      disabled={!li.broker1EntityId}
                      value={li.broker1CommissionPercent ?? ''}
                      onChange={e => updateBroker1Pct(li.id, e.target.value)}
                    />
                  </td>
                  <td style={{ ...SGTD, minWidth: 120 }}>
                    <EntityCombobox
                      inputClassName="grid-cell"
                      cellRef={saleNav.cellRef(idx, BROKER2_COL)} onKeyDown={saleNav.onCellKeyDown(idx, BROKER2_COL)}
                      entities={brokers} value={li.broker2EntityId ?? ''}
                      onChange={v => updateBroker2(li.id, v)}
                      placeholder="Broker 2" clearLabel="No broker"
                    />
                  </td>
                  <td style={SGTD}>
                    <input
                      ref={saleNav.cellRef(idx, BROKER2PCT_COL)} onKeyDown={saleNav.onCellKeyDown(idx, BROKER2PCT_COL)}
                      className="grid-cell no-spinner" type="number" step="0.1" min="0" max="100" placeholder="0"
                      disabled={!li.broker2EntityId}
                      value={li.broker2CommissionPercent ?? ''}
                      onChange={e => updateBroker2Pct(li.id, e.target.value)}
                    />
                  </td>
                  <td className="excel-grid-remove-col" style={{ ...SGTD_TEXT, textAlign: 'center' }}>
                    <button type="button" onClick={() => removeItem(li.id)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', padding: '2px 4px', display: 'inline-flex' }}
                      title="Remove"><IconTrash /></button>
                  </td>
                </tr>
              );
            })}
            {/* Add row — always sits below the committed rows now, not pinned to the top. */}
            <CodeEntryRow available={available} onSelect={addItem} extraCols={(indian ? 2 : 0) + (termsColShown ? 1 : 0)} />
          </tbody>
        </table>
      </div>
      {indian && <GstBoxes value={gstIn} onChange={setGstIn} />}
      {/* Totals bar — small label-over-value figures with thin dividers
          (same look as the preview header's facts). Full amount + Keep(%)
          only appear when the sale has a Keep(%). */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '8px 14px',
        background: 'var(--surface-2)', borderTop: '1.5px solid var(--border)', flexShrink: 0,
      }}>
        {toFixCount > 0 && (
          <span style={{
            fontSize: 11.5, fontWeight: 600, color: 'var(--danger)', background: 'var(--danger-bg)',
            border: '1px solid color-mix(in srgb, var(--danger) 30%, transparent)',
            borderRadius: 'var(--r-sm)', padding: '3px 8px',
          }}>
            {toFixCount} to fix
          </span>
        )}
        <div style={{ marginLeft: 'auto' }}>
          <TotalsFigures figures={[
            { label: 'Items', value: String(lineItems.length) },
            { label: 'Carat', value: `${totals.ct.toFixed(2)} ct` },
            ...(lessPct > 0 ? [
              { label: 'Total', value: money(totals.gross) },
              { label: `Keep ${lessPct}%`, value: `−${money(totals.gross - totals.amt)}`, color: 'var(--danger)' },
            ] : []),
            { label: 'Revenue', value: money(totals.amt), strong: !indian },
            ...(indian ? (() => {
              const t = invoiceGst(round2(totals.amt), gstPcts);
              const m = gstMode(gstPcts);
              return [
                ...(m === 'split' ? [{ label: 'CGST', value: money(t.cgst) }, { label: 'SGST', value: money(t.sgst) }] : []),
                ...(m === 'igst' ? [{ label: 'IGST', value: money(t.igst) }] : []),
                { label: 'Total Amount', value: money(round2(totals.amt + t.total)), strong: true },
              ];
            })() : []),
          ]} />
        </div>
      </div>
      </div>

      {/* ── Additional Charges & Expenses — saleExpenses tied to this sale ──
          Shipping, insurance, export duty… Each row is either our own
          selling cost (hits Profit as Sales Expenses) or charged on to the
          customer (added to this invoice's total and collected with it).
          Mirrors Purchase's Additional Details; save logic lives in
          utils/saleExpenses.ts. */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '20px 0 8px' }}>
        <div className="form-section-label" style={{ marginBottom: 0 }}>
          Additional Charges &amp; Expenses
          <span style={{ fontWeight: 400, color: 'var(--text-3)', marginLeft: 6 }}>
            — Shipping, insurance, duty and the like, tied to this sale ({expenseRows.length})
          </span>
        </div>
        {expenseRows.length > 0 && (
          <div style={{ fontSize: 11.5, color: 'var(--text-2)' }}>
            Charged to customer <span style={{ fontFamily: 'var(--font-num)', fontWeight: 700, color: 'var(--text-1)' }}>{money(expenseTotals.charged)}</span>
            {' · '}Your cost <span style={{ fontFamily: 'var(--font-num)', fontWeight: 700, color: 'var(--text-1)' }}>{money(expenseTotals.own)}</span>
            {expenseTotals.charged > 0 && (
              <>{' · '}Invoice total <span style={{ fontFamily: 'var(--font-num)', fontWeight: 700, color: 'var(--text-1)' }}>{money(totals.amt + expenseTotals.charged)}</span></>
            )}
          </div>
        )}
      </div>
      {expenseRows.length > 0 && (
        <div style={{ border: '1.5px solid var(--border)', borderRadius: 'var(--r-sm)', overflow: 'auto', marginBottom: 8 }}>
          <table className="excel-grid-table">
            <thead>
              <tr>
                <th style={{ ...SGTH, minWidth: 140 }}>Category</th>
                <th style={{ ...SGTH, minWidth: 180 }}>Description</th>
                <th style={{ ...SGTH, width: 100, textAlign: 'right' }}>Amount</th>
                <th style={{ ...SGTH, minWidth: 190 }}>Who pays?</th>
                <th style={{ ...SGTH, width: 128 }}>Date</th>
                <th style={{ ...SGTH, minWidth: 140 }}>Vendor</th>
                <th style={{ ...SGTH, width: 50, textAlign: 'center' }}>Paid?</th>
                <th style={{ ...SGTH, minWidth: 160 }}>Bank Account / Due Date</th>
                <th className="excel-grid-remove-col" style={{ ...SGTH, width: 34 }} />
              </tr>
            </thead>
            <tbody>
              {expenseRows.map(row => (
                <tr key={row._key}>
                  <td style={SGTD}>
                    <select className="grid-cell grid-cell-select" value={row.category}
                      onChange={e => updateExpenseRow(row._key, 'category', e.target.value)}>
                      {SALE_EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                      {!SALE_EXPENSE_CATEGORIES.includes(row.category) && <option value={row.category}>{row.category}</option>}
                    </select>
                  </td>
                  <td style={{ ...SGTD, ...(row.description.trim() ? {} : { background: SALE_MISSING_BG }) }}>
                    <input className="grid-cell" placeholder="e.g. FedEx to Hong Kong"
                      value={row.description} onChange={e => updateExpenseRow(row._key, 'description', e.target.value)} />
                  </td>
                  <td style={{ ...SGTD, ...(parseFloat(row.amount) > 0 ? {} : { background: SALE_MISSING_BG }) }}>
                    <input className="grid-cell no-spinner" type="number" step="0.01" min="0.01" placeholder="0.00"
                      style={{ textAlign: 'right' }}
                      value={row.amount} onChange={e => updateExpenseRow(row._key, 'amount', e.target.value)} />
                  </td>
                  <td style={{ ...SGTD_TEXT, padding: '4px 8px' }}>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {([
                        { v: false, label: 'Our cost' },
                        { v: true,  label: 'Charge customer' },
                      ]).map(o => (
                        <button key={o.label} type="button"
                          className={`filter-chip${row.chargeToCustomer === o.v ? ' active' : ''}`}
                          style={{ fontSize: 10.5, padding: '3px 8px', whiteSpace: 'nowrap' }}
                          onClick={() => updateExpenseRow(row._key, 'chargeToCustomer', o.v)}>
                          {o.label}
                        </button>
                      ))}
                    </div>
                  </td>
                  <td style={SGTD}>
                    <input className="grid-cell" type="date" value={row.date}
                      onChange={e => updateExpenseRow(row._key, 'date', e.target.value)} />
                  </td>
                  <td style={SGTD}>
                    <EntityCombobox
                      inputClassName="grid-cell"
                      entities={entities.map(e => ({ id: e.id, companyName: e.companyName }))}
                      value={row.vendorEntityId}
                      onChange={v => updateExpenseRow(row._key, 'vendorEntityId', v)}
                      placeholder="No vendor" clearLabel="No vendor"
                    />
                  </td>
                  <td style={{ ...SGTD_TEXT, textAlign: 'center' }}>
                    <input type="checkbox" checked={row.payNow}
                      onChange={e => updateExpenseRow(row._key, 'payNow', e.target.checked)} />
                  </td>
                  <td style={{ ...SGTD, ...(row.payNow && !row.bankAccountId ? { background: SALE_MISSING_BG } : {}) }}>
                    {row.payNow ? (
                      <select className="grid-cell grid-cell-select" value={row.bankAccountId}
                        onChange={e => updateExpenseRow(row._key, 'bankAccountId', e.target.value)}>
                        <option value="">Select account…</option>
                        {bankAccounts.map(a => <option key={a.id} value={a.id}>{a.name} — {fmtMoney(a.currentBalance, a.currency)}</option>)}
                      </select>
                    ) : (
                      <input className="grid-cell" type="date" title="Due date (optional)"
                        value={row.dueDate} onChange={e => updateExpenseRow(row._key, 'dueDate', e.target.value)} />
                    )}
                  </td>
                  <td className="excel-grid-remove-col" style={{ ...SGTD_TEXT, textAlign: 'center' }}>
                    <button type="button" onClick={() => removeExpenseRow(row._key)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', padding: '2px 4px', display: 'inline-flex' }}
                      title="Remove"><IconTrash /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ marginBottom: 8 }}>
        <Button variant="secondary" size="sm" icon={<IconPlus />} onClick={addExpenseRow}>Add Charge / Expense</Button>
      </div>
    </Modal>

    <EntityFormModal
      open={createEntityMode !== null}
      onClose={() => setCreateEntityMode(null)}
      defaultTypes={createEntityMode ? [createEntityMode] : undefined}
      onSaved={newEntity => {
        setJustCreatedEntity(newEntity);
        setH('toEntityId', newEntity.id);
        setCreateEntityMode(null);
      }}
    />

    <ConfirmModal
      open={confirmDiscard}
      title="Discard changes?"
      message="This invoice has unsaved changes. Discard them and close, or keep editing?"
      confirmLabel="Discard"
      onConfirm={() => { setConfirmDiscard(false); onClose(); }}
      onClose={() => setConfirmDiscard(false)}
    />
    </Fragment>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function SalesPage() {
  const { user, business } = useAuth();

  const [sales,      setSales]      = useState<SaleInvoice[]>([]);
  const [entities,    setEntities]   = useState<Entity[]>([]);
  const [inventory,   setInventory]  = useState<InventoryItem[]>([]);
  const [memos,       setMemos]      = useState<InventoryMemo[]>([]);
  const [loading,     setLoading]    = useState(true);
  const [settings,    setSettings]   = useState<BusinessSettings | null>(null);
  // Indian: Amount and GST columns before Total Amount; Belgian never shows them.
  const indianList = isIndian(settings);
  const SHOWN_COLUMNS = indianList ? COLUMNS : COLUMNS.filter(c => !GST_COLUMN_KEYS.has(c.key));
  // Logo Size slider: a live draft while dragging, saved to settings
  // a moment after the slider stops moving.
  const [logoDraft,   setLogoDraft]  = useState<number | null>(null);
  const [footImgDraft, setFootImgDraft] = useState<number | null>(null);
  const money = (v: number) => fmtMoney(v ?? 0, settings?.currency ?? 'USD');
  const [previewInvoice, setPreviewInvoice] = useState<SaleInvoice | null>(null);
  const [payTarget,      setPayTarget]      = useState<SaleInvoice | null>(null);
  const [modalOpen,   setModalOpen]  = useState(false);
  const [editInvoice, setEditInvoice] = useState<SaleInvoice | null>(null);
  // Excel import: the sales from the file, opened in the Add Sale form one
  // at a time (importIdx). savedRef tells onClose "saved, go on" apart
  // from "closed, stop".
  const [importOpen,  setImportOpen]  = useState(false);
  const [importQueue, setImportQueue] = useState<SaleImportGroup[] | null>(null);
  const [importIdx,   setImportIdx]   = useState(0);
  const [importDone,  setImportDone]  = useState<{ saved: number; skipped: number; total: number } | null>(null);
  const importSavedRef = useRef(0);
  const importJustSaved = useRef(false);
  const [deleteTarget, setDeleteTarget] = useState<SaleInvoice | null>(null);
  const [deleteError,  setDeleteError]  = useState('');
  const [deleting,     setDeleting]     = useState(false);
  const [searchQ,      setSearchQ]      = useState('');
  // See TransactionCategory. 'real' hides invoices whose every line is
  // books-dummy (books-only, no physical stock); 'books' hides invoices
  // whose every line is cash-real (physical but off the books) — an
  // invoice with a mix of categories across its lines passes both views,
  // same "exclude, don't require-match" rule Inventory's own toggle uses.
  // Default 'real'.
  const { categoryFilter } = useCategoryFilter();
  const [dateFilter,   setDateFilter]   = useState<DateFilter>('this-month');
  const [customFrom,   setCustomFrom]   = useState('');
  const [customTo,     setCustomTo]     = useState('');
  const [currentPage,  setCurrentPage]  = useState(1);
  const [selectedIds,  setSelectedIds]  = useState<Set<string>>(new Set());
  const [sortBy,       setSortBy]       = useState<SortKey>('saleDate');
  const [sortDir,      setSortDir]      = useState<'asc' | 'desc'>('desc');
  const [visibleColumns, setVisibleColumns] = useState<Record<ColumnKey, boolean>>(loadVisibleColumns);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);

  // ── Invoice document view/edit — a single sale's printable invoice
  // replaces the whole page body when open (see the `viewingInvoice` early
  // return below), the same way "Print List" already swaps in the full
  // unpaginated list before calling window.print(). The options panel next
  // to it is persistent (no open/close toggle) and every field saves
  // straight to Firestore — draftNotes is the one exception, a local draft
  // for the free-text memo, saved on blur rather than per keystroke.
  const [viewingId,  setViewingId]  = useState<string | null>(null);
  const [draftNotes, setDraftNotes] = useState('');
  const [savingEdit, setSavingEdit] = useState(false); // brief "Saved" flash after any change
  const invoiceDocWrapRef = useRef<HTMLDivElement>(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    localStorage.setItem(COLUMN_VISIBILITY_KEY, JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  function toggleColumn(key: ColumnKey) {
    setVisibleColumns(v => ({ ...v, [key]: !v[key] }));
  }

  const dateRange = useMemo(
    () => computeDateRange(dateFilter, customFrom, customTo),
    [dateFilter, customFrom, customTo],
  );

  // ── Settings ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const db = getClientDb();
    if (!db || !business) return;
    return onSnapshot(
      doc(db, 'settings', business.id),
      snap => { if (snap.exists()) setSettings(withBusinessSystem(snap.data() as BusinessSettings, business)); },
      () => {},
    );
  }, [business]);

  // ── Firestore ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!dateRange) { setSales([]); setLoading(false); return; }
    const db = getClientDb();
    if (!db) return;
    setLoading(true);

    const u1 = onSnapshot(
      query(
        collection(db, 'sales'),
        where('saleDate', '>=', dateRange.start),
        where('saleDate', '<',  dateRange.end),
        orderBy('saleDate', 'desc'),
      ),
      snap => { setSales(snap.docs.map(d => ({ id: d.id, ...d.data() } as SaleInvoice))); setLoading(false); },
      () => setLoading(false),
    );
    const u2 = onSnapshot(
      query(collection(db, 'entities'), orderBy('companyName')),
      snap => setEntities(snap.docs.map(d => ({ id: d.id, ...d.data() } as Entity))),
    );
    const u3 = onSnapshot(
      query(collection(db, 'inventory'), orderBy('createdAt', 'desc')),
      snap => setInventory(snap.docs.map(d => ({ id: d.id, ...d.data() } as InventoryItem))),
    );
    // Every active memo, across every item — powers the "Customer's Memos"
    // panel in SaleAddModal. No filter/orderBy, so no composite index is
    // needed (same approach as Memos.tsx/Dashboard.tsx); filtered client-side.
    const u4 = onSnapshot(
      collectionGroup(db, 'memos'),
      snap => setMemos(snap.docs.map(d => ({ id: d.id, ...d.data() } as InventoryMemo)).filter(m => m.status === 'active')),
    );
    // Bank accounts — for the invoice document's payment-information block
    // (see InvoiceDocument / invoiceBankAccount below).
    const u5 = onSnapshot(
      collection(db, 'bankAccounts'),
      snap => setBankAccounts(snap.docs.map(d => ({ id: d.id, ...d.data() } as BankAccount)).filter(a => a.isActive !== false)),
    );
    return () => { u1(); u2(); u3(); u4(); u5(); };
  }, [dateRange?.start, dateRange?.end]); // eslint-disable-line react-hooks/exhaustive-deps

  // O(1) lookup instead of entities.find() — same reasoning as Purchase.tsx:
  // sorting/printing the full (unpaginated) list calls this per row, and a
  // linear scan there compounds into an O(n log n × entities) hot path.
  const entityMap = useMemo(() => new Map(entities.map(e => [e.id, e])), [entities]);
  function entityName(id: string) { return entityMap.get(id)?.companyName ?? '—'; }

  // ── Filtering ─────────────────────────────────────────────────────────────
  const baseVisible = useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    return sales.filter(inv => {
      const lines = inv.lineItems ?? [];
      if (categoryFilter === 'real'  && !lines.some(li => (li.category ?? 'books-real') !== 'books-dummy')) return false;
      if (categoryFilter === 'books' && !lines.some(li => (li.category ?? 'books-real') !== 'cash-real'))   return false;
      if (!q) return true;
      const name    = entityName(inv.toEntityId).toLowerCase();
      const broker  = saleBrokerIds(inv).map(id => entityName(id)).join(' ').toLowerCase();
      const basic = invoiceNoMatches(inv.invoiceNo, q) || name.includes(q) || broker.includes(q);
      if (basic) return true;
      return lines.some(li => (li.certificateNo ?? '').toLowerCase().includes(q));
    });
  }, [sales, searchQ, categoryFilter, entities]); // eslint-disable-line react-hooks/exhaustive-deps

  // Excel-style column filters (tick several values per column). Pay
  // Status and Invoice Sent replaced the old header filters; the page still
  // opens on unpaid invoices, as a filter you can remove.
  const PAY_LABEL: Record<string, string> = { paid: 'Paid', partial: 'Partial', unpaid: 'Unpaid' };
  const filterColumns = useMemo<FilterColumn<SaleInvoice>[]>(() => [
    // Shown as the same icons as the table's Cat. column, never as text.
    { key: 'category',   label: 'Cat.',          get: inv => invoiceCategoryOf(inv) ?? '',
      sort: (a, b) => ['books-real', 'cash-real', 'books-dummy', 'mixed'].indexOf(a) - ['books-real', 'cash-real', 'books-dummy', 'mixed'].indexOf(b),
      render: v => <InvoiceCategoryBadge category={v as TransactionCategory | 'mixed'} /> },
    { key: 'invoiceNo',  label: 'Invoice No',    get: inv => (inv.invoiceNo ? shortInvoiceNo(inv.invoiceNo) : '') },
    { key: 'customer',   label: 'Customer',      get: inv => entityName(inv.toEntityId) },
    { key: 'terms',      label: 'Terms',         get: inv => `${inv.terms ?? 0} days` },
    { key: 'broker',     label: 'Broker',        get: inv => saleBrokerIds(inv).map(id => entityName(id)) },
    { key: 'dueDate', label: 'Due Date', get: inv => {
      if (inv.paymentStatus === 'paid') return 'Paid';
      const d = dueDateDisplay(inv.dueDate);
      return d.isOverdue ? 'Overdue' : d.isSoon ? 'Due soon' : 'Not due yet';
    }, sort: (a, b) => ['Overdue', 'Due soon', 'Not due yet', 'Paid'].indexOf(a) - ['Overdue', 'Due soon', 'Not due yet', 'Paid'].indexOf(b) },
    { key: 'payStatus',  label: 'Pay Status',    get: inv => PAY_LABEL[inv.paymentStatus ?? 'unpaid'] ?? 'Unpaid',
      sort: (a, b) => ['Unpaid', 'Partial', 'Paid'].indexOf(a) - ['Unpaid', 'Partial', 'Paid'].indexOf(b) },
    { key: 'sentStatus', label: 'Invoice Sent',  get: inv => (inv.invoiceSentStatus === 'sent' ? 'Sent' : 'Not sent') },
    { key: 'addedBy',    label: 'Added by',      get: inv => addedByName(inv, user) },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [entityMap, user]);
  const { rows: visible, filters: colFilters } = useColumnFilters(baseVisible, filterColumns, { payStatus: ['Unpaid'] });
  const filterable = new Set(filterColumns.map(c => c.key));

  useEffect(() => { setCurrentPage(1); setSelectedIds(new Set()); }, [dateFilter, customFrom, customTo, searchQ, categoryFilter, colFilters.stamp]);

  // ── Sorting — click a column header to sort by it, click again to flip
  // direction. Same mechanism as Purchase/Inventory's own SortableTh. ──────
  function handleSort(key: SortKey) {
    if (sortBy === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(key); setSortDir('desc'); }
  }
  function compareSales(a: SaleInvoice, b: SaleInvoice): number {
    switch (sortBy) {
      case 'category': {
        const rank: Record<string, number> = { 'books-real': 0, 'cash-real': 1, 'books-dummy': 2, mixed: 3 };
        return (rank[invoiceCategoryOf(a) ?? ''] ?? -1) - (rank[invoiceCategoryOf(b) ?? ''] ?? -1);
      }
      case 'invoiceNo':   return (a.invoiceNo ?? '').localeCompare(b.invoiceNo ?? '');
      case 'customer':    return entityName(a.toEntityId).localeCompare(entityName(b.toEntityId));
      case 'saleDate':    return (a.saleDate ?? '').localeCompare(b.saleDate ?? '');
      case 'itemCount':   return (a.itemCount ?? 0) - (b.itemCount ?? 0);
      case 'totalCt':     return (a.totalCarat ?? 0) - (b.totalCarat ?? 0);
      case 'pricePerCt': {
        const pa = (a.totalCarat ?? 0) > 0 ? saleRevenueOf(a) / a.totalCarat! : 0;
        const pb = (b.totalCarat ?? 0) > 0 ? saleRevenueOf(b) / b.totalCarat! : 0;
        return pa - pb;
      }
      case 'netAmount':   return ((a.totalAmountUSD ?? 0) - (a.taxTotalUSD ?? 0)) - ((b.totalAmountUSD ?? 0) - (b.taxTotalUSD ?? 0));
      case 'gstAmount':   return (a.taxTotalUSD ?? 0) - (b.taxTotalUSD ?? 0);
      case 'totalAmount': return (a.totalAmountUSD ?? 0) - (b.totalAmountUSD ?? 0);
      case 'terms':       return (a.terms ?? 0) - (b.terms ?? 0);
      case 'broker':      return saleBrokerIds(a).map(id => entityName(id)).join(', ').localeCompare(saleBrokerIds(b).map(id => entityName(id)).join(', '));
      case 'dueDate':     return (a.dueDate ?? '').localeCompare(b.dueDate ?? '');
      case 'payStatus':   return PAY_RANK[a.paymentStatus ?? 'unpaid'] - PAY_RANK[b.paymentStatus ?? 'unpaid'];
      case 'sentStatus':  return (a.invoiceSentStatus === 'sent' ? 1 : 0) - (b.invoiceSentStatus === 'sent' ? 1 : 0);
      case 'addedBy':     return addedByName(a, user).localeCompare(addedByName(b, user));
    }
  }
  const sortedVisible = useMemo(() => {
    const arr = [...visible];
    arr.sort((a, b) => sortDir === 'asc' ? compareSales(a, b) : compareSales(b, a));
    return arr;
  }, [visible, sortBy, sortDir, entities]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Selection — same shape as Purchase's list (checkbox column carries the
  // row's urgency accent border too, so nothing extra is pinned). ─────────
  function toggleSelect(id: string) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  const selectedInvoices = useMemo(
    () => visible.filter(inv => selectedIds.has(inv.id)),
    [visible, selectedIds],
  );

  const totalPages  = Math.ceil(sortedVisible.length / PAGE_SIZE);
  const pagedSales  = sortedVisible.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const pageIds = pagedSales.map(i => i.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every(id => selectedIds.has(id));
  function toggleSelectPage() {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allPageSelected) pageIds.forEach(id => next.delete(id));
      else pageIds.forEach(id => next.add(id));
      return next;
    });
  }

  // Footer totals — grand totals of the whole filtered list (same "whole
  // filtered set, not just the current page" rule CSV export/other tfoot
  // totals in this app already follow), shown both on screen and printing.
  const footerTotals = useMemo(() => {
    const ct     = sortedVisible.reduce((s, inv) => s + (inv.totalCarat ?? 0), 0);
    const amount = sortedVisible.reduce((s, inv) => s + (inv.totalAmountUSD ?? 0), 0);
    const goods  = sortedVisible.reduce((s, inv) => s + saleRevenueOf(inv), 0);
    return { ct, amount, avgPricePerCt: ct > 0 ? goods / ct : 0 };
  }, [sortedVisible]);

  // ── Export — CSV of every filtered invoice (not just the current page),
  // using whichever columns are currently visible. Print bypasses
  // pagination the same way, so the printed/PDF'd list is complete too. ──
  function exportRowFor(inv: SaleInvoice, key: ColumnKey): string {
    const ct = inv.totalCarat ?? 0;
    const pricePerCt = ct > 0 ? saleRevenueOf(inv) / ct : 0;
    switch (key) {
      case 'category': {
        const cat = invoiceCategoryOf(inv);
        return cat ? (cat === 'mixed' ? 'Mixed' : CATEGORY_META[cat].label) : '';
      }
      case 'invoiceNo':   return inv.invoiceNo ?? '';
      case 'customer':    return entityName(inv.toEntityId);
      case 'saleDate':    return inv.saleDate ?? '';
      case 'itemCount':   return String(inv.itemCount ?? 0);
      case 'totalCt':     return ct.toFixed(2);
      case 'pricePerCt':  return pricePerCt.toFixed(2);
      case 'netAmount':   return ((inv.totalAmountUSD ?? 0) - (inv.taxTotalUSD ?? 0)).toFixed(2);
      case 'gstAmount':   return (inv.taxTotalUSD ?? 0).toFixed(2);
      case 'totalAmount': return (inv.totalAmountUSD ?? 0).toFixed(2);
      case 'terms':       return String(inv.terms ?? 0);
      case 'broker':      return saleBrokerIds(inv).map(id => entityName(id)).join(', ');
      case 'dueDate':     return inv.dueDate ?? '';
      case 'payStatus':   return inv.paymentStatus ?? 'unpaid';
      case 'sentStatus':  return inv.invoiceSentStatus === 'sent' ? 'sent' : 'not-sent';
      case 'addedBy':     return addedByName(inv, user);
    }
  }
  function handleExportCSV() {
    const cols = SHOWN_COLUMNS.filter(c => visibleColumns[c.key]);
    downloadCSV(
      'sales-invoices',
      cols.map(c => c.label),
      sortedVisible.map(inv => cols.map(c => exportRowFor(inv, c.key))),
    );
  }
  // Waits for two animation frames (not a fixed setTimeout) before calling
  // print() — a guessed delay is either wasted time on a short list or,
  // worse, fires before a long list has actually finished rendering; two
  // rAFs guarantee the browser has painted the just-swapped-in full list
  // first, which is what actually fixes "Print List feels slow to open."
  const [printAll, setPrintAll] = useState(false);
  useEffect(() => {
    if (!printAll) return;
    let cancelled = false;
    const after = () => setPrintAll(false);
    window.addEventListener('afterprint', after);
    requestAnimationFrame(() => {
      if (cancelled) return;
      requestAnimationFrame(() => { if (!cancelled) window.print(); });
    });
    return () => { cancelled = true; window.removeEventListener('afterprint', after); };
  }, [printAll]);
  const displayedSales = printAll ? sortedVisible : pagedSales;
  // The table box ends just above the screen's bottom edge (page numbers under it).
  const salesWrapRef = useRef<HTMLDivElement>(null);
  useFitHeight(salesWrapRef, 56, 280, [displayedSales.length > 0, loading]);
  // The tick box and the first 3 visible columns (Cat., Invoice No,
  // Customer) stay in place on sideways scroll; on a phone only the tick
  // box and the next two.
  const salesTableRef = useRef<HTMLTableElement>(null);
  const salesVisibleCount = SHOWN_COLUMNS.filter(c => visibleColumns[c.key]).length;
  useFrozenColumns(salesTableRef, 1 + Math.min(3, salesVisibleCount), 1 + Math.min(2, salesVisibleCount),
    [visibleColumns, displayedSales.length]);

  // ── Actions ───────────────────────────────────────────────────────────────
  function openAdd()  { setEditInvoice(null); setModalOpen(true); }
  // Dashboard "+ New" opens this form via ?new=1.
  useNewParam(openAdd);
  // Inventory popup's Add Sale opens it via ?item=<id> with that stone added.
  const [addItemId, setAddItemId] = useState<string | undefined>();
  useItemParam(id => { setAddItemId(id); openAdd(); });
  function openEdit(inv: SaleInvoice) { setEditInvoice(inv); setModalOpen(true); }
  function closeModal() { setModalOpen(false); setEditInvoice(null); setAddItemId(undefined); }

  function startImport(groups: SaleImportGroup[]) {
    setImportOpen(false);
    importSavedRef.current = 0;
    setImportDone(null);
    setImportIdx(0);
    setImportQueue(groups);
  }
  function nextImport(saved: boolean) {
    if (!importQueue) return;
    if (saved) importSavedRef.current++;
    const next = importIdx + 1;
    if (next < importQueue.length) { setImportIdx(next); return; }
    finishImport(importQueue.length);
  }
  function finishImport(reached: number) {
    const total = importQueue?.length ?? 0;
    const saved = importSavedRef.current;
    setImportQueue(null);
    // Widen the list's dates so the imported (often older) sales show.
    const first = (importQueue ?? []).map(g => g.saleDate).filter(Boolean).sort()[0];
    if (saved > 0 && first) {
      if (first >= `${todayISO().slice(0, 4)}-01-01`) setDateFilter('this-year');
      else { setCustomFrom(first); setCustomTo(todayISO()); setDateFilter('custom'); }
    }
    setImportDone({ saved, skipped: reached - saved, total });
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleting(true); setDeleteError('');
    const db = getClientDb();
    if (db) {
      try {
        const srSnap = await getDocs(query(collection(db, 'salesReturns'), where('saleId', '==', deleteTarget.id)));
        if (!srSnap.empty) {
          setDeleteError('Cannot delete — this sale has a credit note recorded against it. Delete the credit note first.');
          setDeleting(false);
          return;
        }
        // paidUSD covers both real receipts (sales/{id}/payments) and any
        // customer-advance applied to this sale — deleting straight through
        // would orphan that money (bank balance already moved in, or the
        // advance permanently marked "used") with no sale left to explain it.
        // Both have their own correct reversal flow (delete the receipt on AR
        // / "Un-apply" on Customer Advances) — send the user there first.
        if ((deleteTarget.paidUSD ?? 0) > 0.005) {
          setDeleteError('Cannot delete — this sale has a payment or an applied advance recorded against it. Remove those first (AR → delete the payment, or Customer Advances → Un-apply).');
          setDeleting(false);
          return;
        }
        // A payment against one of the per-line Broker1/Broker2 brokers —
        // fetched unfiltered (this subcollection is always small) and
        // checked client-side rather than an inequality query, to sidestep
        // any Firestore index/missing-field subtlety on `!= null`.
        const lineBrokerPaySnap = await getDocs(collection(db, 'sales', deleteTarget.id, 'brokerPayments'));
        if (lineBrokerPaySnap.docs.some(d => !!(d.data() as BrokerPayment).brokerEntityId)) {
          setDeleteError('Cannot delete — a broker commission payment is recorded against this sale. Delete that payment on the Brokers page first.');
          setDeleting(false);
          return;
        }
        const saleHistoryRefs = await findSaleCodeHistoryRefs(db, deleteTarget.lineItems);
        // Its charges/expenses go with it — docs, bank movements, ledger entries.
        const seSnap = await getDocs(query(collection(db, 'saleExpenses'), where('saleId', '==', deleteTarget.id)));
        const expensePlan = planSaleExpenses(db, [], seSnap.docs.map(d => ({ id: d.id, ...d.data() } as SaleExpense)));
        await runTransaction(db, async tx => {
          const expenseAccts = await readSaleExpenseAccounts(tx, expensePlan);
          const plan = await planSaleReversal(tx, db, deleteTarget.lineItems);
          // Deleting only ever REDUCES cash-real exposure (finalLineItems is
          // empty), never creates the Dummy item — counterSnap: null is safe,
          // the create-new-item branch inside can't be reached from here.
          const dummyPlan = await planDummyCreditDelta(tx, db, business!.id, deleteTarget.id, [], settings, null);
          tx.delete(doc(db, 'sales', deleteTarget.id));
          logActivity(tx, db, business!.id, user, {
            module: 'sale', action: 'delete', recordId: deleteTarget.id, recordNo: deleteTarget.invoiceNo,
            partyId: deleteTarget.toEntityId || undefined, itemCount: deleteTarget.itemCount,
            carat: deleteTarget.totalCarat, amountUSD: deleteTarget.totalAmountUSD,
          });
          applySaleReversal(tx, db, plan);
          applySaleExpenses(tx, db, expensePlan, expenseAccts, {
            businessId: business!.id, userId: user!.id, baseCurrency: settings?.currency ?? 'USD',
            prefix: settings?.saleExpensePrefix || 'SE', counterStart: 0,
            saleId: deleteTarget.id, saleInvoiceNo: deleteTarget.invoiceNo,
            saleDate: deleteTarget.saleDate, customerEntityId: deleteTarget.toEntityId,
          });
          saleHistoryRefs.forEach(ref => tx.delete(ref));
          applyDummyCreditDelta(tx, db, dummyPlan, { toEntityId: deleteTarget.toEntityId, saleDate: deleteTarget.saleDate }, deleteTarget.invoiceNo, user!.id);
          // Remove this sale's ledger entries (revenue/COGS + broker accrual).
          tx.delete(doc(db, 'journalEntries', entryId('sales_invoice', deleteTarget.id)));
          tx.delete(doc(db, 'journalEntries', entryId('broker_accrual', deleteTarget.id)));
          for (const brokerEntityId of computeLineBrokerCommissions(deleteTarget.lineItems ?? []).keys()) {
            tx.delete(doc(db, 'journalEntries', lineBrokerAccrualId(deleteTarget.id, brokerEntityId)));
          }
        });
        setDeleteTarget(null);
      } catch (e) {
        setDeleteError(e instanceof Error ? e.message : 'Failed to delete.');
      }
    }
    setDeleting(false);
  }

  const hasActiveFilters = !!searchQ || colFilters.active.length > 0;

  // Bank shown on a printed invoice: the one picked in that invoice's
  // Options panel, else Settings' invoice bank, else the default account,
  // else the first real bank account (never a cash box).
  const invoiceBanks = useMemo(() => bankAccounts.filter(a => a.type === 'bank'), [bankAccounts]);
  function invoiceBankFor(inv: SaleInvoice): BankAccount | undefined {
    const byId = (id?: string) => (id ? invoiceBanks.find(a => a.id === id) : undefined);
    return byId(inv.invoiceOptions?.bankAccountId)
      ?? byId(settings?.invoiceBankAccountId)
      ?? invoiceBanks.find(a => a.isDefault)
      ?? invoiceBanks[0];
  }

  // The EUR → USD rate is saved on the invoice itself, so changing it later
  // never rewrites an invoice already sent. It also becomes the default the
  // next new invoice starts with (BusinessSettings.invoiceEurUsdRate).
  async function saveEurUsdRate(inv: SaleInvoice, raw: string) {
    const db = getClientDb();
    if (!db || !business) return;
    const n = parseFloat(raw.replace(',', '.'));
    const value = Number.isFinite(n) && n > 0 ? Math.round(n * 10000) / 10000 : null;
    if ((value ?? undefined) === (inv.invoiceOptions?.eurUsdRate ?? settings?.invoiceEurUsdRate)) return;
    try {
      await updateDoc(doc(db, 'sales', inv.id), { 'invoiceOptions.eurUsdRate': value, updatedAt: serverTimestamp() });
      if (value) await setDoc(doc(db, 'settings', business.id), { invoiceEurUsdRate: value, updatedAt: serverTimestamp() }, { merge: true });
      flashSaved();
    } catch { /* leave the field as typed; it retries on the next blur */ }
  }

  async function toggleSent(inv: SaleInvoice) {
    const db = getClientDb();
    if (!db) return;
    const nextSent = inv.invoiceSentStatus !== 'sent';
    try {
      await updateDoc(doc(db, 'sales', inv.id), {
        invoiceSentStatus: nextSent ? 'sent' : 'not-sent',
        invoiceSentAt: nextSent ? new Date().toISOString() : null,
        updatedAt: serverTimestamp(),
      });
    } catch { /* transient — the live listener will just show the pre-toggle state again */ }
  }

  const viewingInvoice = viewingId ? sales.find(s => s.id === viewingId) ?? null : null;

  // Clears the notes draft when switching invoices (or leaving the view) so
  // a half-typed memo from one invoice never bleeds into another.
  function viewInvoice(id: string | null) {
    const inv = id ? sales.find(s => s.id === id) : undefined;
    setDraftNotes(inv?.notes ?? '');
    setViewingId(id);
  }
  // Receivable's "Print" opens /sales?view=<id> (once the sales have loaded,
  // so the invoice's own notes fill the draft).
  useViewParam(id => viewInvoice(id), sales.length > 0);

  // Printing now happens from inside a portaled Modal (a sibling of
  // .app-shell), not a full-page swap of .page-content — the existing
  // @media print rules only clean up the page chrome, not the modal's own
  // overlay/backdrop/scroll. A body class toggled right before print (and
  // cleared on 'afterprint', same cleanup pattern as `printAll` on the
  // list pages) lets a dedicated `body.printing-invoice` print rule in
  // index.css hide the app and strip the modal chrome instead.
  function handlePrintInvoice() {
    document.body.classList.add('printing-invoice');
    const after = () => {
      document.body.classList.remove('printing-invoice');
      window.removeEventListener('afterprint', after);
    };
    window.addEventListener('afterprint', after);
    window.print();
  }

  // A real, standalone PDF file (not "print, then choose Save as PDF" —
  // the browser print dialog is a manual extra step, and on some setups
  // (odd printer drivers, some mobile browsers) it doesn't reliably land on
  // a clean PDF at all). Rasterizes the on-screen .invoice-doc node with
  // html2canvas, then slices that single tall image across as many A4 pages
  // as it needs — the standard jsPDF+html2canvas multi-page technique, since
  // jsPDF can't reflow live DOM/text across pages on its own.
  //
  // scale: 3 (not html2canvas' default 1×, or the browser's own devicePixelRatio)
  // renders at ~216 DPI at A4 width — the earlier 2× pass (~144 DPI) was soft
  // once stretched to full page width, which read as "blurry text". JPEG
  // instead of PNG is what actually shrinks the file: this is a rasterized
  // photo-like render (anti-aliased text edges, subpixel font smoothing), not
  // flat vector art, so PNG's lossless compression barely helps and produced
  // a multi-MB file — JPEG at 0.92 quality is visually indistinguishable here
  // and typically 5-10× smaller.
  async function handleDownloadPdf() {
    const sheets = Array.from(invoiceDocWrapRef.current?.querySelectorAll<HTMLElement>('.invoice-doc-sheet') ?? []);
    if (sheets.length === 0 || !viewingInvoice) return;
    setDownloadingPdf(true);
    try {
      // One A4 page per invoice sheet: each sheet is already laid out at
      // A4 proportions with its own repeated header (see InvoiceDocument).
      const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait', compress: true });
      const pageWidth  = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      for (let i = 0; i < sheets.length; i++) {
        const canvas = await html2canvas(sheets[i], { scale: 3, useCORS: true, backgroundColor: '#ffffff' });
        if (i > 0) pdf.addPage();
        const imgHeight = Math.min(pageHeight, (canvas.height * pageWidth) / canvas.width);
        pdf.addImage(canvas.toDataURL('image/jpeg', 0.92), 'JPEG', 0, 0, pageWidth, imgHeight);
      }
      pdf.save(`Invoice-${lotNoDisplay(viewingInvoice.invoiceNo)}.pdf`);
    } catch { /* best-effort — the Print button still works as a fallback */ }
    setDownloadingPdf(false);
  }

  function flashSaved() {
    setSavingEdit(true);
    setTimeout(() => setSavingEdit(false), 1500);
  }

  useEffect(() => {
    if (logoDraft === null || !business) return;
    if (logoDraft === (settings?.invoiceLogoWidth ?? LOGO_W_DEFAULT)) return;
    const t = setTimeout(async () => {
      const db = getClientDb();
      if (!db) return;
      try {
        await setDoc(doc(db, 'settings', business.id), { invoiceLogoWidth: logoDraft, updatedAt: serverTimestamp() }, { merge: true });
        flashSaved();
      } catch { /* the slider keeps its value; the next move retries */ }
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logoDraft]);

  useEffect(() => {
    if (footImgDraft === null || !business) return;
    if (footImgDraft === (settings?.invoiceFooterImageHeight ?? FOOTER_IMG_H_DEFAULT)) return;
    const t = setTimeout(async () => {
      const db = getClientDb();
      if (!db) return;
      try {
        await setDoc(doc(db, 'settings', business.id), { invoiceFooterImageHeight: footImgDraft, updatedAt: serverTimestamp() }, { merge: true });
        flashSaved();
      } catch { /* the slider keeps its value; the next move retries */ }
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [footImgDraft]);

  // Every option field saves the instant it's changed — this is a
  // persistent panel next to the document now, not a dialog you open, edit,
  // then explicitly commit, so there's nothing to "Save" as a batch. Dot-
  // notation update() touches only this one key under invoiceOptions,
  // so two quick toggles in a row can never clobber each other.
  // Invoice Currency: the currency the customer sees. Picking one fills a
  // starting rate (Dashboard exchange rates, or the saved EUR rate); a typed
  // rate replaces it. Back to the books currency clears both.
  async function saveInvoiceCurrency(inv: SaleInvoice, cur: string, rateText?: string) {
    const db = getClientDb();
    if (!db) return;
    const base = settings?.currency ?? 'USD';
    const update: Record<string, unknown> = { updatedAt: serverTimestamp() };
    if (cur === base) {
      update['invoiceOptions.currency'] = null;
      update['invoiceOptions.rate'] = null;
    } else {
      const typed = rateText != null ? parseFloat(rateText) : NaN;
      const rate = typed > 0 ? typed
        : (inv.invoiceOptions?.currency === cur && inv.invoiceOptions?.rate)
          || suggestInvoiceRate(base, cur, settings?.exchangeRates, inv.invoiceOptions?.eurUsdRate ?? settings?.invoiceEurUsdRate)
          || null;
      update['invoiceOptions.currency'] = cur;
      update['invoiceOptions.rate'] = rate;
    }
    try { await updateDoc(doc(db, 'sales', inv.id), update); flashSaved(); } catch { /* listener keeps the old value */ }
  }

  async function updateInvoiceOption(
    invoiceId: string,
    key: keyof NonNullable<SaleInvoice['invoiceOptions']>,
    value: boolean | string,
  ) {
    const db = getClientDb();
    if (!db) return;
    try {
      await updateDoc(doc(db, 'sales', invoiceId), {
        [`invoiceOptions.${key}`]: value,
        updatedAt: serverTimestamp(),
      });
      flashSaved();
    } catch { /* the live listener just keeps showing the pre-toggle state */ }
  }

  // The memo is free text, so it saves on blur rather than on every
  // keystroke — everything else in this panel is immediate.
  async function saveInvoiceNotes(invoiceId: string) {
    const db = getClientDb();
    if (!db) return;
    try {
      await updateDoc(doc(db, 'sales', invoiceId), {
        notes: draftNotes.trim() || null,
        updatedAt: serverTimestamp(),
      });
      flashSaved();
    } catch { /* the field keeps the unsaved draft so nothing is lost */ }
  }

  // Viewing/printing a single invoice: rendered as a <Modal> further down
  // (near the page's other modals), driven by this same viewingId state —
  // see the print-from-modal note on handlePrintInvoice below for how
  // printing works now that this isn't a full-page swap anymore.

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <AppLayout title="Sales" subtitle="Record and track diamond sales" toolbar={<>
          {/* Left: find. Search, then the filters (each names its choice). */}
          <div className="tb-search">
            <span className="tb-search-icon"><IconSearch /></span>
            <input
              value={searchQ} onChange={e => setSearchQ(e.target.value)}
              placeholder="Invoice no, cert no, customer…"
              aria-label="Search sales"
            />
            {searchQ && (
              <button type="button" className="tb-search-clear" onClick={() => setSearchQ('')} title="Clear search">×</button>
            )}
          </div>

          {/* Date: one button naming the range; Custom shows two date boxes. */}
          <Dropdown
            trigger={() => (
              <button type="button" className={`icon-btn${dateFilter !== 'this-month' ? ' active' : ''}`} style={{ height: 34 }}>
                <IconCalendarSm /> {DATE_FILTER_LABELS[dateFilter]}
                <span style={{ color: 'var(--text-3)', display: 'flex' }}><IconChevronDownSm /></span>
              </button>
            )}
          >
            {([
              { key: 'this-month', label: 'This Month' },
              { key: '3-months',   label: '3 Months'   },
              { key: 'this-year',  label: 'This Year'  },
              { key: 'custom',     label: 'Custom'     },
            ] as { key: DateFilter; label: string }[]).map(({ key, label }) => (
              <button key={key} type="button" className="dropdown-item" onClick={() => setDateFilter(key)}>
                <span style={{ width: 14, display: 'flex', color: 'var(--primary)', flexShrink: 0 }}>
                  {dateFilter === key && <IconCheckSm />}
                </span>
                {label}
              </button>
            ))}
          </Dropdown>
          {dateFilter === 'custom' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="date" className="tb-date" value={customFrom} max={customTo || todayISO()}
                onChange={e => setCustomFrom(e.target.value)} aria-label="From date" />
              <span style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 600 }}>→</span>
              <input type="date" className="tb-date" value={customTo} min={customFrom || undefined} max={todayISO()}
                onChange={e => setCustomTo(e.target.value)} aria-label="To date" />
            </div>
          )}

          {hasActiveFilters && (
            <button type="button" className="tb-clear" onClick={() => { setSearchQ(''); colFilters.clear(); }}>
              Clear
            </button>
          )}
      </>}
      headerActions={<>
          {/* Right: tools as one joined group, then the main action last. */}
          <div className="tb-group">
            <Dropdown
              align="right"
              closeOnInnerClick={false}
              trigger={() => (
                <button type="button" className="tb-group-btn" title="Columns" aria-label="Columns"><IconColumns /></button>
              )}
            >
              <div className="dropdown-group-label">Show Columns</div>
              {SHOWN_COLUMNS.map(c => (
                <label key={c.key} className="dropdown-item" style={{ cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={visibleColumns[c.key]}
                    onChange={() => toggleColumn(c.key)}
                    style={{ accentColor: 'var(--primary)' }}
                  />
                  {c.label}
                </label>
              ))}
              <div className="dropdown-divider" />
              <button type="button" className="dropdown-item" onClick={() => setVisibleColumns({ ...DEFAULT_VISIBLE })}>
                Select All
              </button>
            </Dropdown>
            <Dropdown
              align="right"
              trigger={() => (
                <button type="button" className="tb-group-btn" title="Export" aria-label="Export"><IconExport /></button>
              )}
            >
              <button type="button" className="dropdown-item" onClick={handleExportCSV}>Export CSV</button>
              <button type="button" className="dropdown-item" onClick={() => setPrintAll(true)}>Print List</button>
            </Dropdown>
            <button type="button" className="tb-group-btn" title="Import from Excel" aria-label="Import" onClick={() => setImportOpen(true)}>
              <IconImportSm />
            </button>
          </div>
          <Button variant="primary" size="sm" icon={<IconPlus />} onClick={openAdd} style={{ height: 34, boxSizing: 'border-box' }}>
            Add Sale
          </Button>
      </>}>


      {importDone && (
        <div className="msg-note" style={{ marginBottom: 12 }}>
          {importDone.saved > 0
            ? `Imported ${importDone.saved} of ${importDone.total} sale${importDone.total !== 1 ? 's' : ''}.`
            : 'No sales were imported.'}
          {importDone.skipped > 0 && ` ${importDone.skipped} skipped.`}
          {importDone.saved + importDone.skipped < importDone.total && ` Stopped before the last ${importDone.total - importDone.saved - importDone.skipped}.`}
          <button type="button" className="line-item-link-btn" style={{ marginLeft: 8 }} onClick={() => setImportDone(null)}>Dismiss</button>
        </div>
      )}

      {/* ── Summary strip + Column customizer — one row, Columns pinned right ──
          Stats reserve their row's height via `visibility` (not conditional
          render) even while loading/empty, so the Columns trigger never
          jumps position — same technique as Purchase's selection-details
          bar + Columns row. Selecting one or more invoices swaps the same
          strip from "whole filtered list" totals to "just the selection" —
          no separate selection bar, same place, same fields (Total ct /
          Revenue / Cost / Margin), so a selected invoice's exact value and
          margin reads straight off it. */}
      {(() => {
        const hasSelection = selectedIds.size > 0;
        const statsSource  = hasSelection ? selectedInvoices : visible;
        const totCt     = statsSource.reduce((s, i) => s + (i.totalCarat ?? 0), 0);
        const totAmt    = statsSource.reduce((s, i) => s + saleRevenueOf(i), 0);
        const totCost   = statsSource.reduce((s, i) => s + (i.totalCostingUSD ?? 0), 0);
        const totMargin = totAmt - totCost;
        const showStats = !loading && (hasSelection || visible.length > 0);
        const stats = [
          { label: hasSelection ? 'Selected' : 'Total', val: String(statsSource.length), mono: false },
          { label: 'Total ct', val: `${totCt.toFixed(2)} ct`,     mono: true  },
          { label: 'Revenue',  val: money(totAmt),                mono: true  },
          // Cost and Margin only for the owner, or staff given the right.
          ...(canAccessPage(user, COST_MARGIN_KEY) ? [
            { label: 'Cost',     val: money(totCost),               mono: true  },
            { label: 'Margin',   val: money(totMargin),             mono: true  },
          ] : []),
        ];
        return (
          <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 8, rowGap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', flex: '1 1 auto', visibility: showStats ? 'visible' : 'hidden' }}>
              {stats.map(({ label, val, mono }) => (
                <div key={label} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: 'var(--surface)', border: '1px solid var(--border)',
                  borderRadius: 'var(--r-sm)', padding: '6px 12px',
                  boxShadow: 'var(--shadow-xs)', fontSize: 12,
                }}>
                  <span style={{ color: 'var(--text-3)', fontWeight: 500 }}>{label}</span>
                  <span style={{ width: 1, height: 12, background: 'var(--border)', flexShrink: 0 }} />
                  <span style={{
                    color: label === 'Margin' ? (totMargin >= 0 ? 'var(--success)' : 'var(--danger)') : 'var(--text-1)',
                    fontWeight: 700, fontFamily: mono ? 'var(--font-num)' : undefined,
                  }}>{val}</span>
                </div>
              ))}
              {hasSelection && (
                <button onClick={() => setSelectedIds(new Set())} style={{
                  display: 'flex', alignItems: 'center', fontSize: 12, fontWeight: 700,
                  color: 'var(--danger)', background: 'var(--danger-bg)',
                  border: '1px solid color-mix(in srgb, var(--danger) 30%, transparent)',
                  borderRadius: 'var(--r-sm)', padding: '6px 12px', cursor: 'pointer',
                }}>
                  Clear selection
                </button>
              )}
            </div>
            <ColumnFilterChips filters={colFilters} inline />
          </div>
        );
      })()}

      {/* ── Custom date prompt ── */}
      {dateFilter === 'custom' && !dateRange && (
        <div style={{
          background: 'var(--surface)', border: '1.5px dashed var(--border)',
          borderRadius: 'var(--r-md)', padding: '36px 24px', textAlign: 'center', marginBottom: 12,
        }}>
          <div style={{ marginBottom: 10, color: 'var(--text-3)' }}><IconCalendar size={28} /></div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)', marginBottom: 6 }}>Select a date range</div>
          <div style={{ fontSize: 13, color: 'var(--text-3)' }}>Choose a From and To date above to load sales.</div>
        </div>
      )}

      {/* ── Table / states ── */}
      {loading ? (() => {
        const skeletonWidths = [
          ACCENT_SKELETON_WIDTH,
          ...SHOWN_COLUMNS.filter(c => visibleColumns[c.key]).map(c => c.skeletonWidth),
        ];
        return (
          <div className="table-wrap">
            <table>
              <thead><tr>{skeletonWidths.map((w, i) => (
                <th key={i}><div className="skeleton" style={{ height: 11, width: w, borderRadius: 4 }} /></th>
              ))}</tr></thead>
              <tbody>{[...Array(5)].map((_, i) => (
                <tr key={i} style={{ opacity: 1 - i * 0.18 }}>
                  {skeletonWidths.map((w, j) => (
                    <td key={j}><div className="skeleton" style={{ height: 13, width: w, borderRadius: 4 }} /></td>
                  ))}
                </tr>
              ))}</tbody>
            </table>
          </div>
        );
      })() : dateRange && visible.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <div style={{ marginBottom: 12, color: 'var(--text-3)' }}>{sales.length === 0 ? <IconTrendUp size={34} /> : <IconSearch size={34} />}</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)', marginBottom: 6 }}>
            {sales.length === 0 ? 'No sales in this period' : 'No sales match your filters'}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-3)', maxWidth: 320, margin: '0 auto' }}>
            {sales.length === 0
              ? 'Click "Add Sale" to record your first sale.'
              : 'Try changing the column filters or the search.'}
          </div>
          {sales.length === 0
            ? <div style={{ marginTop: 20 }}><Button variant="primary" size="sm" icon={<IconPlus />} onClick={openAdd}>Add Sale</Button></div>
            : <div style={{ marginTop: 16 }}>
                <button onClick={() => { setSearchQ(''); colFilters.clear(); }}
                  style={{ fontSize: 13, padding: '5px 10px', border: '1.5px solid color-mix(in srgb, var(--danger) 35%, transparent)', borderRadius: 'var(--r-sm)', background: 'var(--danger-bg)', color: 'var(--danger)', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap', fontFamily: 'inherit' }}>
                  Clear all filters
                </button>
              </div>
          }
        </div>
      ) : dateRange ? (
        <>
          <div ref={salesWrapRef} className="table-wrap table-fit table-fit-stats table-freeze-cols">
            <table ref={salesTableRef}>
              <thead>
                <tr>
                  <th style={{ width: 40, minWidth: 40, maxWidth: 40, textAlign: 'center' }}>
                    <input type="checkbox" checked={allPageSelected} onChange={toggleSelectPage} title="Select all on page" />
                  </th>
                  {SHOWN_COLUMNS.filter(c => visibleColumns[c.key]).map(c => (
                    <SortableTh
                      key={c.key} label={c.label} sortKey={c.key} activeKey={sortBy} dir={sortDir} onSort={handleSort}
                      align={c.key === 'totalCt' || c.key === 'pricePerCt' || c.key === 'totalAmount' || c.key === 'netAmount' || c.key === 'gstAmount' ? 'right' : c.key === 'category' ? 'center' : undefined}
                      width={FIXED_W[c.key]}
                      filter={filterable.has(c.key) ? <ColumnFilterButton filters={colFilters} colKey={c.key} label={c.label} /> : undefined}
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayedSales.map(inv => {
                  // Paid in full: the due date is just a date (no red, no "141d over"),
                  // same as Purchase.
                  const ddRaw      = dueDateDisplay(inv.dueDate);
                  const isPaidInv  = inv.paymentStatus === 'paid';
                  const dd         = { ...ddRaw, isOverdue: !isPaidInv && ddRaw.isOverdue, isSoon: !isPaidInv && ddRaw.isSoon };
                  const accent     = dd.isOverdue ? 'var(--danger)' : dd.isSoon ? '#f59e0b' : 'transparent';
                  const ct         = inv.totalCarat ?? 0;
                  const pricePerCt = ct > 0 ? saleRevenueOf(inv) / ct : 0;
                  return (
                    <tr key={inv.id} style={{ transition: 'background 0.12s', cursor: 'pointer' }}
                      onClick={() => setPreviewInvoice(inv)}>
                        {/* Pinned selection + urgency-accent cell — not a data column, so it stays
                            even if every customizable column (including Invoice No) is hidden. */}
                        <td style={{ width: 32, textAlign: 'center', paddingLeft: 6, paddingRight: 2, borderLeft: `3px solid ${accent}`, transition: 'border-color 0.15s' }}
                          onClick={e => e.stopPropagation()}>
                          <input type="checkbox" checked={selectedIds.has(inv.id)} onChange={() => toggleSelect(inv.id)} />
                        </td>
                        {visibleColumns.category && (
                          <td style={{ textAlign: 'center' }}><InvoiceCategoryBadge category={invoiceCategoryOf(inv)} /></td>
                        )}
                        {visibleColumns.invoiceNo && (
                          <td>
                            <span
                              title={inv.invoiceNo || undefined}
                              style={{
                                display: 'inline-block', background: 'var(--primary-light)', color: 'var(--primary)',
                                fontFamily: 'var(--font-num)', fontSize: 11, fontWeight: 700,
                                padding: '3px 8px', borderRadius: 6, letterSpacing: '0.02em',
                              }}>
                              {lotNoDisplay(inv.invoiceNo)}
                            </span>
                          </td>
                        )}
                        {visibleColumns.customer && (
                          <td style={{ fontSize: 12, color: 'var(--text-2)', maxWidth: 220 }}>
                            <span className="cell-clip" title={entityName(inv.toEntityId)}>{entityName(inv.toEntityId)}</span>
                          </td>
                        )}
                        {visibleColumns.saleDate && (
                          <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{fmtShortDate(inv.saleDate)}</td>
                        )}
                        {visibleColumns.itemCount && (
                          <td style={{ fontFamily: 'var(--font-num)', fontSize: 12, color: 'var(--text-2)' }}>{inv.itemCount ?? 0}</td>
                        )}
                        {visibleColumns.totalCt && (
                          <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)', fontSize: 12, color: 'var(--text-2)' }}>{ct.toFixed(2)} ct</td>
                        )}
                        {visibleColumns.pricePerCt && (
                          <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)', fontSize: 12, color: 'var(--text-2)' }}>
                            {pricePerCt > 0 ? money(pricePerCt) : <span style={{ opacity: 0.4 }}>—</span>}
                          </td>
                        )}
                        {indianList && visibleColumns.netAmount && (
                          <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)', fontSize: 12, color: 'var(--text-2)' }}>
                            {money((inv.totalAmountUSD ?? 0) - (inv.taxTotalUSD ?? 0))}
                          </td>
                        )}
                        {indianList && visibleColumns.gstAmount && (
                          <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)', fontSize: 12, color: 'var(--text-2)' }}
                            title={inv.taxTotalUSD ? [inv.cgstUSD ? `CGST ${money(inv.cgstUSD)}` : '', inv.sgstUSD ? `SGST ${money(inv.sgstUSD)}` : '', inv.igstUSD ? `IGST ${money(inv.igstUSD)}` : ''].filter(Boolean).join(' · ') : undefined}>
                            {inv.taxTotalUSD ? money(inv.taxTotalUSD) : <span style={{ opacity: 0.4 }}>—</span>}
                          </td>
                        )}
                        {visibleColumns.totalAmount && (
                          <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)', fontSize: 12, fontWeight: 700, color: 'var(--text-1)' }}>
                            {money(inv.totalAmountUSD ?? 0)}
                            {/* Invoiced in another currency: what the customer sees, small and grey. */}
                            {(() => { const f = fmtInInvoiceCurrency(inv.totalAmountUSD ?? 0, inv, settings?.currency ?? 'USD'); return f ? <span className="inv-cur-note">{f}</span> : null; })()}
                          </td>
                        )}
                        {visibleColumns.terms && (
                          <td style={{ fontFamily: 'var(--font-num)', fontSize: 12, color: 'var(--text-2)' }}>
                            {inv.terms ? `${inv.terms}d` : <span style={{ opacity: 0.4 }}>—</span>}
                          </td>
                        )}
                        {visibleColumns.broker && (
                          <td style={{ fontSize: 12, color: 'var(--text-2)' }}>
                            {(() => {
                              const ids = saleBrokerIds(inv);
                              const names = ids.map(id => entityName(id));
                              // One name per line, so two brokers stack instead of widening the column.
                              // Keyed by broker id (not name): while company names are still
                              // loading every name reads "—", and duplicate keys left a stale
                              // "—" line behind once the real names arrived.
                              return ids.length
                                ? <div title={names.join(', ')} style={{ lineHeight: 1.35 }}>
                                    {ids.map((id, k) => <div key={id} className="cell-clip" style={{ maxWidth: 180 }}>{names[k]}</div>)}
                                  </div>
                                : <span style={{ opacity: 0.4 }}>—</span>;
                            })()}
                          </td>
                        )}
                        {visibleColumns.dueDate && (
                          <td>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <span style={{ fontSize: 12, fontWeight: 500, color: dd.isOverdue ? 'var(--danger)' : dd.isSoon ? '#c2410c' : 'var(--text-1)' }}>{dd.label}</span>
                              {!isPaidInv && <span style={{ fontSize: 10, fontWeight: 600, lineHeight: 1, color: dd.isOverdue ? 'var(--danger)' : dd.isSoon ? '#f59e0b' : 'var(--text-3)' }}>{dd.badge}</span>}
                            </div>
                          </td>
                        )}
                        {visibleColumns.payStatus && <td><PayBadge inv={inv} /></td>}
                        {visibleColumns.sentStatus && (
                          <td onClick={e => e.stopPropagation()}>
                            <button
                              type="button"
                              onClick={() => toggleSent(inv)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                              title={inv.invoiceSentStatus === 'sent' ? 'Click to mark Not Sent' : 'Click to mark Sent'}
                            >
                              <SentBadge sent={inv.invoiceSentStatus === 'sent'} />
                            </button>
                          </td>
                        )}
                        {visibleColumns.addedBy && (
                          <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{addedByName(inv, user) || <span style={{ opacity: 0.4 }}>—</span>}</td>
                        )}
                      </tr>
                  );
                })}
              </tbody>
              {printAll && visible.length > 0 && (
                <tfoot>
                  <tr>
                    <td colSpan={1 + SHOWN_COLUMNS.filter(c => visibleColumns[c.key]).length}>
                      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 12 }}>
                        <span style={{ color: 'var(--text-2)' }}>
                          Total ct <strong style={{ color: 'var(--text-1)', fontFamily: 'var(--font-num)' }}>{footerTotals.ct.toFixed(2)}</strong>
                        </span>
                        <span style={{ color: 'var(--text-2)' }}>
                          Total Amount <strong style={{ color: 'var(--text-1)', fontFamily: 'var(--font-num)' }}>{money(footerTotals.amount)}</strong>
                        </span>
                        <span style={{ color: 'var(--text-2)' }}>
                          Avg. Price/ct <strong style={{ color: 'var(--text-1)', fontFamily: 'var(--font-num)' }}>{money(footerTotals.avgPricePerCt)}</strong>
                        </span>
                      </div>
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          <div className="no-print">
            <Pagination
              current={currentPage} total={totalPages}
              totalItems={visible.length} pageSize={PAGE_SIZE}
              onChange={setCurrentPage}
            />
          </div>
        </>
      ) : null}

      {/* ── View / Print Invoice — opens in place on the Sales screen instead
          of navigating away. Options sit permanently alongside the
          document — no dialog to open/close, every field applies
          immediately (same as before the modal conversion). ── */}
      {viewingInvoice && (() => {
        const entity = entityMap.get(viewingInvoice.toEntityId);
        const vOpts  = viewingInvoice.invoiceOptions ?? {};
        return (
          <Modal
            open
            onClose={() => viewInvoice(null)}
            hideHead
            size="2xl"
          >
            <ModalBanner
              title={entity?.companyName || lotNoDisplay(viewingInvoice.invoiceNo)}
              eyebrow="Sale Invoice"
              noGradient
              onClose={() => viewInvoice(null)}
              right={
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {savingEdit && <span style={{ fontSize: 12, color: 'var(--success)', fontWeight: 600 }}>✓ Saved</span>}
                  <span title={viewingInvoice.invoiceNo}><LotPill>{lotNoDisplay(viewingInvoice.invoiceNo)}</LotPill></span>
                  <div className="header-action-bar">
                    <button
                      type="button"
                      className={viewingInvoice.invoiceSentStatus === 'sent' ? 'active' : undefined}
                      onClick={() => toggleSent(viewingInvoice)}
                      title={viewingInvoice.invoiceSentStatus === 'sent' ? 'Click to mark Not Sent' : 'Click to mark Sent'}
                    >
                      <IconCheck />
                      <span>{viewingInvoice.invoiceSentStatus === 'sent' ? 'Sent' : 'Mark as Sent'}</span>
                    </button>
                    <button type="button" onClick={handleDownloadPdf} disabled={downloadingPdf} title="Download PDF">
                      <IconPdf />
                      <span>{downloadingPdf ? 'Generating…' : 'PDF'}</span>
                    </button>
                    <button type="button" onClick={handlePrintInvoice} title="Print">
                      <IconPrint />
                      <span>Print</span>
                    </button>
                  </div>
                </div>
              }
            />
            <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div ref={invoiceDocWrapRef} style={{ flex: '1 1 700px', minWidth: 0, position: 'relative' }}>
                <InvoiceDocument
                  invoice={viewingInvoice}
                  entity={entity}
                  settings={settings && (logoDraft !== null || footImgDraft !== null) ? {
                    ...settings,
                    ...(logoDraft !== null ? { invoiceLogoWidth: logoDraft } : {}),
                    ...(footImgDraft !== null ? { invoiceFooterImageHeight: footImgDraft } : {}),
                  } : settings}
                  business={business}
                  bankAccount={invoiceBankFor(viewingInvoice)}
                />
              </div>

              {/* top: 60, not -20 — -20 is what ModalBanner itself uses to bleed
                  flush against .modal-body's edge (its own negative margin
                  cancels the 20px padding). This panel has no such margin
                  trick, so stealing that same -20 offset made it stick at
                  the identical scroll position as the banner and disappear
                  behind it (the banner's zIndex: 2 wins). 60 clears the
                  noGradient banner's own ~68px rendered height (40px avatar
                  + 14px top/bottom padding) plus a small gap, so the panel
                  sticks just below the banner instead of under it. */}
              <div className="no-print card" style={{ flex: '0 0 320px', width: 320, position: 'sticky', top: 60 }}>
                <div className="card-title">Invoice Options</div>

                <div style={{ marginBottom: 18 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 8 }}>
                    Accent Color
                  </label>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {INVOICE_ACCENT_SWATCHES.map(c => {
                      const active = (vOpts.accentColor || '#0F172A') === c;
                      return (
                        <button
                          key={c}
                          type="button"
                          title={c}
                          onClick={() => updateInvoiceOption(viewingInvoice.id, 'accentColor', c)}
                          style={{
                            width: 22, height: 22, borderRadius: '50%', background: c, padding: 0, cursor: 'pointer',
                            border: active ? '2px solid var(--text-1)' : '2px solid var(--surface)',
                            boxShadow: '0 0 0 1px var(--border)',
                          }}
                        />
                      );
                    })}
                  </div>
                </div>

                <OptionsSection title="Page Layout">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {INVOICE_OPTION_TOGGLES.map(o => (
                      <label key={o.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--text-1)', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={!!vOpts[o.key]}
                          onChange={e => updateInvoiceOption(viewingInvoice.id, o.key, e.target.checked)}
                          style={{ accentColor: 'var(--primary)', flexShrink: 0 }}
                        />
                        {o.label}
                      </label>
                    ))}
                  </div>
                </OptionsSection>

                {settings?.invoiceLogoDataUrl && (() => {
                  const w = logoDraft ?? settings.invoiceLogoWidth ?? LOGO_W_DEFAULT;
                  return (
                    <OptionsSection title="Logo Size" defaultOpen>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 11, color: 'var(--text-3)' }}>Small</span>
                        <input
                          type="range" min={LOGO_W_MIN} max={LOGO_W_MAX} step={2} value={w}
                          onChange={e => setLogoDraft(Number(e.target.value))}
                          aria-label="Logo size"
                          style={{ flex: 1, accentColor: 'var(--primary)', cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: 11, color: 'var(--text-3)' }}>Big</span>
                      </div>
                      {w !== LOGO_W_DEFAULT && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                          <button type="button" className="line-item-link-btn" style={{ fontSize: 11.5 }}
                            onClick={() => setLogoDraft(LOGO_W_DEFAULT)}>Reset</button>
                        </div>
                      )}
                    </OptionsSection>
                  );
                })()}

                {settings?.invoiceFooterNote?.trim() && (
                  <OptionsSection title="Footer Note" defaultOpen>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--text-1)', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={!vOpts.hideFooterNote}
                        onChange={e => updateInvoiceOption(viewingInvoice.id, 'hideFooterNote', !e.target.checked)}
                        style={{ accentColor: 'var(--primary)', flexShrink: 0 }}
                      />
                      Show footer note
                    </label>
                  </OptionsSection>
                )}

                {settings?.invoiceRjcCertDataUrl && (() => {
                  const h = footImgDraft ?? settings.invoiceFooterImageHeight ?? FOOTER_IMG_H_DEFAULT;
                  const shown = !vOpts.hideFooterImage;
                  return (
                    <OptionsSection title="Footer Image" defaultOpen>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--text-1)', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={shown}
                          onChange={e => updateInvoiceOption(viewingInvoice.id, 'hideFooterImage', !e.target.checked)}
                          style={{ accentColor: 'var(--primary)', flexShrink: 0 }}
                        />
                        Show footer image
                      </label>
                      {shown && (
                        <>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
                            <span style={{ fontSize: 11, color: 'var(--text-3)' }}>Small</span>
                            <input
                              type="range" min={FOOTER_IMG_H_MIN} max={FOOTER_IMG_H_MAX} step={2} value={h}
                              onChange={e => setFootImgDraft(Number(e.target.value))}
                              aria-label="Footer image size"
                              style={{ flex: 1, accentColor: 'var(--primary)', cursor: 'pointer' }}
                            />
                            <span style={{ fontSize: 11, color: 'var(--text-3)' }}>Big</span>
                          </div>
                          {h !== FOOTER_IMG_H_DEFAULT && (
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                              <button type="button" className="line-item-link-btn" style={{ fontSize: 11.5 }}
                                onClick={() => setFootImgDraft(FOOTER_IMG_H_DEFAULT)}>Reset</button>
                            </div>
                          )}
                        </>
                      )}
                    </OptionsSection>
                  );
                })()}

                {/* The currency the customer sees. The sale and its payments stay
                    in the books currency; locked once a payment is recorded. */}
                <OptionsSection title="Invoice Currency" defaultOpen>
                  {(() => {
                    const base = settings?.currency ?? 'USD';
                    const cur  = invoiceCurrencyOf(viewingInvoice, base);
                    const locked = (viewingInvoice.paidUSD ?? 0) > 0.005;
                    return (
                      <div className="invoice-trade-fields" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <FormField label="Currency" className="invoice-trade-field">
                          <select value={cur} disabled={locked}
                            onChange={e => saveInvoiceCurrency(viewingInvoice, e.target.value)}>
                            {CURRENCY_LIST.map(c => <option key={c.code} value={c.code}>{c.code} · {c.name}{c.code === base ? ' (books)' : ''}</option>)}
                          </select>
                        </FormField>
                        {cur !== base && (
                          <FormField label={`1 ${cur} in ${base}`} className="invoice-trade-field">
                            <input
                              key={`irate-${viewingInvoice.id}-${vOpts.rate ?? ''}`}
                              type="number" step="0.0001" min="0" inputMode="decimal" className="no-spinner"
                              defaultValue={vOpts.rate ?? ''} disabled={locked}
                              onBlur={e => saveInvoiceCurrency(viewingInvoice, cur, e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                              placeholder="e.g. 1.0850"
                            />
                          </FormField>
                        )}
                        <p className="form-hint" style={{ margin: 0 }}>
                          {locked ? 'Locked: a payment is recorded on this invoice.'
                            : cur !== base ? `Prints in ${cur}. The sale and payments stay in ${base}.` : 'Prints in the books currency.'}
                        </p>
                      </div>
                    );
                  })()}
                </OptionsSection>

                <OptionsSection title="Bank & Exchange Rate" defaultOpen>
                  <div className="invoice-trade-fields" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <FormField label="Bank Account" className="invoice-trade-field">
                      <select
                        value={invoiceBankFor(viewingInvoice)?.id ?? ''}
                        onChange={e => updateInvoiceOption(viewingInvoice.id, 'bankAccountId', e.target.value)}
                        disabled={invoiceBanks.length === 0}
                      >
                        {invoiceBanks.length === 0 && <option value="">No bank account yet</option>}
                        {invoiceBanks.map(a => (
                          <option key={a.id} value={a.id}>{a.bankName || a.name}{a.iban ? ` (${a.iban})` : ''}</option>
                        ))}
                      </select>
                    </FormField>
                    {invoiceBanks.length === 0 && (
                      <p className="form-hint" style={{ margin: 0 }}>Add a bank account on the Bank page to show it here.</p>
                    )}
                    <FormField label="1 EUR in USD" className="invoice-trade-field">
                      <input
                        key={`rate-${viewingInvoice.id}-${vOpts.eurUsdRate ?? settings?.invoiceEurUsdRate ?? ''}`}
                        type="number" step="0.0001" min="0" inputMode="decimal" className="no-spinner"
                        defaultValue={vOpts.eurUsdRate ?? settings?.invoiceEurUsdRate ?? ''}
                        onBlur={e => saveEurUsdRate(viewingInvoice, e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                        placeholder="e.g. 1.0850"
                      />
                    </FormField>
                    <p className="form-hint" style={{ margin: 0 }}>Saved on this invoice. New invoices start with it.</p>
                  </div>
                </OptionsSection>

                <OptionsSection title="Trade Details">
                  <div className="invoice-trade-fields" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {INVOICE_TEXT_FIELDS.map(f => (
                      <FormField key={f.key} label={f.label} className="invoice-trade-field">
                        <input
                          defaultValue={(vOpts[f.key] as string | undefined) ?? ''}
                          key={`${viewingInvoice.id}-${f.key}`}
                          onBlur={e => updateInvoiceOption(viewingInvoice.id, f.key, e.target.value.trim())}
                          placeholder="—"
                        />
                      </FormField>
                    ))}
                  </div>
                </OptionsSection>

                <OptionsSection title="Customer Memo" defaultOpen>
                  <div className="form-group invoice-trade-field">
                    <textarea
                      value={draftNotes}
                      onChange={e => setDraftNotes(e.target.value)}
                      onBlur={() => saveInvoiceNotes(viewingInvoice.id)}
                      placeholder="A note printed at the bottom of this invoice…"
                    />
                  </div>
                </OptionsSection>
              </div>
            </div>
          </Modal>
        );
      })()}

      <SaleAddModal
        open={modalOpen} onClose={closeModal}
        entities={entities} inventory={inventory} memos={memos} settings={settings} editInvoice={editInvoice}
        sales={sales} bankAccounts={bankAccounts} initialItemId={addItemId}
      />

      <ImportSalesModal
        open={importOpen} onClose={() => setImportOpen(false)}
        entities={entities} inventory={inventory} dummyId={settings?.dummyInventoryItemId}
        onStart={startImport}
      />
      {importQueue && importQueue[importIdx] && (
        <SaleAddModal
          key={`import-${importIdx}`}
          open
          onClose={() => {
            if (importJustSaved.current) { importJustSaved.current = false; nextImport(true); }
            else finishImport(importIdx);
          }}
          onSaved={() => { importJustSaved.current = true; }}
          onSkip={() => nextImport(false)}
          prefill={importQueue[importIdx]}
          prefillLabel={`Import · Sale ${importIdx + 1} of ${importQueue.length}`}
          entities={entities} inventory={inventory} memos={memos} settings={settings}
          sales={sales} bankAccounts={bankAccounts}
        />
      )}

      <SalePreviewModal
        invoice={previewInvoice}
        entityName={entityName}
        onClose={() => setPreviewInvoice(null)}
        onPay={inv => setPayTarget(inv)}
        onView={id => viewInvoice(id)}
        onEdit={inv => openEdit(inv)}
        onDelete={inv => { setDeleteTarget(inv); setDeleteError(''); }}
      />

      <RecordSalePaymentModal
        invoice={payTarget}
        onClose={() => setPayTarget(null)}
        entityName={payTarget ? entityName(payTarget.toEntityId) : undefined}
      />

      <ConfirmModal
        open={!!deleteTarget}
        onClose={() => { setDeleteTarget(null); setDeleteError(''); }}
        onConfirm={handleDelete}
        title="Delete Sale Invoice"
        message={
          <>
            Are you sure you want to delete invoice <strong>{deleteTarget?.invoiceNo}</strong>?
            All items on this invoice will be returned to stock. This cannot be undone.
          </>
        }
        confirmLabel="Delete Invoice"
        loading={deleting}
        error={deleteError}
      />
    </AppLayout>
  );
}
